import { 
	recentCohortQuery
} from "./utils";
const today = new Date();
const new_building_start = today.getFullYear() - 7;
const current_year = today.getFullYear();

/**
 * Pied-à-terre surcharge, shared by the Owner Outreach, Inventory and Houses searches.
 * Thresholds and rates: https://pat.marketproof.com/pied-a-terre-tax-law — condos from
 * $1M of DOF market value, 1-3 family homes from $5M. The rules live in
 * shared/helpers/piedATerre.js, which drives the badge and the Taxes-tab panel; keep
 * the thresholds here in step with it.
 *
 * What qualifies is decided ENTIRELY on supplemental_tax_roll — the DOF record type,
 * building class, tax class and fmv of the row itself — not on our ownership_type.
 * Qualifying on ownership_type inflated the condo count from 12k to 26k: it let any
 * $1M+ class-1 lot through the condo threshold (5.9k ordinary houses we happen to label
 * condo/co-op) and matched co-op corporation lots, whose fmv is a whole building (6.4k).
 *
 * - Two record types qualify at $1M. `rectype:1` lot rows in the condo unit classes
 *   (R1-R4, R6 — R0 base lots carry no value, R5/R7/R8 are commercial, R9 is a whole
 *   co-op section), and `rectype:U` rows, DOF's per-unit valuations inside a lot, which
 *   is how co-op units are reached. A co-op's `rectype:1` lot row is deliberately NOT in
 *   the tier: its fmv is the whole corporation, not an apartment.
 * - Tax class picks the tier: class 2 takes the $1M schedule, class 1 takes the $5M one.
 *   A condo sitting in class 1 (R3/R6 in 1A/1C) therefore lands on the $5M side, not the
 *   $1M side — 5.1k lots, and counting them as condos overshot the published total.
 * - The home tier drops the condo (R*) and vacant-land (V*) classes that also sit in
 *   class 1.
 *
 * Roll rows matched line up with https://pat.marketproof.com/pied-a-terre-surcharge-analysis:
 * 11,830 condo lots (published 11,741), 5,586 co-op units (published 5,586) and 6,795
 * 1-3 family (published 6,755).
 *
 * The join tries both key fields: the roll files each lot under DOF's primary address,
 * so `dof_property` misses anything we key off a different entrance (111 W 57th St is
 * `p-1-106-w-58th-st-…`), while `dof_alternate_properties` carries every variant but is
 * only populated for condo lots.
 */
const _patRollQuery = clause => ['dof_property', 'dof_alternate_properties']
	.map(field => `_query_:"{!join from=${field} fromIndex=supplemental_tax_roll to=key}(${clause})"`)
	.join(' OR ');
const _patCCCQuery = `(${_patRollQuery('fmv:[1000000 TO *] AND tax_class:2* AND (bldg_class:(R1 R2 R3 R4 R6) OR rectype:U)')})`;
const _patHouseQuery = `(${_patRollQuery('fmv:[5000000 TO *] AND tax_class:1* AND -bldg_class:R* AND -bldg_class:V*')})`;
export const _buildingFilters = [
	{
		name: "Construction",
		show_name: true,
		search_key: "sales_start_date",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Move-in Ready",
				filter: "completed",
				dropdown_name: "Move-in Ready (Has CO/TCO)",
				search_value: "[NOW-25YEARS TO NOW] OR building_status:(resale) OR offering_verified_status:(\"sold out\") OR first_closing_date:[NOW-25YEARS TO NOW]",
			},
			{
				name: "Under Construction",
				filter: "not-completed",
				dropdown_name: "Under Construction (No CO/TCO)",
				search_value: "[* TO NOW-25YEARS] AND -building_status:(resale) AND -offering_verified_status:(\"sold out\")",
			}
			
		]
	},
	{
		name: "Price / Sq Ft",
		show_name: true,
		search_key: "inventory_stats_listings_average_ppsf",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< $750 PPSF ",
				filter: "less-than-750",
				dropdown_name: "Less than $750",
				search_value: "[100 TO 750]"
			},
			{
				name: "$750 to $1,000 PPSF",
				filter: "75-to-1000",
				dropdown_name: "$750 to $1,000",
				search_value: "[751 TO 1000]"
			},
			{
				name: "$1,000 to $1,500 PPSF",
				filter: "1000-to-1500",
				dropdown_name: "$1,000 to $1,500",
				search_value: "[1001 TO 1500]"
			},
			{
				name: "$1,500 to $2,000 PPSF",
				filter: "1500-to-2000",
				dropdown_name: "$1,500 to $2,000",
				search_value: "[1500 TO 2000]"
			},
			{
				name: "$2,000 to $2,500 PPSF",
				filter: "2000-to-2500",
				dropdown_name: "$2,000 to $2,500",
				search_value: "[2001 to 2500]"
			},
			{
				name: "$2,500 to $5,000 PPSF",
				filter: "2500-to-5000",
				dropdown_name: "$2,500 to $5,000",
				search_value: "[2501 to 5000]"
			},
			{
				name: "$5,000+ PPSF",
				filter: "5000-or-more",
				dropdown_name: "$5,000 or more",
				search_value: "[5001 TO *]"
			}
		]
	},
	{
		name: "Starting From",
		show_name: true,
		search_key: "inventory_stats_listings_low_price",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "From: < $500,000 ",
				filter: "less-than-500",
				dropdown_name: "Less than $500K",
				search_value: "[100 TO 500000]"
			},
			{
				name: "From: $500K to $750K",
				filter: "500-to-750",
				dropdown_name: "$500K to $750K",
				search_value: "[500000 TO 750000]"
			},
			{
				name: "From: $750K to $1M",
				filter: "750-to-1000",
				dropdown_name: "$750K to $1M",
				search_value: "[750001 TO 1000000]"
			},
			{
				name: "From: $1M to $1.5M",
				filter: "1000-to-1500",
				dropdown_name: "$1M to $1.5M",
				search_value: "[1000001 TO 1500000]"
			},
			{
				name: "From: $1.5M to $2M",
				filter: "1500-to-2000",
				dropdown_name: "$1.5M to $2M",
				search_value: "[1500001 TO 2000000]"
			},
			{
				name: "From: $2M+",
				filter: "2000+",
				dropdown_name: "$2M+",
				search_value: "[2000000 TO *]"
			}
		]
	},
	
	{
		name: "Unsold Units",
		show_name: true,
		search_key: "inventory_unsold",
		values: [
			{
				name: "Any Unsold Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-10 Unsold Units",
				filter: "1-to-10",
				dropdown_name: "1 to 10",
				search_value: "[1 TO 10]"
			},
			{
				name: "11-30 Unsold Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Unsold Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Unsold Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Unsold Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 + Unsold Units",
				filter: "201-or-more",
				dropdown_name: "201 or more",
				search_value: "[201 TO *]"
			}
		]
	},
	/*{
		name: "Percent Sold",
		show_name: true,
		search_key: "inventory_pct_sold",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Less Than 15%",
				filter: "0-to-15",
				dropdown_name: "Less Than 15%",
				search_value: "[0 TO 15]"
			},
			{
				name: "Less Than 50%",
				filter: "0-to-49",
				dropdown_name: "Less Than 50%",
				search_value: "[0 TO 49]"
			},
			{
				name: "50% to 79%",
				filter: "50-to-79",
				dropdown_name: "50% to 79%",
				search_value: "[50 TO 79]"
			},
			{
				name: "80% to 99%",
				filter: "80-to-99",
				dropdown_name: "80% to 99%",
				search_value: "[80 TO 99]"
			},
			{
				name: "Sold Out",
				filter: "100 TO *",
				dropdown_name: "Sold Out",
				search_value: "[100 TO *]"
			}
		]
	},*/
	{
		name: "% Sold",
		show_name: true,
		search_key: "inventory_pct_sold",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Has Sales",
				filter: "has-sales",
				dropdown_name: "Has Sales",
				search_value: "[1 TO *] OR offering_verified_status:\"sold out\""
			},
			{
				name: "Has No Sales",
				filter: "no-sales",
				dropdown_name: "Has No Sales",
				search_value: "[* TO 0] AND -offering_verified_status:\"sold out\""
			},
			{
				name: "Less Than 15%",
				filter: "0-to-15",
				dropdown_name: "Less Than 15%",
				search_value: "[0 TO 15]"
			},
			{
				name: "Less Than 35%",
				filter: "0-to-35",
				dropdown_name: "Less Than 35%",
				search_value: "[0 TO 34]"
			},
			{
				name: "Less Than 50%",
				filter: "0-to-49",
				dropdown_name: "Less Than 50%",
				search_value: "[0 TO 49]"
			},
			{
				name: "More Than 15%",
				filter: "15-to-100",
				dropdown_name: "More Than 15%",
				search_value: "[15 TO *]"
			},
			{
				name: "More Than 50%",
				filter: "50-to-100",
				dropdown_name: "More Than 50%",
				search_value: "[50 TO *]"
			},
			{
				name: "50% to 79%",
				filter: "50-to-79",
				dropdown_name: "50% to 79%",
				search_value: "[50 TO 79]"
			},
			{
				name: "80% to 99%",
				filter: "80-to-99",
				dropdown_name: "80% to 99%",
				search_value: "[80 TO 99]"
			},
			{
				name: "Not Sold Out",
				filter: "not-sold-out",
				dropdown_name: "Not Sold Out",
				search_value: "[* TO 99] AND -offering_verified_status:\"sold out\""
			},
			{
				name: "Sold Out",
				filter: "sold-out",
				dropdown_name: "Sold Out",
				search_value: "[100 TO *]"
			}
			
		]
	},
	{
		name: "Condo Units",
		show_name: true,
		search_key: "inventory_total",
		values: [
			{
				name: "Any Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-10 Condo Units",
				filter: "1-to-10",
				dropdown_name: "1 to 10 Units",
				search_value: "[1 TO 10]"
			},
			{
				name: "11-30 Condo Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30 Units",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Condo Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50 Units",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Condo Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100 Units",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Condo Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200 Units",
				search_value: "[101 TO 200]"
			},
			{
				name: "201-400 Condo Units",
				filter: "201-to-400",
				dropdown_name: "201 to 400 Units",
				search_value: "[201 TO 400]"
			},
			{
				name: "401 + Condo Units",
				filter: "401-or-more",
				dropdown_name: "401 or more Units",
				search_value: "[401 TO *]"
			}
		]
	},
	{
		name: "Offering Type",
		show_name: true,
		search_key: "offering_construction",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "New Construction",
				filter: "new-construction",
				dropdown_name: "New Construction",
				search_value: "new",
			},
			{
				name: "Conversion",
				filter: "conversion",
				dropdown_name: "Conversion",
				search_value: "conversion",
			},			
			{
				name: "Rehab",
				filter: "rehab",
				dropdown_name: "Rehab",
				search_value: "rehab",
			}
			
		]
	},
	{
		name: "Status",
		show_name: true,
		search_key: "offering_verified_status",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Vetted by Marketproof",
				filter: "vetted",
				dropdown_name: "Vetted by Marketproof",
				search_value: "active"
			},
			{
				name: "Vetting In Progress",
				filter: "in-progress",
				dropdown_name: "Vetting In Progress",
				search_value: "(\"coming soon\" \"offering plan required\" \"condo declaration vetted\")"
			}
		]
	},	
	{
		name: "Added",
		show_name: false,
		search_key: "offering_verified_active_date",
		values: [
			{
				name: "Any Time",
				filter: "",
				dropdown_name: "Any Time",
				search_value: ""
			},
			{
				name: "Recently Added",
				filter: "recently-added",
				dropdown_name: "Recenly Added",
				search_value: "[NOW-6MONTHS TO NOW]"
			}
		]
	},
];


export const _unitFilters = [
	{
		name: "Status",
		show_name: true,
		search_key: "current_status",
		multi_filter: true,
		hide_analysis:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Available (Active or Shadow)",
				filter: "available",
				dropdown_name: "Available (Active or Shadow)",
				search_value: `(((current_status:shadow AND -ownership_type:(*co?op* *coop* !condo*p)) AND -offering_construction:conversion AND property_status:sponsor AND ${recentCohortQuery}) OR (current_status:transfer AND -offering_construction:conversion AND property_status:sponsor AND ${recentCohortQuery})) (active)`
			},
			{
				name: "Shadow",
				filter: "shadow",
				dropdown_name: "Shadow",
				search_value: `(((current_status:shadow AND -ownership_type:(*co?op* *coop* !condo*p)) AND -offering_construction:conversion AND property_status:sponsor AND ${recentCohortQuery}) OR (current_status:transfer AND -offering_construction:conversion AND property_status:sponsor AND ${recentCohortQuery}))`
			},
			
			{
				name: "Active",
				filter: "active",
				dropdown_name: "Active",
				search_value: "(active)"
			},
			{
				name: "Sold (Contract or Closed)",
				filter: "sold",
				dropdown_name: "Sold (Contract or Closed)",
				search_value: "((*contract* OR *pending*) (not_yet_recorded OR *sold* OR closed OR resale) OR (offering_status:sold not_yet_recorded OR transfer) OR (current_status:shadow AND offering_submitted_date:[1971-01-01T00:00:00Z TO 2010-11-01T00:00:00Z]))"
			},
			{
				name: "In Contract",
				filter: "in-contract",
				dropdown_name: "In Contract",
				search_value: "(*contract* OR *pending*)"
			},
			{
				name: "Closed",
				filter: "closed",
				dropdown_name: "Closed",
				search_value: "(not_yet_recorded OR *sold* OR closed OR resale) OR (offering_status:sold not_yet_recorded OR transfer) OR (current_status:shadow AND offering_submitted_date:[* TO 2010-11-01T00:00:00Z])"
			}
		]
	},
	{
		name: "Ownership",
		show_name: true,
		search_key: "ownership_type",
		multi_filter:true,
		hide_building:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "(*condo* !condo*p)",
			},
			{
				name: "Co-op",
				filter: "coop",
				dropdown_name: "Co-op",
				search_value: "(*co?op* *coop* !condo*p)",
			},			
			{
				name: "Condop",
				filter: "condop",
				dropdown_name: "Condop",
				search_value: "(condop*)",
			}
			
		]
	},
	
	{
		name: "Asking Price",
		show_name: true,
		search_key: ["min_price", "max_price"],
		filter_key : "asking_price",
		price_filter: true,
		search_value: ['last_listed_sale_price_all:[{min_price} TO {max_price}]']		
	},
	{
		name: "Asking PPSF",
		show_name: true,
		search_key: ["min_ppsf_price", "max_ppsf_price"],
		filter_key : "asking_ppsf",
		price_filter: true,
		target: 'fq',
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['{!frange l={min_ppsf_price} u={max_ppsf_price}}div(last_listed_sale_price_all,sq_ft)'],
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< $750 PPSF ",
				filter: "less-than-750",
				dropdown_name: "Less than $750",
				search_value: "{!frange l=100 u=750}div(last_listed_sale_price_all,sq_ft)"
			},
			{
				name: "$750 to $1,000 PPSF",
				filter: "75-to-1000",
				dropdown_name: "$750 to $1,000",
				search_value: "{!frange l=751 u=1000}div(last_listed_sale_price_all,sq_ft)"
			},
			{
				name: "$1,000 to $1,500 PPSF",
				filter: "1000-to-1500",
				dropdown_name: "$1,000 to $1,500",
				search_value: "{!frange l=1000 u=1500}div(last_listed_sale_price_all,sq_ft)"
			},
			{
				name: "$1,500 to $2,000 PPSF",
				filter: "1500-to-2000",
				dropdown_name: "$1,500 to $2,000",
				search_value: "{!frange l=1500 u=2000}div(last_listed_sale_price_all,sq_ft)"
			},
			{
				name: "$2,000 to $2,500 PPSF",
				filter: "2000-to-2500",
				dropdown_name: "$2,000 to $2,500",
				search_value: "{!frange l=2000 u=2500}div(last_listed_sale_price_all,sq_ft)"
			},
			{
				name: "$2,500 to $5,000 PPSF",
				filter: "2500-to-5000",
				dropdown_name: "$2,500 to $5,000",
				search_value: "{!frange l=2500 u=5000}div(last_listed_sale_price_all,sq_ft)"
			},
			{
				name: "$5,000+ PPSF",
				filter: "5000-or-more",
				dropdown_name: "$5,000 or more",
				search_value: "{!frange l=5000 u=99999}div(last_listed_sale_price_all,sq_ft)"
			}
		]
	},
	{
		name: "Bedrooms",
		show_name: true,
		search_key: "bedrooms",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Studio",
				filter: "studio",
				dropdown_name: "Studio",
				search_value: "0",
			},
			{
				name: "1 Bedroom",
				filter: "1-bedroom",
				dropdown_name: "1 Bedroom",
				search_value: "1",
			},
			{
				name: "2 Bedroom",
				filter: "2-bedroom",
				dropdown_name: "2 Bedroom",
				search_value: "2",
			},
			{
				name: "3 Bedroom",
				filter: "3-bedroom",
				dropdown_name: "3 Bedroom",
				search_value: "3",
			},
			{
				name: "4+ Bedroom",
				filter: "4-or-more",
				dropdown_name: "4+ Bedroom",
				search_value: "[4 TO *]",
			},
		]
	},
	{
		name: "Amenities",
		show_name: true,
		search_key: "building_features",
		autocomplete: true,
		core_name: "we3-properties",
		fq: ["deleted:false"],
		group_amenities: true,
		hide_building:true,
	},
	{
		name: "Unit Type",
		show_name: true,
		search_key: "property_type",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Residential",
				filter: "residential",
				dropdown_name: "Residential Units",
				search_value: "(*residential*condo* *bulk*condo* condo *co-op* \"condo unit\" \"resident manager unit\" *coop* *affordable* \"\" *dwelling* *timeshare* *residential* *apartment*)"
			},
			{
				name: "Commercial",
				filter: "commercial",
				dropdown_name: "Commercial Units",
				search_value: "(*commercial* *office* *retail*)"
			},
			{
				name: "Storage",
				filter: "storage",
				dropdown_name: "Storage Units",
				search_value: "(*storage*)"
			},
			{
				name: "Parking",
				filter: "parking",
				dropdown_name: "Parking",
				search_value: "(*parking* *garage*)"
			},
			{
				name: "Amenities",
				filter: "amenities",
				dropdown_name: "Amenities",
				search_value: "(*gym* *common* *lobby* *pool* *entertainment*)"
			}
		]
	},
	{
		name: "Baths",
		show_name: true,
		search_key: "baths",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},			
			{
				name: "1 Bath",
				filter: "1-bath",
				dropdown_name: "1 Bath",
				search_value: "1 OR baths:1",
			},
			{
				name: "2 Bath",
				filter: "2-bath",
				dropdown_name: "2 Bath",
				search_value: "2 OR baths:2",
			},
			{
				name: "3 Bath",
				filter: "3-bath",
				dropdown_name: "3 Bath",
				search_value: "3 OR baths:3",
			},
			{
				name: "4+ Bath",
				filter: "4-or-more",
				dropdown_name: "4+ Bath",
				search_value: "[4 TO *] OR baths:[4 TO *]",
			},
		]
	},

	{
		name: "Interior SF",
		show_name: true,
		search_key: ["min_sq_ft", "max_sq_ft"],
		filter_key : "sq_ft",
		price_filter: true,
		labels : ["Min Sq Ft", "Max Sq Ft"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['sq_ft:[{min_sq_ft} TO {max_sq_ft}]'], 
		value_prefix : "",
		title_suffix : "SF",
		values: [
			{
				name: "Any Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< 500 ",
				filter: "less-than-500",
				dropdown_name: "Less than 500 SF",
				search_value: "[10 TO 500] OR sq_ft:[10 TO 500] "
			},
			{
				name: "501 to 750 SF",
				filter: "501-to-750",
				dropdown_name: "501 to 750 SF",
				search_value: "[501 TO 750] OR sq_ft:[501 TO 750] "
			},
			{
				name: "751 to 1,000 SF",
				filter: "751-to-1000",
				dropdown_name: "751 to 1000 SF",
				search_value: "[751 TO 1000] OR sq_ft:[751 TO 1000]"
			},
			{
				name: "1,001 to 1,500 SF",
				filter: "1001-to-1500",
				dropdown_name: "1,001 to 1,500 SF",
				search_value: "[1001 TO 1500] OR sq_ft:[1001 TO 1500]"
			},
			{
				name: "1,501 to 2,000 SF",
				filter: "1501-to-2000",
				dropdown_name: "1,501 to 2,000 SF",
				search_value: "[1501 TO 2000] OR sq_ft:[1501 TO 2000]"
			},
			{
				name: "2,001 to 3,000 SF",
				filter: "2001-to-3000",
				dropdown_name: "2,001 to 3,000 SF",
				search_value: "[2001 TO 3000] OR sq_ft:[2001 TO 3000]"
			},
			{
				name: "3,000+ SF",
				filter: "3001-or-more",
				dropdown_name: "More than 3000 SF",
				search_value: "[3001 TO *] OR sq_ft:[3001 TO *]"
			}
		]
	},
	{
		name: "Exterior SF",
		show_name: true,
		search_key : "offering_exterior_sq_ft",
		multi_filter: true,
/*
		search_key: ["min_ext_sq_ft", "max_ext_sq_ft"],
		filter_key : "ext_sq_ft",
		price_filter: true,
		labels : ["Min Exterior SF", "Max Exterior SF"],
		min_max_values : [50, 500],
		min_max_increment : 50,
		format_type : "number",
		search_value: ['offering_exterior_sq_ft:[{min_ext_sq_ft} TO {max_ext_sq_ft}]'], 
		value_prefix : "",	
		title_suffix : "SF",
*/	
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Has Exterior SF",
				filter: "has-exterior-sf",
				dropdown_name: "Has Exterior SF",
				search_value: "[1 TO *]"
			},
			{
				name: "No Exterior SF",
				filter: "no-exterior-sf",
				dropdown_name: "No Exterior SF",
				search_value: "[* TO 0]"
			},
			{
				name: "< 50 ",
				filter: "less-than-50",
				dropdown_name: "Less than 50 SF",
				search_value: "[0 TO 50]"
			},
			{
				name: "50 to 100 SF",
				filter: "50-to-100",
				dropdown_name: "50 to 100 SF",
				search_value: "[51 TO 100]"
			},
			{
				name: "101 to 200 SF",
				filter: "100-to-200",
				dropdown_name: "101 to 200 SF",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 to 500 SF",
				filter: "200-to-500",
				dropdown_name: "201 to 500 SF",
				search_value: "[201 TO 500]"
			},			
			{
				name: "500+ SF",
				filter: "500-more",
				dropdown_name: "More than 500 SF",
				search_value: "[501 TO *]"
			}
		]
	},
	{
		name: "Floors",
		show_name: true,
		search_key: ["min_floor", "max_floor"],
		filter_key : "floor",
		price_filter: true,
		// target: 'fq',
		labels : ["Min Floor", "Max Floor"],

		custom_min_max_values : {
			"min" : [1, 11, 21, 31, 41],
			"max" : [10, 20, 30, 40, 50, "No Max"]

		},	
		no_max:true,
		format_type : "number",
		search_value: ['floor:[{min_floor} TO {max_floor}]']		,
		format_type : "number",
		value_prefix : "",	
		
		
	},
	/*{
		name: "Offering Discount",
		show_name: true,
		search_key: "offering_discount",
		target: 'fq',
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Less than 1%",
				filter: "less-than-sold",
				dropdown_name: "Asking Less than Sold",
				search_value: "{!frange l=-0.01 u=0}div(sub(initial_offering_price,last_listed_sale_price_all),initial_offering_price))"
			},
			{
				name: "$750 to $1,000 PPSF",
				filter: "75-to-1000",
				dropdown_name: "$750 to $1,000",
				search_value: "{!frange l=751 u=1000}div(last_listed_sale_price_all,sq_ft)"
			},
			{
				name: "$1,000 to $1,500 PPSF",
				filter: "1000-to-1500",
				dropdown_name: "$1,000 to $1,500",
				search_value: "{!frange l=1000 u=1500}div(last_listed_sale_price_all,sq_ft)"
			},
			{
				name: "$1,500 to $2,000 PPSF",
				filter: "1500-to-2000",
				dropdown_name: "$1,500 to $2,000",
				search_value: "{!frange l=1500 u=2000}div(last_listed_sale_price_all,sq_ft)"
			},
			{
				name: "$2,000 to $2,500 PPSF",
				filter: "2000-to-2500",
				dropdown_name: "$2,000 to $2,500",
				search_value: "{!frange l=2000 u=2500}div(last_listed_sale_price_all,sq_ft)"
			},
			{
				name: "$2,500 to $5,000 PPSF",
				filter: "2500-to-5000",
				dropdown_name: "$2,500 to $5,000",
				search_value: "{!frange l=2500 u=5000}div(last_listed_sale_price_all,sq_ft)"
			},
			{
				name: "$5,000+ PPSF",
				filter: "5000-or-more",
				dropdown_name: "$5,000 or more",
				search_value: "{!frange l=5000 u=99999}div(last_listed_sale_price_all,sq_ft)"
			}
		]
	},*/
		
	
	{
		name: "Sold Date",
		show_name: true,
		filter_key: "sold_date",
		search_key: ["min_date", "max_date"],
		date_filter: true,
		search_value: ['last_sold_date_formatted:[{min_date} TO {max_date}]']		
	},
	{
		name: "Contract Date",
		show_name: true,
		filter_key: "contract_date",
		search_key: ["min_contract_date", "max_contract_date"],
		date_filter: true,
		search_value: ['last_contract_date_formatted:[{min_contract_date} TO {max_contract_date}]']		
	},
	{
		name: "Sold Price",
		show_name: true,
		search_key: ["min_sold_price", "max_sold_price"],
		filter_key : "sold_price",
		price_filter: true,
		search_value: ['last_sold_price:[{min_sold_price} TO {max_sold_price}]']		
	},
	{
		name: "Sold PPSF",
		show_name: true,
		search_key: ["min_sold_ppsf_price", "max_sold_ppsf_price"],
		filter_key : "sold_ppsf",
		price_filter: true,
		target: 'fq',
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['{!frange l={min_sold_ppsf_price} u={max_sold_ppsf_price}}div(last_sold_price,sq_ft)'],
		
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< $750 PPSF ",
				filter: "less-than-750",
				dropdown_name: "Less than $750",
				search_value: "{!frange l=100 u=750}div(last_sold_price,sq_ft)"
			},
			{
				name: "$750 to $1,000 PPSF",
				filter: "75-to-1000",
				dropdown_name: "$750 to $1,000",
				search_value: "{!frange l=751 u=1000}div(last_sold_price,sq_ft)"
			},
			{
				name: "$1,000 to $1,500 PPSF",
				filter: "1000-to-1500",
				dropdown_name: "$1,000 to $1,500",
				search_value: "{!frange l=1000 u=1500}div(last_sold_price,sq_ft)"
			},
			{
				name: "$1,500 to $2,000 PPSF",
				filter: "1500-to-2000",
				dropdown_name: "$1,500 to $2,000",
				search_value: "{!frange l=1500 u=2000}div(last_sold_price,sq_ft)"
			},
			{
				name: "$2,000 to $2,500 PPSF",
				filter: "2000-to-2500",
				dropdown_name: "$2,000 to $2,500",
				search_value: "{!frange l=2000 u=2500}div(last_sold_price,sq_ft)"
			},
			{
				name: "$2,500 to $5,000 PPSF",
				filter: "2500-to-5000",
				dropdown_name: "$2,500 to $5,000",
				search_value: "{!frange l=2500 u=5000}div(last_sold_price,sq_ft)"
			},
			{
				name: "$5,000+ PPSF",
				filter: "5000-or-more",
				dropdown_name: "$5,000 or more",
				search_value: "{!frange l=5000 u=99999}div(last_sold_price,sq_ft)"
			}
		]
	},
	{
		name: "Unit Number",
		show_name: true,
		search_key: "address_2_normalized",
		text_filter: true
	},
	{
		name: "Special Pricing",
		show_name: true,
		search_key: "special_pricing",
		target: 'fq',
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Asking Below Sold",
				filter: "below-sold",
				dropdown_name: "Asking Below Sold",
				search_value: "{!frange l=1 u=1}if(lt(last_listed_sale_price_all,last_sold_price),1,0)",
				additional_fq : ["{!frange l=1 u=1}if(gt(last_listed_sale_date_all,last_sold_date),1,0)"]
			},
			{
				name: "Unpriced Units",
				filter: "unpriced-units",
				dropdown_name: "Unpriced Units",
				search_value: "last_listed_sale_price_all:[* TO -1] AND source_organizations:NYAG",
			}
		]
	},
	{
		name: "Carrying Cost",
		show_name: true,
		search_key: ["min_carry_cost", "max_carry_cost"],
		filter_key : "monthly_carrying_charges",
		price_filter: true,
		labels : ["Min Carry Cost", "Max Carry Cost"],
		min_max_values : [500, 5000],
		min_max_increment : 500,
		format_type : "number",		
		search_value: ['offering_monthly_carrying_charges:[{min_carry_cost} TO {max_carry_cost}]'], 	
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< $1,000",
				filter: "less-than-1000",
				dropdown_name: "Less than $1000",
				search_value: "[0 TO 1000] OR monthly_fees:[0 TO 1000]"
			},
			{
				name: "$1,000 to $1,500",
				filter: "1001-to-1501",
				dropdown_name: "$1,000 to $1,500",
				search_value: "[1001 TO 1500] OR monthly_fees:[1001 TO 1500]"
			},
			{
				name: "$1,500 to $2,000",
				filter: "1501-to-2001",
				dropdown_name: "$1,500 to $2,000",
				search_value: "[1501 TO 2000] OR monthly_fees:[1501 TO 2000]"
			},
			{
				name: "$2,000 to $3,000",
				filter: "2001-to-3001",
				dropdown_name: "$2,000 to $3,000",
				search_value: "[2001 TO 3000] OR monthly_fees:[2001 TO 3000]"
			},
			{
				name: "$3,000 to $5,000",
				filter: "3,000-to-5000",
				dropdown_name: "$3,000 to $5,000",
				search_value: "[3001 TO 5000] OR monthly_fees:[3001 TO 5000]"
			},
			{
				name: "$5,000+",
				filter: "5000-or-more",
				dropdown_name: "$5,000 or more",
				search_value: "[5001 TO *] OR monthly_fees:[5001 TO *]"
			}
		]
	},
	{
		name: "Owner Type",
		show_name: true,
		search_key: "owner",
		multi_filter: true,
		values: [
			{
				name: "Absentee Owner",
				filter: "absentee",
				dropdown_name: "Absentee Owner",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=dof-tax-bills to=alternate_keys}absentee_owner:true",
				exclude_sold_within_days: 365
			},
			{
				name: "Original Owner",
				filter: "original",
				dropdown_name: "Original Owner",
				search_value: "* AND times_exchanged:[0 TO 1]"
			},
			{
				name: "Individual Owner",
				filter: "individual",
				dropdown_name: "Individual Owner",
				search_value: "(*) AND -owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c *LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C)"
			},
			{
				name: "Corporate Owner",
				filter: "corporate",
				dropdown_name: "Corporate Owner",
				search_value: "(*LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C) OR owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)"
			},
			{
				name: "Unmasked LLC Owner",
				filter: "unmasked",
				dropdown_name: "Unmasked LLC Owner",
				search_value: "(*LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C) OR owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)",
				additional_fq: "{!join from=property fromIndex=acris-signatories to=alternate_keys}signatory_name:*"
			},
			{
				name: "Unmasked LLC (Non-Attorney)",
				filter: "unmasked-no-attorney",
				dropdown_name: "Unmasked LLC (Non-Attorney)",
				search_value: "(*LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C) OR owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)",
				additional_fq: "{!join from=property fromIndex=acris-signatories to=alternate_keys}(signatory_name:* AND -signatory_attorney_bar_registration_number:*)"
			},
			{
				name: "Unmasked LLC (Attorney)",
				filter: "unmasked-attorney",
				dropdown_name: "Unmasked LLC (Attorney)",
				search_value: "(*LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C) OR owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)",
				additional_fq: "{!join from=property fromIndex=acris-signatories to=alternate_keys}signatory_attorney_bar_registration_number:*"
			}
		]
	},
	{
		"name": "Distress",
		"show_name": true,
		"search_key": "property_distress",
		"target": "fq",
		"multi_filter": true,
		"minWidth": 200,
		"join_query": "{!join method=crossCollection from=property fromIndex=we3-acris-real-property to=key}",
		"values": [
			{
				"name": "Any Lien",
				"filter": "any_liens",
				"dropdown_name": "Any Lien (Last 5 Years)",
				"search_value": "(doc_type:(fl ftl tls ntxl dpftl judg locc tolcc brup ucc1 ucc3) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Tax & Gov Liens",
				"filter": "gov_liens",
				"dropdown_name": "Tax & Gov Liens (Last 5 Years)",
				"search_value": "(doc_type:(fl ftl tls ntxl dpftl judg) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Common & Mech. Liens",
				"filter": "common_mechanics_liens",
				"dropdown_name": "Common & Mech. Liens (Last 5 Years)",
				"search_value": "(doc_type:(locc tolcc) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Bankruptcy & Debt Liens",
				"filter": "bankruptcy_liens",
				"dropdown_name": "Bankruptcy & Debt Liens (Last 5 Years)",
				"search_value": "(doc_type:(brup ucc1 ucc3) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Lis Pendens",
				"filter": "lis_pendens",
				"dropdown_name": "Lis Pendens (Foreclosure Filing)",
				"search_value": "",
				"additional_fq": "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}*:*"
			}
		]
	},
	{
		name: "Condo Units",
		show_name: true,
		search_key: "inventory_total",
		filter_type: "building",
		multi_filter: true,
		values: [
			{
				name: "Any Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-10 Condo Units",
				filter: "1-to-10",
				dropdown_name: "1 to 10 Units",
				search_value: "[1 TO 10]"
			},
			{
				name: "11-30 Condo Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30 Units",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Condo Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50 Units",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Condo Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100 Units",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Condo Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200 Units",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 + Condo Units",
				filter: "201-or-more",
				dropdown_name: "201 or more Units",
				search_value: "[201 TO *]"
			}
		]
	},
	{
		name: "Unsold Units",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "inventory_unsold",
		filter_type: "building",
		multi_filter: true,
		values: [
			{
				name: "Any Unsold Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-10 Unsold Units",
				filter: "1-to-10",
				dropdown_name: "1 to 10",
				search_value: "[1 TO 10]"
			},
			{
				name: "11-30 Unsold Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Unsold Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Unsold Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Unsold Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 + Unsold Units",
				filter: "201-or-more",
				dropdown_name: "201 or more",
				search_value: "[201 TO *]"
			}
		]
	},
	{
		name: "Starting From",
		show_name: true,
		search_key: "inventory_stats_listings_low_price",
		filter_type: "building",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "From: < $500,000 ",
				filter: "less-than-500",
				dropdown_name: "Less than $500K",
				search_value: "[100 TO 500000]"
			},
			{
				name: "From: $500K to $750K",
				filter: "500-to-750",
				dropdown_name: "$500K to $750K",
				search_value: "[500000 TO 750000]"
			},
			{
				name: "From: $750K to $1M",
				filter: "750-to-1000",
				dropdown_name: "$750K to $1M",
				search_value: "[750001 TO 1000000]"
			},
			{
				name: "From: $1M to $1.5M",
				filter: "1000-to-1500",
				dropdown_name: "$1M to $1.5M",
				search_value: "[1000001 TO 1500000]"
			},
			{
				name: "From: $1.5M to $2M",
				filter: "1500-to-2000",
				dropdown_name: "$1.5M to $2M",
				search_value: "[1500001 TO 2000000]"
			},
			{
				name: "From: $2M+",
				filter: "2000+",
				dropdown_name: "$2M+",
				search_value: "[2000000 TO *]"
			}
		]
	},
	{
		name: "% Sold",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "inventory_pct_sold",
		filter_type: "building",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Has Sales",
				filter: "has-sales",
				dropdown_name: "Has Sales",
				search_value: "[1 TO *] OR offering_verified_status:\"sold out\""
			},
			{
				name: "Has No Sales",
				filter: "no-sales",
				dropdown_name: "Has No Sales",
				search_value: "[* TO 0] AND -offering_verified_status:\"sold out\""
			},
			{
				name: "Less Than 15%",
				filter: "0-to-15",
				dropdown_name: "Less Than 15%",
				search_value: "[0 TO 15]"
			},
			{
				name: "Less Than 35%",
				filter: "0-to-35",
				dropdown_name: "Less Than 35%",
				search_value: "[0 TO 34]"
			},
			{
				name: "Less Than 50%",
				filter: "0-to-49",
				dropdown_name: "Less Than 50%",
				search_value: "[0 TO 49]"
			},
			{
				name: "More Than 15%",
				filter: "15-to-100",
				dropdown_name: "More Than 15%",
				search_value: "[15 TO *]"
			},
			{
				name: "More Than 50%",
				filter: "50-to-100",
				dropdown_name: "More Than 50%",
				search_value: "[50 TO *]"
			},
			{
				name: "50% to 79%",
				filter: "50-to-79",
				dropdown_name: "50% to 79%",
				search_value: "[50 TO 79]"
			},
			{
				name: "80% to 99%",
				filter: "80-to-99",
				dropdown_name: "80% to 99%",
				search_value: "[80 TO 99]"
			},
			{
				name: "Not Sold Out",
				filter: "not-sold-out",
				dropdown_name: "Not Sold Out",
				search_value: "[* TO 99] AND -offering_verified_status:\"sold out\""
			},
			{
				name: "Sold Out",
				filter: "sold-out",
				dropdown_name: "Sold Out",
				search_value: "[100 TO *]"
			},
			
		]
	},
	{
		name: "Construction",
		show_name: true,
		search_key: "building_effective_start",
		filter_type: "building",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Move-in Ready",
				filter: "completed",
				dropdown_name: "Move-in Ready (Has CO/TCO)",
				search_value: "[NOW-25YEARS TO NOW] OR building_status:(resale) OR offering_verified_status:(\"sold out\") OR first_closing_date:[NOW-25YEARS TO NOW]",
			},
			{
				name: "Under Construction",
				filter: "not-completed",
				dropdown_name: "Under Construction (No CO/TCO)",
				search_value: "[* TO NOW-25YEARS] AND -building_status:(resale) AND -offering_verified_status:(\"sold out\") AND first_closing_date:[* TO NOW-25YEARS]",
			}
			
		]
	},	
	{
		name: "Offering Type",
		show_name: true,
		search_key: "offering_construction",
		filter_type: "building",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "New Construction",
				filter: "new-construction",
				dropdown_name: "New Construction",
				search_value: "new",
			},
			{
				name: "Conversion",
				filter: "conversion",
				dropdown_name: "Conversion",
				search_value: "conversion",
			},			
			{
				name: "Rehab",
				filter: "rehab",
				dropdown_name: "Rehab",
				search_value: "rehab",
			},
			{
				name: "Exclude Conversion",
				filter: "exclude-conversion",
				dropdown_name: "Exclude Conversion",
				search_value: "(new rehab \"\")",
			}
			
		]
	},	
	// {
	// 	name: "Ownership",
	// 	show_name: true,
	// 	search_key: "ownership_type",
	// 	values: [
	// 		{
	// 			name: "Any",
	// 			filter: "",
	// 			dropdown_name: "Any",
	// 			search_value: ""
	// 		},
	// 		{
	// 			name: "Condo",
	// 			filter: "condo",
	// 			dropdown_name: "Condo",
	// 			search_value: "condo AND -ownership_type:cooperative",
	// 		},
	// 		{
	// 			name: "Co-op",
	// 			filter: "co-op",
	// 			dropdown_name: "Co-op",
	// 			search_value: "coop",
	// 		}
	// 		
	// 	]
	// },
	{
		name: "Condo Dec",
		show_name: true,
		exclude_stage: ["pipeline", "resale"],
		search_key: "cdec_date",
		filter_type: "building",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "No Condo Dec",
				filter: "no-cdec",
				dropdown_name: "No Condo Dec",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z] AND building_status:(pipeline sponsor)",
			},
			{
				name: "Has Condo Dec",
				filter: "has-cdec",
				dropdown_name: "Has Condo Dec",
				search_value: "[1970-01-05T00:00:00Z TO NOW] OR building_status:(resale)",
			}		
		]
	},
	{
		name: "Year Built",
		show_name: true,
		exclude_stage: ["pipeline"],
		format_type : "number",
		search_key: ["min_year_built", "max_year_built"],
		filter_key : "year_built",
		price_filter: true,
		labels : ["Min Year Built", "Max Year Built"],
		min_max_values : [1900, current_year],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [1900, 1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020],
			"max" : [1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020, current_year]
		},		
		format_type : "year",
		search_value: ['year_built:[{min_year_built} TO {max_year_built}]'], 
		value_prefix : "",	
		title_suffix : ""
	},
	{
		name: "Sales Started",
		show_name: true,
		search_key: ["min_start_date", "max_start_date"],
		exclude_stage: ["pipeline"],
		filter_type: "building",
		fiter_key: "sales_start_date",
		date_filter: true,
		search_value: ['sales_start_date:[{min_start_date} TO {max_start_date}]']		
	},
	
	{
		name: "Offering Submitted",
		show_name: true,
		search_key: ["min_submitted_date", "max_submitted_date"],
		date_filter: true,
		filter_type: "building",
		fiter_key: "offering_submitted_date",
		search_value: ['offering_submitted_date:[{min_submitted_date} TO {max_submitted_date}]']		
	},
	{
		name: "Offering Accepted",
		show_name: true,
		search_key: ["min_accepted_date", "max_accepted_date"],
		date_filter: true,
		filter_type: "building",
		fiter_key: "offering_accepted_date",
		search_value: ['offering_accepted_date:[{min_accepted_date} TO {max_accepted_date}]']		
	},
	{
		name: "Offering Effective",
		show_name: true,
		search_key: ["min_effective_date", "max_effective_date"],
		date_filter: true,
		filter_type: "building",
		fiter_key: "offering_effective_date",
		search_value: ['offering_effective_date:[{min_effective_date} TO {max_effective_date}]']		
	},
	// {
	// 	name: "Sales Started",
	// 	show_name: true,
	// 	exclude_stage: ["pipeline"],
	// 	search_key: "sales_start_date",
	// 	filter_type: "building",
	// 	multi_filter: true,
	// 	values: [
	// 		{
	// 			name: "Any",
	// 			filter: "",
	// 			dropdown_name: "Any",
	// 			search_value: ""
	// 		},
	// 		{
	// 			name: "Not Started",
	// 			filter: "not-started",
	// 			dropdown_name: "Not Started",
	// 			search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z]",
	// 		},
	// 		{
	// 			name: "Has Started",
	// 			filter: "has-started",
	// 			dropdown_name: "Has Started",
	// 			search_value: "[NOW-15YEARS TO NOW]",
	// 		},
	// 		{
	// 			name: "Within 1 Year",
	// 			filter: "1-year",
	// 			dropdown_name: "Within 1 Year",
	// 			search_value: "[NOW-1YEARS TO NOW]",
	// 		},
	// 		{
	// 			name: "1 to 2 Years",
	// 			filter: "2-year",
	// 			dropdown_name: "Last 1 to 2 Years",
	// 			search_value: "[NOW-2YEARS TO NOW-1YEARS]",
	// 		},
	// 		{
	// 			name: "2 to 5 Years",
	// 			filter: "2-5-year",
	// 			dropdown_name: "Last 2 to 5 Years",
	// 			search_value: "[NOW-5YEARS TO NOW-2YEARS]",
	// 		},
	// 		{
	// 			name: "5 to 7 Years",
	// 			filter: "5-7-year",
	// 			dropdown_name: "Last 5 to 7 Years",
	// 			search_value: "[NOW-7YEARS TO NOW-5YEARS]",
	// 		},
	// 		{
	// 			name: "7 to 10 Years",
	// 			filter: "7-10-year",
	// 			dropdown_name: "Last 5 to 10 Years",
	// 			search_value: "[NOW-10YEARS TO NOW-7YEARS]",
	// 		},
	// 		{
	// 			name: "10 to 15 Years",
	// 			filter: "10-15-year",
	// 			dropdown_name: "Last 10 to 15 Years",
	// 			search_value: "[NOW-15YEARS TO NOW-10YEARS]",
	// 		},
	// 		{
	// 			name: "More Than 15 Years",
	// 			filter: "more-15-year",
	// 			dropdown_name: "More Than 15 Years Ago",
	// 			search_value: "[NOW-20YEARS TO NOW-15YEARS]",
	// 		}				
	// 	]
	// },
	{
		name: "Building Last Listing",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "last_listed_sale_date_formatted",
		filter_type: "building",
		search_key: ["min_listed_date", "max_listed_date"],
		date_filter: true,
		search_value: ['last_listed_sale_date_formatted:[{min_listed_date} TO {max_listed_date}]'],
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "No Activity",
				filter: "no-activity",
				dropdown_name: "No Activity",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z]",
			},
			{
				name: "Any Activity",
				filter: "any-activity",
				dropdown_name: "Any Activity",
				search_value: "[NOW-15YEARS TO NOW]",
			},
			{
				name: "Within 1 Year",
				filter: "1-year",
				dropdown_name: "Within 1 Year",
				search_value: "[NOW-1YEARS TO NOW]",
			},
			{
				name: "1 to 2 Years",
				filter: "2-year",
				dropdown_name: "Last 1 to 2 Years",
				search_value: "[NOW-2YEARS TO NOW-1YEARS]",
			},
			{
				name: "2 to 5 Years",
				filter: "2-5-year",
				dropdown_name: "Last 2 to 5 Years",
				search_value: "[NOW-5YEARS TO NOW-2YEARS]",
			},
			{
				name: "More Than 5 Years",
				filter: "more-5-year",
				dropdown_name: "More Than 5 Years Ago",
				search_value: "[NOW-15YEARS TO NOW-5YEARS]",
			}			
		]
	},
	{
		name: "Building Last Sale",
		show_name: true,
		exclude_stage: ["pipeline"],
		filter_type: "building",
		multi_filter:true,
		search_key: ["min_sold_date", "max_sold_date"],
		date_filter: true,
		search_value: ['last_sold_date_formatted:[{min_sold_date} TO {max_sold_date}]'],
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "No Activity",
				filter: "no-activity",
				dropdown_name: "No Activity",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z] AND -offering_verified_status:\"sold out\"",
			},
			{
				name: "Any Activity",
				filter: "any-activity",
				dropdown_name: "Any Activity",
				search_value: "[NOW-15YEARS TO NOW] OR offering_verified_status:\"sold out\"",
			},
			{
				name: "Within 1 Year",
				filter: "1-year",
				dropdown_name: "Within 1 Year",
				search_value: "[NOW-1YEARS TO NOW]",
			},
			{
				name: "1 to 2 Years",
				filter: "2-year",
				dropdown_name: "Last 1 to 2 Years",
				search_value: "[NOW-2YEARS TO NOW-1YEARS]",
			},
			{
				name: "2 to 5 Years",
				filter: "2-5-year",
				dropdown_name: "Last 2 to 5 Years",
				search_value: "[NOW-5YEARS TO NOW-2YEARS]",
			},
			{
				name: "More Than 5 Years",
				filter: "more-5-year",
				dropdown_name: "More Than 5 Years Ago",
				search_value: "[NOW-15YEARS TO NOW-5YEARS]",
			}			
		]
	},	
/*
	{
		name: "Source",
		show_name: false,
		search_key: "source_organizations",
		default_name: 'All Sources',
		building_hide: false,
		admin_only: true,
		values: [
			{
				name: "All",
				filter: "",
				dropdown_name: "All",
				search_value: ""
			},
			{
				name: "Offering Plan (NYAG)",
				filter: "nyag",
				dropdown_name: "Offering Plan (NYAG)",
				search_value: "NYAG",
			},
			{
				name: "Condo Declaration (CDEC)",
				filter: "cdec",
				dropdown_name: "Condo Declaration (CDEC)",
				search_value: "CDEC",
			},
			{
				name: "Amended Condo Declaration (CDEC)",
				filter: "adec",
				dropdown_name: "Amended Condo Declaration (CDEC)",
				search_value: "ADEC",
			},
			{
				name: "City Records",
				filter: "city-records",
				dropdown_name: "City Records",
				search_value: '* AND (verification_source:* OR secondary_normalization_grade:A) AND (property_type:("single residential condo unit" OR "single residential coop unit" OR "condo unit" OR "coop unit" OR "co-op unit" OR "condop unit"))',
			}
		]
	},
*/
	{
		// Same control as Owner Outreach — see _patFmvQuery at the top of this file for the
		// thresholds and what the join can and cannot reach (co-op units cannot be matched).
		name: "Pied-à-Terre Surcharge",
		show_name: true,
		search_key: "pat_value",
		target: "fq",
		minWidth: 220,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "All Types",
				filter: "pat_all",
				dropdown_name: "All Types",
				search_value: `${_patCCCQuery} OR ${_patHouseQuery}`
			},
			{
				name: "Condo/Co-op $1M+",
				filter: "pat_ccc",
				dropdown_name: "Condo/Co-op $1M+",
				search_value: _patCCCQuery
			},
			{
				name: "1–3 Family $5M+",
				filter: "pat_house",
				dropdown_name: "1–3 Family $5M+",
				search_value: _patHouseQuery
			},
		]
	},

];

export const _ownerOutreachFilters = [
	
	{
		name: "Property Type",
		show_name: true,
		search_key: "ownership_type",
		multi_filter:true,
		hide_building:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "(*condo* !condo*p)",
			},
			{
				name: "Co-op",
				filter: "coop",
				dropdown_name: "Co-op",
				search_value: "(*co?op* *coop* !condo*p)",
			},			
			{
				name: "Condop",
				filter: "condop",
				dropdown_name: "Condop",
				search_value: "(condop*)",
			},
			{
				name: "Single-Family",
				filter: "single-family",
				dropdown_name: "Single-Family",
				search_value: "(single?family)",
			},			
			{
				name: "Multi-Family",
				filter: "multi-family",
				dropdown_name: "Multi-Family",
				search_value: "(multi?family)",
			}		
			
		]
	},
	{
		name: "Status",
		show_name: true,
		search_key: "current_status",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Active",
				filter: "active",
				dropdown_name: "Active",
				search_value: "(active)"
			},
			{
				name: "Coming Soon",
				filter: "coming-soon",
				dropdown_name: "Coming Soon",
				search_value: "(\"coming soon\")"
			},
			{
				name: "In Contract",
				filter: "in-contract",
				dropdown_name: "In Contract",
				search_value: "(\"in contract\")"
			},
			{
				name: "Off Market",
				filter: "off-market",
				dropdown_name: "Off Market",
				search_value: "(sold shadow not_yet_recorded transfer \"bulk sold\" \"\" \"never listed\")"
			}
		]
	},
	{
		name: "Sold Date",
		show_name: true,
		filter_key: "sold_date",
		search_key: ["min_date", "max_date"],
		date_filter: true,
		search_value: ['last_sold_date_formatted:[{min_date} TO {max_date}]']		
	},
	{
		name: "Sold Price",
		show_name: true,
		search_key: ["min_sold_price", "max_sold_price"],
		filter_key : "sold_price",
		price_filter: true,
		search_value: ['last_sold_price:[{min_sold_price} TO {max_sold_price}]']		
	},
	{
		name: "Sold PPSF",
		show_name: true,
		search_key: ["min_sold_ppsf_price", "max_sold_ppsf_price"],
		filter_key : "sold_ppsf",
		price_filter: true,
		target: 'fq',
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['{!frange l={min_sold_ppsf_price} u={max_sold_ppsf_price}}div(last_sold_price,sq_ft)'],
		
	},
	
	
	{
		name: "Bedrooms",
		show_name: true,
		search_key: "bedrooms",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Studio",
				filter: "studio",
				dropdown_name: "Studio",
				search_value: "0",
			},
			{
				name: "1 Bedroom",
				filter: "1-bedroom",
				dropdown_name: "1 Bedroom",
				search_value: "1",
			},
			{
				name: "2 Bedroom",
				filter: "2-bedroom",
				dropdown_name: "2 Bedroom",
				search_value: "2",
			},
			{
				name: "3 Bedroom",
				filter: "3-bedroom",
				dropdown_name: "3 Bedroom",
				search_value: "3",
			},
			{
				name: "4+ Bedroom",
				filter: "4-or-more",
				dropdown_name: "4+ Bedroom",
				search_value: "[4 TO *]",
			},
		]
	},
	
	{
		name: "Owner Type",
		show_name: true,
		search_key: "owner",
		multi_filter: true,
		values: [
			{
				name: "Absentee Owner",
				filter: "absentee",
				dropdown_name: "Absentee Owner",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=dof-tax-bills to=alternate_keys}absentee_owner:true",
				exclude_sold_within_days: 365
			},
			{
				name: "Exclude Absentee",
				filter: "exclude-absentee",
				dropdown_name: "Exclude Absentee",
				search_value: "",
				additional_fq: "-{!join from=property fromIndex=dof-tax-bills to=alternate_keys}absentee_owner:true"
			},
			{
				name: "Original Owner",
				filter: "original",
				dropdown_name: "Original Owner",
				search_value: "* AND times_exchanged:[0 TO 1]"
			},
			{
				name: "Exclude Original Owner",
				filter: "exclude-original",
				dropdown_name: "Exclude Original Owner",
				search_value: "* AND times_exchanged:[2 TO *]"
			},
			{
				name: "Individual Owner",
				filter: "individual",
				dropdown_name: "Individual Owner",
				search_value: "(*) AND -owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c *LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C)"
			},
			{
				name: "Exclude Individual Owner",
				filter: "exclude-individual",
				dropdown_name: "Exclude Individual Owner",
				search_value: "(*LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C) OR owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)"
			},
			{
				name: "Corporate Owner",
				filter: "corporate",
				dropdown_name: "Corporate Owner",
				search_value: "(*LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C) OR owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)"
			},
			{
				name: "Exclude Corporate Owner",
				filter: "exclude-corporate",
				dropdown_name: "Exclude Corporate Owner",
				search_value: "(*) AND -owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c *LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C)"
			},
			{
				name: "Unmasked LLC Owner",
				filter: "unmasked",
				dropdown_name: "Unmasked LLC Owner",
				search_value: "(*LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C) OR owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)",
				additional_fq: "{!join from=property fromIndex=acris-signatories to=alternate_keys}signatory_name:*"
			},
			{
				name: "Unmasked LLC (Non-Attorney)",
				filter: "unmasked-no-attorney",
				dropdown_name: "Unmasked LLC (Non-Attorney)",
				search_value: "(*LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C) OR owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)",
				additional_fq: "{!join from=property fromIndex=acris-signatories to=alternate_keys}(signatory_name:* AND -signatory_attorney_bar_registration_number:*)"
			},
			{
				name: "Unmasked LLC (Attorney)",
				filter: "unmasked-attorney",
				dropdown_name: "Unmasked LLC (Attorney)",
				search_value: "(*LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C) OR owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)",
				additional_fq: "{!join from=property fromIndex=acris-signatories to=alternate_keys}signatory_attorney_bar_registration_number:*"
			}
		]
	},
	{
		name: "Baths",
		show_name: true,
		search_key: "baths",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},			
			{
				name: "1 Bath",
				filter: "1-bath",
				dropdown_name: "1 Bath",
				search_value: "1 OR baths:1",
			},
			{
				name: "2 Bath",
				filter: "2-bath",
				dropdown_name: "2 Bath",
				search_value: "2 OR baths:2",
			},
			{
				name: "3 Bath",
				filter: "3-bath",
				dropdown_name: "3 Bath",
				search_value: "3 OR baths:3",
			},
			{
				name: "4+ Bath",
				filter: "4-or-more",
				dropdown_name: "4+ Bath",
				search_value: "[4 TO *] OR baths:[4 TO *]",
			},
		]
	},
	
	

	{
		name: "Interior SF",
		show_name: true,
		search_key: ["min_sq_ft", "max_sq_ft"],
		filter_key : "sq_ft",
		price_filter: true,
		labels : ["Min Sq Ft", "Max Sq Ft"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['sq_ft:[{min_sq_ft} TO {max_sq_ft}]'], 
		value_prefix : "",
		title_suffix : "SF"
	},
	{
		name: "Exterior SF",
		show_name: true,
		search_key : "offering_exterior_sq_ft",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Has Exterior SF",
				filter: "has-exterior-sf",
				dropdown_name: "Has Exterior SF",
				search_value: "[1 TO *]"
			},
			{
				name: "No Exterior SF",
				filter: "no-exterior-sf",
				dropdown_name: "No Exterior SF",
				search_value: "[* TO 0]"
			},
			{
				name: "< 50 ",
				filter: "less-than-50",
				dropdown_name: "Less than 50 SF",
				search_value: "[0 TO 50]"
			},
			{
				name: "50 to 100 SF",
				filter: "50-to-100",
				dropdown_name: "50 to 100 SF",
				search_value: "[51 TO 100]"
			},
			{
				name: "101 to 200 SF",
				filter: "100-to-200",
				dropdown_name: "101 to 200 SF",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 to 500 SF",
				filter: "200-to-500",
				dropdown_name: "201 to 500 SF",
				search_value: "[201 TO 500]"
			},			
			{
				name: "500+ SF",
				filter: "500-more",
				dropdown_name: "More than 500 SF",
				search_value: "[501 TO *]"
			}
		]

	},
	{
		name: "Amenities",
		show_name: true,
		search_key: "building_features",
		autocomplete: true,
		core_name: "we3-properties",
		fq: ["deleted:false"],
		group_amenities: true,
		hide_building:true,
	},
	{
		name: "Unit Type",
		show_name: true,
		search_key: "property_type",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Residential",
				filter: "residential",
				dropdown_name: "Residential Units",
				search_value: "(*residential*condo* *bulk*condo* condo *co-op* \"condo unit\" \"resident manager unit\" *coop* *affordable* \"\" *dwelling* *timeshare* *residential* *apartment* *family* sc d2)"
			},
			{
				name: "Commercial",
				filter: "commercial",
				dropdown_name: "Commercial Units",
				search_value: "(*commercial* *office* *retail*)"
			},
			{
				name: "Storage",
				filter: "storage",
				dropdown_name: "Storage Units",
				search_value: "(*storage*)"
			},
			{
				name: "Parking",
				filter: "parking",
				dropdown_name: "Parking",
				search_value: "(*parking* *garage*)"
			},
			{
				name: "Amenities",
				filter: "amenities",
				dropdown_name: "Amenities",
				search_value: "(*gym* *common* *lobby* *pool* *entertainment*)"
			}
		]
	},
	{
		name: "Owner Name",
		show_name: true,
		search_key: "owner_name",
		text_filter: true,
		search_fields: ["owner"]
	},
	{
		name: "Floor",
		show_name: true,
		search_key: ["min_floor", "max_floor"],
		filter_key : "floor",
		price_filter: true,
		// target: 'fq',
		labels : ["Min Floor", "Max Floor"],

		custom_min_max_values : {
			"min" : [1, 11, 21, 31, 41],
			"max" : [10, 20, 30, 40, 50, "No Max"]

		},	
		no_max:true,
		format_type : "number",
		search_value: ['floor:[{min_floor} TO {max_floor}]']		,
		format_type : "number",
		value_prefix : "",	
		
		
	},
	{
		name: "Last Listed for Sale",
		show_name: true,
		exclude_stage: ["pipeline"],
		filter_key: "last_listed_sale",
		search_key: "last_listed_sale_date_formatted",
		search_key: ["min_last_listed_date", "max_last_listed_date"],
		date_filter: true,
		unix_date:true,
		search_value: ['last_listed_sale_date_all:[{min_last_listed_date} TO {max_last_listed_date}]'],
	},
	{
		name: "Listed Without Sale",
		show_name: true,
		exclude_stage: ["pipeline"],
		filter_key: "listed_without_sale",
		search_key: ["min_listed_not_sold_date", "max_listed_not_sold_date"],
		date_filter: true,
		unix_date:true,
		search_value: ['last_listed_sale_date_all:[{min_listed_not_sold_date} TO {max_listed_not_sold_date}]'],
		additional_fq: '{!frange l=7776000}sub(last_listed_sale_date_all, last_sold_date)',
	},

	{
		name: "Asking Price",
		show_name: true,
		search_key: ["min_price", "max_price"],
		filter_key : "asking_price",
		price_filter: true,
		search_value: ['last_listed_sale_price_all:[{min_price} TO {max_price}]']
	},
	{
		// DOF fair market value from the supplemental tax roll (taxyr 2027 — the only roll loaded).
		// Joined rather than denormalized: supplemental_tax_roll is single-shard, so a classic
		// {!join} works and method=crossCollection is neither needed nor available here.
		// dof_property is single-valued and already in we3-properties key format (p-...).
		// unbounded_max because FMV reaches ~$1.8B on whole apartment buildings — the price
		// handler's default 99999999 stand-in for an unset max would silently drop 351 lots.
		name: "DOF MV",
		show_name: true,
		search_key: ["min_fmv", "max_fmv"],
		filter_key : "tax_fmv",
		price_filter: true,
		labels : ["Min Market Value", "Max Market Value"],
		min_max_values : [500000, 20000000],
		min_max_increment : 500000,
		search_value: ['fmv:[{min_fmv} TO {max_fmv}]'],
		target : "fq",
		join_query : "{!join from=dof_property fromIndex=supplemental_tax_roll to=key}",
		unbounded_max : true,
		value_prefix : "$"
	},
	{
		name: "Asking PPSF",
		show_name: true,
		search_key: ["min_ppsf_price", "max_ppsf_price"],
		filter_key : "asking_ppsf",
		price_filter: true,
		target: 'fq',
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['{!frange l={min_ppsf_price} u={max_ppsf_price}}div(last_listed_sale_price_all,sq_ft)']
	},
	{
		name: "Tax Benefits",
		show_name: true,
		search_key: "tax_benefits",
		target : "fq",
		multi_filter : true,
		join_query : "{!join from=alternate_buildings fromIndex=we3-tax-abatements-subsidies to=building}",
		values: [
			
			{
				name: "421a",
				filter: "421a",
				dropdown_name: "421a",
				search_value: "(program_421a:true)",
			},
			{
				name: "J51",
				filter: "j51",
				dropdown_name: "J51",
				search_value: "(program_j51:true)",
			},
			{
				name: "421a (10+ Years Left)",
				filter: "421a-10",
				dropdown_name: "421a (10+ Years Left)",
				search_value: "(program_421a_end_date:[NOW+10YEAR TO *])",
			},
			{
				name: "421a (5-10 Years Left)",
				filter: "421a-5",
				dropdown_name: "421a (5-10 Years Left)",
				search_value: "(program_421a_end_date:[NOW+5YEAR TO *])",
			},
			{
				name: "421a (<5 Years Left)",
				filter: "421a-4",
				dropdown_name: "421a (<5 Years Left)",
				search_value: "(program_421a_end_date:[NOW TO NOW+5YEAR])",
			},
			{
				name: "421a (Expired)",
				filter: "421a-exp",
				dropdown_name: "421a (Expired)",
				search_value: "(program_421a_end_date:[* TO NOW])",
			},
			
			{
				name: "J51 (10+ Years Left)",
				filter: "j51-10",
				dropdown_name: "J51 (10+ Years Left)",
				search_value: "(program_j51:true AND program_j51_end_date:[NOW+10YEAR TO *])",
			},
			{
				name: "J51 (5-10 Years Left)",
				filter: "j51-5",
				dropdown_name: "J51 (5-10 Years Left)",
				search_value: "(program_j51:true AND program_j51_end_date:[NOW+5YEAR TO NOW+10YEAR])",
			},
			{
				name: "J51 (<5 Years Left)",
				filter: "j51-4",
				dropdown_name: "J51 (<5 Years Left)",
				search_value: "(program_j51:true AND program_j51_end_date:[NOW TO NOW+5YEAR])",
			},
			{
				name: "J51 (Expired)",
				filter: "j51-exp",
				dropdown_name: "J51 (Expired)",
				search_value: "(program_j51_end_date:[* TO Now])",
			},
			
			
		]
	},
	{
		name: "Last Listed for Rent",
		show_name: true,
		exclude_stage: ["pipeline"],
		filter_key: "last_listed_rent",
		search_key: "last_listed_rent_date_all",
		search_key: ["min_last_listed_rent_date", "max_last_listed_rent_date"],
		date_filter: true,
		unix_date:true,
		search_value: ['last_listed_rent_date_all:[{min_last_listed_rent_date} TO {max_last_listed_rent_date}]'],
	},
	{

		name: "Rental Activity",
		show_name: true,
		search_key: "rental_activity",
		multi_filter: true,
		values: [
			{
				name: "Not Rented in Past 2 Years",
				filter: "never_rented",
				dropdown_name: "Not Rented in Past 2 Years",
				search_value: "",
				additional_fq: "{!frange u=0}sub(last_listed_rent_date_all, div(ms(NOW/DAY-2YEAR),1000))"
			},
		]
	},
	{
		// Pied-a-terre surcharge — thresholds and join notes on _patFmvQuery at the top of this file.
		//
		// Single-select, not multi: "All" is the union of the other two, and the multi_filter path
		// pushes each selected value as its own fq (ANDed), so ticking both subsets would return
		// zero rather than the union. Three mutually exclusive choices model this correctly.
		//
		// minWidth is honoured by the shared building_filters/UnitsSearchTableFiltersSelect, which is
		// what Owner Outreach renders (via BuildingSearchAppBar) — it sizes the control, the label
		// and the dropdown paper together.
		name: "Pied-à-Terre Surcharge",
		show_name: true,
		search_key: "pat_value",
		target: "fq",
		minWidth: 220,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "All Types",
				filter: "pat_all",
				dropdown_name: "All Types",
				search_value: `${_patCCCQuery} OR ${_patHouseQuery}`
			},
			{
				name: "Condo/Co-op $1M+",
				filter: "pat_ccc",
				dropdown_name: "Condo/Co-op $1M+",
				search_value: _patCCCQuery
			},
			{
				name: "1–3 Family $5M+",
				filter: "pat_house",
				dropdown_name: "1–3 Family $5M+",
				search_value: _patHouseQuery
			},
		]
	},
	{
		"name": "Distress",
		"show_name": true,
		"search_key": "property_distress",
		"target": "fq",
		"multi_filter": true,
		"minWidth":200,
		"join_query": "{!join method=crossCollection from=property fromIndex=we3-acris-real-property to=key}",
		"values": [
			{
				"name": "Any Lien",
				"filter": "any_liens",
				"dropdown_name": "Any Lien (Last 5 Years)",
				"search_value": "(doc_type:(fl ftl tls ntxl dpftl judg locc tolcc brup ucc1 ucc3) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Tax & Gov Liens",
				"filter": "gov_liens",
				"dropdown_name": "Tax & Gov Liens (Last 5 Years)",
				"search_value": "(doc_type:(fl ftl tls ntxl dpftl judg) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Common & Mech. Liens",
				"filter": "common_mechanics_liens",
				"dropdown_name": "Common & Mech. Liens (Last 5 Years)",
				"search_value": "(doc_type:(locc tolcc) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Bankruptcy & Debt Liens",
				"filter": "bankruptcy_liens",
				"dropdown_name": "Bankruptcy & Debt Liens (Last 5 Years)",
				"search_value": "(doc_type:(brup ucc1 ucc3) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Lis Pendens",
				"filter": "lis_pendens",
				"dropdown_name": "Lis Pendens (Foreclosure Filing)",
				"search_value": "",
				"additional_fq": "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}*:*"
			}
		]
	},
	{
		name: "Unit Number",
		show_name: true,
		search_key: "address_2_normalized",
		text_filter: true
	},
	{
		name: "Special Pricing",
		show_name: true,
		search_key: "special_pricing",
		target: 'fq',
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Asking Below Sold",
				filter: "below-sold",
				dropdown_name: "Asking Below Sold",
				search_value: "{!frange l=1 u=1}if(lt(last_listed_sale_price_all,last_sold_price),1,0)",
				additional_fq : ["{!frange l=1 u=1}if(gt(last_listed_sale_date_all,last_sold_date),1,0)"]
			},
			{
				name: "Unpriced Units",
				filter: "unpriced-units",
				dropdown_name: "Unpriced Units",
				search_value: "last_listed_sale_price_all:[* TO -1] AND source_organizations:NYAG",
			}
		]
	},
	{
		name: "Carrying Cost",
		show_name: true,
		search_key: ["min_carry_cost", "max_carry_cost"],
		filter_key : "monthly_carrying_charges",
		price_filter: true,
		labels : ["Min Carry Cost", "Max Carry Cost"],
		min_max_values : [500, 5000],
		min_max_increment : 500,
		format_type : "number",		
		search_value: ['offering_monthly_carrying_charges:[{min_carry_cost} TO {max_carry_cost}]'], 	
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< $1,000",
				filter: "less-than-1000",
				dropdown_name: "Less than $1000",
				search_value: "[0 TO 1000] OR monthly_fees:[0 TO 1000]"
			},
			{
				name: "$1,000 to $1,500",
				filter: "1001-to-1501",
				dropdown_name: "$1,000 to $1,500",
				search_value: "[1001 TO 1500] OR monthly_fees:[1001 TO 1500]"
			},
			{
				name: "$1,500 to $2,000",
				filter: "1501-to-2001",
				dropdown_name: "$1,500 to $2,000",
				search_value: "[1501 TO 2000] OR monthly_fees:[1501 TO 2000]"
			},
			{
				name: "$2,000 to $3,000",
				filter: "2001-to-3001",
				dropdown_name: "$2,000 to $3,000",
				search_value: "[2001 TO 3000] OR monthly_fees:[2001 TO 3000]"
			},
			{
				name: "$3,000 to $5,000",
				filter: "3,000-to-5000",
				dropdown_name: "$3,000 to $5,000",
				search_value: "[3001 TO 5000] OR monthly_fees:[3001 TO 5000]"
			},
			{
				name: "$5,000+",
				filter: "5000-or-more",
				dropdown_name: "$5,000 or more",
				search_value: "[5001 TO *] OR monthly_fees:[5001 TO *]"
			}
		]
	},
	{
		name: "Year Built",
		show_name: true,
		exclude_stage: ["pipeline"],
		format_type : "number",
		search_key: ["min_year_built", "max_year_built"],
		filter_key : "year_built",
		price_filter: true,
		labels : ["Min Year Built", "Max Year Built"],
		min_max_values : [1900, current_year],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [1900, 1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020],
			"max" : [1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020, current_year]
		},		
		format_type : "year",
		search_value: ['year_built:[{min_year_built} TO {max_year_built}]'], 
		value_prefix : "",	
		title_suffix : ""
	},
	{
		name: "Sales Started",
		show_name: true,
		search_key: ["min_start_date", "max_start_date"],
		exclude_stage: ["pipeline"],
		filter_type: "building",
		fiter_key: "sales_start_date",
		date_filter: true,
		search_value: ['sales_start_date:[{min_start_date} TO {max_start_date}]']		
	},
	
	{
		name: "Offering Submitted",
		show_name: true,
		search_key: ["min_submitted_date", "max_submitted_date"],
		date_filter: true,
		filter_type: "building",
		fiter_key: "offering_submitted_date",
		search_value: ['offering_submitted_date:[{min_submitted_date} TO {max_submitted_date}]']		
	},
	{
		name: "Offering Accepted",
		show_name: true,
		search_key: ["min_accepted_date", "max_accepted_date"],
		date_filter: true,
		filter_type: "building",
		fiter_key: "offering_accepted_date",
		search_value: ['offering_accepted_date:[{min_accepted_date} TO {max_accepted_date}]']		
	},
	{
		name: "Offering Effective",
		show_name: true,
		search_key: ["min_effective_date", "max_effective_date"],
		date_filter: true,
		filter_type: "building",
		fiter_key: "offering_effective_date",
		search_value: ['offering_effective_date:[{min_effective_date} TO {max_effective_date}]']		
	},
	{
		name: "Building Last Listing",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "last_listed_sale_date_formatted",
		filter_type: "building",
		search_key: ["min_listed_date", "max_listed_date"],
		date_filter: true,
		search_value: ['last_listed_sale_date_formatted:[{min_listed_date} TO {max_listed_date}]'],
	},
	{
		name: "Building Last Sale",
		show_name: true,
		exclude_stage: ["pipeline"],
		filter_type: "building",
		search_key: ["min_sold_date", "max_sold_date"],
		date_filter: true,
		search_value: ['last_sold_date_formatted:[{min_sold_date} TO {max_sold_date}]']
	},	


];

export const EXPIRED_LISTINGS_MAX_DATE_RANGE = "NOW-1YEAR";

export const _expiredListingsFilters = [
	{
		name: "Off-Market Date",
		show_name: true,
		filter_key: "off_market_date",
		search_key: ["min_off_market_date", "max_off_market_date"],
		date_filter: true,
		date_presets: [
			{ name: "Today", amount: 0, unit: "days" },
			{ name: "Yesterday", amount: 1, unit: "days" },
			{ name: "2 Days Ago", amount: 2, unit: "days" },
			{ name: "3 Days Ago", amount: 3, unit: "days" },
			{ name: "1 Week Ago", amount: 7, unit: "days" },
			{ name: "2 Weeks Ago", amount: 14, unit: "days" },
			{ name: "1 Month Ago", amount: 1, unit: "months" },
			{ name: "2 Months Ago", amount: 2, unit: "months" },
			{ name: "3 Months Ago", amount: 3, unit: "months" },
			{ name: "6 Months Ago", amount: 6, unit: "months" }
		],
		search_value: ['(last_listed_sale_tom_date_formatted:[{min_off_market_date} TO {max_off_market_date}] OR last_listed_sale_pom_date_formatted:[{min_off_market_date} TO {max_off_market_date}] OR last_listed_sale_expired_date_formatted:[{min_off_market_date} TO {max_off_market_date}])']
	},
	{
		name: "Off-Market Type",
		show_name: true,
		search_key: "off_market_type",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "TOM (Temporarily Off Market)",
				filter: "tom",
				dropdown_name: "Temporarily Off Market",
				search_value: `last_listed_sale_tom_date_formatted:[${EXPIRED_LISTINGS_MAX_DATE_RANGE} TO NOW]`
			},
			{
				name: "POM (Permanently Off Market)",
				filter: "pom",
				dropdown_name: "Permanently Off Market",
				search_value: `last_listed_sale_pom_date_formatted:[${EXPIRED_LISTINGS_MAX_DATE_RANGE} TO NOW]`
			},
			{
				name: "Expired",
				filter: "expired",
				dropdown_name: "Listing Expired",
				search_value: `last_listed_sale_expired_date_formatted:[${EXPIRED_LISTINGS_MAX_DATE_RANGE} TO NOW]`
			}
		]
	},
	{
		name: "Days on Market",
		show_name: true,
		filter_key: "dom_expired",
		search_key: ["min_dom_expired", "max_dom_expired"],
		price_filter: true,
		labels: ["Min Days", "Max Days"],
		custom_min_max_values: { min: [60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360], max: [60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360, "No Max"] },
		format_type: "number",
		value_prefix: "",
		title_suffix: "Days",
		no_max: true,
		search_value: ['days_on_market:[{min_dom_expired} TO {max_dom_expired}]']
	},
	{
		name: "Property Type",
		show_name: true,
		search_key: "ownership_type",
		multi_filter:true,
		hide_building:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "(*condo* !condo*p)",
			},
			{
				name: "Co-op",
				filter: "coop",
				dropdown_name: "Co-op",
				search_value: "(*co?op* *coop* !condo*p)",
			},
			{
				name: "Condop",
				filter: "condop",
				dropdown_name: "Condop",
				search_value: "(condop*)",
			},
			{
				name: "Single-Family",
				filter: "single-family",
				dropdown_name: "Single-Family",
				search_value: "(single?family)",
			},
			{
				name: "Multi-Family",
				filter: "multi-family",
				dropdown_name: "Multi-Family",
				search_value: "(multi?family)",
			}
		]
	},
	{
		name: "Bedrooms",
		show_name: true,
		search_key: "bedrooms",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Studio",
				filter: "studio",
				dropdown_name: "Studio",
				search_value: "0",
			},
			{
				name: "1 Bedroom",
				filter: "1-bedroom",
				dropdown_name: "1 Bedroom",
				search_value: "1",
			},
			{
				name: "2 Bedroom",
				filter: "2-bedroom",
				dropdown_name: "2 Bedroom",
				search_value: "2",
			},
			{
				name: "3 Bedroom",
				filter: "3-bedroom",
				dropdown_name: "3 Bedroom",
				search_value: "3",
			},
			{
				name: "4+ Bedroom",
				filter: "4-or-more",
				dropdown_name: "4+ Bedroom",
				search_value: "[4 TO *]",
			},
		]
	},
	{
		name: "Baths",
		show_name: true,
		search_key: "baths",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1 Bath",
				filter: "1-bath",
				dropdown_name: "1 Bath",
				search_value: "1 OR baths:1",
			},
			{
				name: "2 Bath",
				filter: "2-bath",
				dropdown_name: "2 Bath",
				search_value: "2 OR baths:2",
			},
			{
				name: "3 Bath",
				filter: "3-bath",
				dropdown_name: "3 Bath",
				search_value: "3 OR baths:3",
			},
			{
				name: "4+ Bath",
				filter: "4-or-more",
				dropdown_name: "4+ Bath",
				search_value: "[4 TO *] OR baths:[4 TO *]",
			},
		]
	},
	{
		name: "Interior SF",
		show_name: true,
		search_key: ["min_sq_ft", "max_sq_ft"],
		filter_key : "sq_ft",
		price_filter: true,
		labels : ["Min Sq Ft", "Max Sq Ft"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['sq_ft:[{min_sq_ft} TO {max_sq_ft}]'],
		value_prefix : "",
		title_suffix : "SF"
	},
	{
		name: "Last Asking Price",
		show_name: true,
		search_key: ["min_price", "max_price"],
		filter_key : "asking_price",
		price_filter: true,
		search_value: ['last_listed_sale_price_all:[{min_price} TO {max_price}]']
	},
	{
		name: "Last Asking PPSF",
		show_name: true,
		search_key: ["min_ppsf_price", "max_ppsf_price"],
		filter_key : "asking_ppsf",
		price_filter: true,
		target: 'fq',
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['{!frange l={min_ppsf_price} u={max_ppsf_price}}div(last_listed_sale_price_all,sq_ft)']
	},
	{
		name: "Owner Type",
		show_name: true,
		search_key: "owner",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Original Owner",
				filter: "original",
				dropdown_name: "Original Owner",
				search_value: "* AND times_exchanged:[0 TO 1]"
			},
			{
				name: "Individual Owner",
				filter: "individual",
				dropdown_name: "Individual Owner",
				search_value: "(*) AND -owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c *LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C)"
			},
			{
				name: "Corporate Owner",
				filter: "corporate",
				dropdown_name: "Corporate Owner",
				search_value: "(*LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C) OR owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)"
			}
		]
	},
	{
		name: "Owner Name",
		show_name: true,
		search_key: "owner_name",
		text_filter: true,
		search_fields: ["owner"]
	}
];

export const _onMarketListingsFilters = [
	{
		name: "Days on Market",
		show_name: true,
		filter_key: "dom_on_market",
		search_key: ["min_dom_on_market", "max_dom_on_market"],
		price_filter: true,
		target: "fq",
		labels: ["Min Days", "Max Days"],
		custom_min_max_values: { min: [60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360], max: [60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360, "No Max"] },
		format_type: "number",
		value_prefix: "",
		title_suffix: "Days",
		no_max: true,
		search_value: ['{!frange l={min_dom_on_market} u={max_dom_on_market}}div(sub(ms(NOW),product(last_listed_sale_date_all,1000)),86400000)']
	},
	{
		name: "Listing Date",
		show_name: true,
		filter_key: "listing_date",
		search_key: ["min_listing_date", "max_listing_date"],
		date_filter: true,
		search_value: ['last_listed_sale_date_formatted:[{min_listing_date} TO {max_listing_date}]']
	},
	{
		name: "Property Type",
		show_name: true,
		search_key: "ownership_type",
		multi_filter:true,
		hide_building:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "(*condo* !condo*p)",
			},
			{
				name: "Co-op",
				filter: "coop",
				dropdown_name: "Co-op",
				search_value: "(*co?op* *coop* !condo*p)",
			},
			{
				name: "Condop",
				filter: "condop",
				dropdown_name: "Condop",
				search_value: "(condop*)",
			},
			{
				name: "Single-Family",
				filter: "single-family",
				dropdown_name: "Single-Family",
				search_value: "(single?family)",
			},
			{
				name: "Multi-Family",
				filter: "multi-family",
				dropdown_name: "Multi-Family",
				search_value: "(multi?family)",
			}
		]
	},
	{
		name: "Bedrooms",
		show_name: true,
		search_key: "bedrooms",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Studio",
				filter: "studio",
				dropdown_name: "Studio",
				search_value: "0",
			},
			{
				name: "1 Bedroom",
				filter: "1-bedroom",
				dropdown_name: "1 Bedroom",
				search_value: "1",
			},
			{
				name: "2 Bedroom",
				filter: "2-bedroom",
				dropdown_name: "2 Bedroom",
				search_value: "2",
			},
			{
				name: "3 Bedroom",
				filter: "3-bedroom",
				dropdown_name: "3 Bedroom",
				search_value: "3",
			},
			{
				name: "4+ Bedroom",
				filter: "4-or-more",
				dropdown_name: "4+ Bedroom",
				search_value: "[4 TO *]",
			},
		]
	},
	{
		name: "Baths",
		show_name: true,
		search_key: "baths",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1 Bath",
				filter: "1-bath",
				dropdown_name: "1 Bath",
				search_value: "1 OR baths:1",
			},
			{
				name: "2 Bath",
				filter: "2-bath",
				dropdown_name: "2 Bath",
				search_value: "2 OR baths:2",
			},
			{
				name: "3 Bath",
				filter: "3-bath",
				dropdown_name: "3 Bath",
				search_value: "3 OR baths:3",
			},
			{
				name: "4+ Bath",
				filter: "4-or-more",
				dropdown_name: "4+ Bath",
				search_value: "[4 TO *] OR baths:[4 TO *]",
			},
		]
	},
	{
		name: "Interior SF",
		show_name: true,
		search_key: ["min_sq_ft", "max_sq_ft"],
		filter_key : "sq_ft",
		price_filter: true,
		labels : ["Min Sq Ft", "Max Sq Ft"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['sq_ft:[{min_sq_ft} TO {max_sq_ft}]'],
		value_prefix : "",
		title_suffix : "SF"
	},
	{
		name: "Asking Price",
		show_name: true,
		search_key: ["min_price", "max_price"],
		filter_key : "asking_price",
		price_filter: true,
		search_value: ['last_listed_sale_price_all:[{min_price} TO {max_price}]']
	},
	{
		name: "Asking PPSF",
		show_name: true,
		search_key: ["min_ppsf_price", "max_ppsf_price"],
		filter_key : "asking_ppsf",
		price_filter: true,
		target: 'fq',
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['{!frange l={min_ppsf_price} u={max_ppsf_price}}div(last_listed_sale_price_all,sq_ft)']
	},
	{
		name: "Owner Type",
		show_name: true,
		search_key: "owner",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Original Owner",
				filter: "original",
				dropdown_name: "Original Owner",
				search_value: "* AND times_exchanged:[0 TO 1]"
			},
			{
				name: "Individual Owner",
				filter: "individual",
				dropdown_name: "Individual Owner",
				search_value: "(*) AND -owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c *LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C)"
			},
			{
				name: "Corporate Owner",
				filter: "corporate",
				dropdown_name: "Corporate Owner",
				search_value: "(*LLC *INC *CORP *TRUST *L.P. *AUTHORITY *ASSOCIATES *COMPANY *L.L.C) OR owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)"
			}
		]
	},
	{
		name: "Owner Name",
		show_name: true,
		search_key: "owner_name",
		text_filter: true,
		search_fields: ["owner"]
	}
];

export const _brokerOutreachFilters = [
	{
		name: "Brokerage",
		title: "Search by Brokerage",
		show_name: true,
		search_key: "company_name",
		autocomplete: true,
		core_name: "search-agents",
		fq: ["deleted:false", "source_namespace:REBNY-IDX"],
		// group_amenities: true,
		hide_building:true,
		minWidth:200,
		filter_match_operator: "OR",
		hide_operator: true
	},
	{
		name: "Property Type",
		show_name: true,
		search_key: "ownership_type",
		multi_filter:true,
		target : "fq",
		"minWidth":180,
		join_query : "{!join from=agent_emails fromIndex=we3-transactions to=email}",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "ownership_type:(*condo* !condo*p)",
			},
			{
				name: "Co-op",
				filter: "coop",
				dropdown_name: "Co-op",
				search_value: "ownership_type:(*co?op* *coop* !condo*p)",
			},			
			{
				name: "Condop",
				filter: "condop",
				dropdown_name: "Condop",
				search_value: "ownership_type:(condop*)",
			},
			{
				name: "Single-Family",
				filter: "single-family",
				dropdown_name: "Single-Family",
				search_value: "ownership_type:(single?family)",
			},			
			{
				name: "Multi-Family",
				filter: "multi-family",
				dropdown_name: "Multi-Family",
				search_value: "ownership_type:(multi?family)",
			}			
			
			
		]
	},
	{
		name: "Property Stage",
		show_name: true,
		search_key: "property_stage",
		multi_filter:true,
		target : "fq",
		"minWidth":180,
		join_query : "{!join from=agent_emails fromIndex=we3-transactions to=email}",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "New Dev",
				filter: "new-dev",
				dropdown_name: "New Dev",
				search_value: "sponsored_sale:true",
			},
			{
				name: "Resale",
				filter: "resale",
				dropdown_name: "Resale",
				search_value: "-sponsored_sale:true",
			}
		]
	},
	{
		// XCJ-HOLD: search-listings is a 3-shard collection, so a classic cross-core join fails
		// outright. method=crossCollection would fix it, but it requires a single-valued `from`
		// field and contact_emails is multiValued (search-listings has no single-valued agent
		// email field). Un-hide once the backend exposes one, or once this is reworked as a
		// two-step: facet search-listings on contact_emails, then fq `email:(...)` on search-agents.
		hidden: true,
		name: "Listing Status",
		show_name: true,
		search_key: "listing_status",
		multi_filter:true,
		target : "fq",
		join_query : "{!join from=contact_emails fromIndex=search-listings to=email}",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Active",
				filter: "active",
				dropdown_name: "Active",
				search_value: "sale_status:active AND deleted:false AND source_namespace:REBNY-IDX AND listing_type:sale",
			},
			{
				name: "In Contract",
				filter: "contract",
				dropdown_name: "In Contract",
				search_value: "sale_status:(pending *contract*) AND deleted:false AND source_namespace:REBNY-IDX AND listing_type:sale",
			}
		]
	},
	{
		hidden: true, // XCJ-HOLD: see Listing Status above — same multiValued contact_emails join
		name: "List Price",
		show_name: true,
		search_key: ["min_list_price", "max_list_price"],
		filter_key : "list_price",
		price_filter: true,
		search_value: ['price:[{min_list_price} TO {max_list_price}]'],
		target : "fq",
		join_query : "{!join from=contact_emails fromIndex=search-listings to=email}",	
	},
	{
		hidden: true, // XCJ-HOLD: see Listing Status above — same multiValued contact_emails join
		name: "List Date",
		show_name: true,
		filter_key: "list_date",
		search_key: ["min_list_date", "max_list_date"],
		date_filter: true,
		search_value: ['active_date_start:[{min_list_date} TO {max_list_date}]'],
		target : "fq",
		join_query : "{!join from=contact_emails fromIndex=search-listings to=email}",		
	},
	{
		name: "Sold Price",
		show_name: true,
		search_key: ["min_sold_price", "max_sold_price"],
		filter_key : "sold_price",
		price_filter: true,
		search_value: ['price:[{min_sold_price} TO {max_sold_price}]'],
		target : "fq",
		join_query : "{!join from=agent_emails fromIndex=we3-transactions to=email}",		
	},
	{
		name: "Sold Date",
		show_name: true,
		filter_key: "sold_date",
		search_key: ["min_date", "max_date"],
		date_filter: true,
		search_value: ['close_date_formatted:[{min_date} TO {max_date}]'],
		target : "fq",
		join_query : "{!join from=agent_emails fromIndex=we3-transactions to=email}",		
	},
	
	{
		name: "Bedrooms",
		show_name: true,
		search_key: "bedrooms",
		multi_filter: true,
		target : "fq",
		join_query : "{!join from=agent_emails fromIndex=we3-transactions to=email}",	
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Studio",
				filter: "studio",
				dropdown_name: "Studio",
				search_value: "bedrooms:0",
			},
			{
				name: "1 Bedroom",
				filter: "1-bedroom",
				dropdown_name: "1 Bedroom",
				search_value: "bedrooms:1",
			},
			{
				name: "2 Bedroom",
				filter: "2-bedroom",
				dropdown_name: "2 Bedroom",
				search_value: "bedrooms:2",
			},
			{
				name: "3 Bedroom",
				filter: "3-bedroom",
				dropdown_name: "3 Bedroom",
				search_value: "bedrooms:3",
			},
			{
				name: "4+ Bedroom",
				filter: "4-or-more",
				dropdown_name: "4+ Bedroom",
				search_value: "bedrooms:[4 TO *]",
			},
		]
	},
	
	{
		name: "Baths",
		show_name: true,
		search_key: "baths",
		multi_filter: true,
		target : "fq",
		join_query : "{!join from=agent_emails fromIndex=we3-transactions to=email}",	
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},			
			{
				name: "1 Bath",
				filter: "1-bath",
				dropdown_name: "1 Bath",
				search_value: "baths:1",
			},
			{
				name: "2 Bath",
				filter: "2-bath",
				dropdown_name: "2 Bath",
				search_value: "baths:2",
			},
			{
				name: "3 Bath",
				filter: "3-bath",
				dropdown_name: "3 Bath",
				search_value: "baths:3",
			},
			{
				name: "4+ Bath",
				filter: "4-or-more",
				dropdown_name: "4+ Bath",
				search_value: "baths:[4 TO *]",
			},
		]
	},
	{
		name: "Square Foot",
		show_name: true,
		search_key: ["min_sq_ft", "max_sq_ft"],
		filter_key : "sq_ft",
		price_filter: true,
		labels : ["Min Sq Ft", "Max Sq Ft"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['sq_ft:[{min_sq_ft} TO {max_sq_ft}]'], 
		value_prefix : "",
		title_suffix : "SF",
		target : "fq",
		join_query : "{!join from=agent_emails fromIndex=we3-transactions to=email}",	
	},
	{
		name: "Year Built",
		show_name: true,
		exclude_stage: ["pipeline"],
		format_type : "number",
		search_key: ["min_year_built", "max_year_built"],
		filter_key : "year_built",
		price_filter: true,
		labels : ["Min Year Built", "Max Year Built"],
		min_max_values : [1900, current_year],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [1900, 1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020],
			"max" : [1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020, current_year]
		},		
		format_type : "year",
		search_value: ['year_built:[{min_year_built} TO {max_year_built}]'], 
		value_prefix : "",	
		title_suffix : ""
	},
	{
		name: "Sales Started",
		show_name: true,
		search_key: ["min_start_date", "max_start_date"],
		exclude_stage: ["pipeline"],
		filter_type: "building",
		fiter_key: "sales_start_date",
		date_filter: true,
		search_value: ['sales_start_date:[{min_start_date} TO {max_start_date}]']		
	},
	
	{
		name: "Offering Submitted",
		show_name: true,
		search_key: ["min_submitted_date", "max_submitted_date"],
		date_filter: true,
		filter_type: "building",
		fiter_key: "offering_submitted_date",
		search_value: ['offering_submitted_date:[{min_submitted_date} TO {max_submitted_date}]']		
	},
	{
		name: "Offering Accepted",
		show_name: true,
		search_key: ["min_accepted_date", "max_accepted_date"],
		date_filter: true,
		filter_type: "building",
		fiter_key: "offering_accepted_date",
		search_value: ['offering_accepted_date:[{min_accepted_date} TO {max_accepted_date}]']		
	},
	{
		name: "Offering Effective",
		show_name: true,
		search_key: ["min_effective_date", "max_effective_date"],
		date_filter: true,
		filter_type: "building",
		fiter_key: "offering_effective_date",
		search_value: ['offering_effective_date:[{min_effective_date} TO {max_effective_date}]']		
	},
	{
		name: "Building Last Listing",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "last_listed_sale_date_formatted",
		filter_type: "building",
		search_key: ["min_listed_date", "max_listed_date"],
		date_filter: true,
		search_value: ['last_listed_sale_date_formatted:[{min_listed_date} TO {max_listed_date}]'],
	},
	{
		name: "Building Last Sale",
		show_name: true,
		exclude_stage: ["pipeline"],
		filter_type: "building",
		multi_filter:true,
		search_key: ["min_sold_date", "max_sold_date"],
		date_filter: true,
		search_value: ['last_sold_date_formatted:[{min_sold_date} TO {max_sold_date}]']
	},	
]

export const _buildingComingSoonFilters = [
	{
		name: "Borough",
		show_name: true,
		default_name: 'All Boroughs',
		search_key: "borough",
		values: [
			{
				name: "All Boroughs",
				filter: "",
				dropdown_name: "All Boroughs",
				search_value: ""
			},
			{
				name: "Manhattan",
				filter: "manhattan",
				dropdown_name: "Manhattan",
				search_value: "manhattan",
			},
			{
				name: "Brooklyn",
				filter: "brooklyn",
				dropdown_name: "Brooklyn",
				search_value: "brooklyn",
			},
			{
				name: "Queens",
				filter: "queens",
				dropdown_name: "Queens",
				search_value: "queens",
			},
		]
	},
	{
		name: "Construction",
		show_name: true,
		search_key: "sales_start_date",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Move-in Ready",
				filter: "completed",
				dropdown_name: "Move-in Ready",
				search_value: " * AND building_effective_start:[NOW-25YEARS TO NOW]",
			},
			{
				name: "Under Construction",
				filter: "not-completed",
				dropdown_name: "Under Construction",
				search_value: "[NOW-5YEARS TO NOW] AND -building_effective_start:[NOW-25YEARS TO NOW]",
			}
			
		]
	},	
	{
		name: "Price / Sq Ft",
		show_name: true,
		search_key: "inventory_stats_listings_average_ppsf",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< $750 PPSF ",
				filter: "less-than-750",
				dropdown_name: "Less than $750",
				search_value: "[100 TO 750]"
			},
			{
				name: "$750 to $1,000 PPSF",
				filter: "75-to-1000",
				dropdown_name: "$750 to $1,000",
				search_value: "[751 TO 1000]"
			},
			{
				name: "$1,000 to $1,500 PPSF",
				filter: "1000-to-1500",
				dropdown_name: "$1,000 to $1,500",
				search_value: "[1001 TO 1500]"
			},
			{
				name: "$1,500 to $2,000 PPSF",
				filter: "1500-to-2000",
				dropdown_name: "$1,500 to $2,000",
				search_value: "[1500 TO 2000]"
			},
			{
				name: "$2,000 to $2,500 PPSF",
				filter: "2000-to-2500",
				dropdown_name: "$2,000 to $2,500",
				search_value: "[2001 to 2500]"
			},
			{
				name: "$2,500 to $5,000 PPSF",
				filter: "2500-to-5000",
				dropdown_name: "$2,500 to $5,000",
				search_value: "[2501 to 5000]"
			},
			{
				name: "$5,000+ PPSF",
				filter: "5000-or-more",
				dropdown_name: "$5,000 or more",
				search_value: "[5001 TO *]"
			}
		]
	},
	{
		name: "Starting From",
		show_name: true,
		search_key: "inventory_stats_listings_low_price",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "From: < $500,000 ",
				filter: "less-than-500",
				dropdown_name: "Less than $500K",
				search_value: "[100 TO 500000]"
			},
			{
				name: "From: $500K to $750K",
				filter: "500-to-750",
				dropdown_name: "$500K to $750K",
				search_value: "[500000 TO 750000]"
			},
			{
				name: "From: $750K to $1M",
				filter: "750-to-1000",
				dropdown_name: "$750K to $1M",
				search_value: "[750001 TO 1000000]"
			},
			{
				name: "From: $1M to $1.5M",
				filter: "1000-to-1500",
				dropdown_name: "$1M to $1.5M",
				search_value: "[1000001 TO 1500000]"
			},
			{
				name: "From: $1.5M to $2M",
				filter: "1500-to-2000",
				dropdown_name: "$1.5M to $2M",
				search_value: "[1500001 TO 2000000]"
			},
			{
				name: "From: $2M+",
				filter: "2000+",
				dropdown_name: "$2M+",
				search_value: "[2000000 TO *]"
			}
		]
	},
	
	{
		name: "Unsold Units",
		show_name: true,
		search_key: "inventory_unsold",
		values: [
			{
				name: "Any Unsold Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-10 Unsold Units",
				filter: "1-to-10",
				dropdown_name: "1 to 10",
				search_value: "[1 TO 10]"
			},
			{
				name: "11-30 Unsold Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Unsold Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Unsold Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Unsold Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 + Unsold Units",
				filter: "201-or-more",
				dropdown_name: "201 or more",
				search_value: "[201 TO *]"
			}
		]
	},
	{
		name: "% Sold",
		show_name: true,
		search_key: "inventory_pct_sold",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Has Sales",
				filter: "has-sales",
				dropdown_name: "Has Sales",
				search_value: "[1 TO *] OR offering_verified_status:\"sold out\""
			},
			{
				name: "Has No Sales",
				filter: "no-sales",
				dropdown_name: "Has No Sales",
				search_value: "[* TO 0] AND -offering_verified_status:\"sold out\""
			},
			{
				name: "Less Than 15%",
				filter: "0-to-15",
				dropdown_name: "Less Than 15%",
				search_value: "[0 TO 15]"
			},
			{
				name: "Less Than 35%",
				filter: "0-to-35",
				dropdown_name: "Less Than 35%",
				search_value: "[0 TO 34]"
			},
			{
				name: "Less Than 50%",
				filter: "0-to-49",
				dropdown_name: "Less Than 50%",
				search_value: "[0 TO 49]"
			},
			{
				name: "More Than 15%",
				filter: "15-to-100",
				dropdown_name: "More Than 15%",
				search_value: "[15 TO *]"
			},
			{
				name: "More Than 50%",
				filter: "50-to-100",
				dropdown_name: "More Than 50%",
				search_value: "[50 TO *]"
			},
			{
				name: "50% to 79%",
				filter: "50-to-79",
				dropdown_name: "50% to 79%",
				search_value: "[50 TO 79]"
			},
			{
				name: "80% to 99%",
				filter: "80-to-99",
				dropdown_name: "80% to 99%",
				search_value: "[80 TO 99]"
			},
			{
				name: "Not Sold Out",
				filter: "not-sold-out",
				dropdown_name: "Not Sold Out",
				search_value: "[* TO 99] AND -offering_verified_status:\"sold out\""
			},
			{
				name: "Sold Out",
				filter: "sold-out",
				dropdown_name: "Sold Out",
				search_value: "[100 TO *]"
			},
			
		]
	},
	{
		name: "Building Size",
		show_name: true,
		search_key: "inventory_total",
		values: [
			{
				name: "Any Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-10 Condo Units",
				filter: "1-to-10",
				dropdown_name: "1 to 10 Units",
				search_value: "[1 TO 10]"
			},
			{
				name: "11-30 Condo Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30 Units",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Condo Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50 Units",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Condo Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100 Units",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Condo Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200 Units",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 + Condo Units",
				filter: "201-or-more",
				dropdown_name: "201 or more Units",
				search_value: "[201 TO *]"
			}
		]
	},
	{
		name: "Status",
		show_name: true,
		search_key: "offering_verified_status",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Coming Soon",
				filter: "coming-soon",
				dropdown_name: "Coming Soon",
				search_value: '("coming soon")'
			},
			{
				name: "Submit Offering Plan",
				filter: "submit-offering-plan",
				dropdown_name: "Submit Offering Plan",
				search_value: '("offering plan required")'
			},
			{
				name: "Condo Dec Vetted",
				filter: "condo-dec-vetted",
				dropdown_name: "Condo Dec Vetted",
				search_value: '("condo declaration vetted")'
			},
		]
	}	
];
export const _houseAdminFilters = [
{
	name: "Type",
	show_name: true,
	search_key: "ownership_type",
	multi_filter:true,
	values: [
		{
			name: "Any",
			filter: "",
			dropdown_name: "Any",
			search_value: ""
		},	
		{
			name: "Single-Family",
			filter: "single-family",
			dropdown_name: "Single-Family",
			search_value: "(single?family) AND total_units:[1 TO 1]",
		},			
		{
			name: "Multi-Family (All)",
			filter: "multi-family",
			dropdown_name: "Multi-Family (All)",
			search_value: "((*family) NOT (single?family)) OR (ownership_type:rental AND total_units:[* TO 3])",
		},			
		{
			name: "Multi-Family (2 Units)",
			filter: "two-family",
			dropdown_name: "Multi-Family (2 Units)",
			search_value: "((*family) NOT (single?family) OR (ownership_type:rental AND total_units:[* TO 3])) AND total_units:[2 TO 2]",
		},			
		{
			name: "Multi-Family (3 Units)",
			filter: "three-family",
			dropdown_name: "Multi-Family (3 Units)",
			search_value: "((*family) NOT (single?family) OR (ownership_type:rental AND total_units:[* TO 3])) AND total_units:[3 TO 3]",
		},			
		{
			name: "Multi-Family (4+ Units)",
			filter: "four-family",
			dropdown_name: "Multi-Family (4+ Units)",
			search_value: "((*family) NOT (single?family) OR (ownership_type:rental AND total_units:[* TO 3])) AND total_units:[4 TO 6]",
		}
		
	]
},
{
	name: "Status",
	show_name: true,
	search_key: "current_status",
	multi_filter: true,
	values: [
		{
			name: "Any",
			filter: "",
			dropdown_name: "Any",
			search_value: ""
		},
		{
			name: "Active",
			filter: "active",
			dropdown_name: "Active",
			search_value: "(active)"
		},
		{
			name: "Coming Soon",
			filter: "coming-soon",
			dropdown_name: "Coming Soon",
			search_value: "(\"coming soon\")"
		},
		{
			name: "In Contract",
			filter: "in-contract",
			dropdown_name: "In Contract",
			search_value: "(\"in contract\")"
		},
		{
			name: "Off Market",
			filter: "off-market",
			dropdown_name: "Off Market",
			search_value: "(sold shadow not_yet_recorded transfer \"bulk sold\" \"\" \"never listed\")"
		}
	]
},
{
	name: "Asking Price",
	show_name: true,
	search_key: ["min_listed_price", "max_listed_price"],
	filter_key : "listed_price",
	price_filter: true,
	search_value: ['last_listed_sale_price_all:[{min_listed_price} TO {max_listed_price}]']		
},
{
	name: "Asking PPSF",
	show_name: true,
	search_key: ["min_listed_ppsf_price", "max_listed_ppsf_price"],
	filter_key : "listed_ppsf",
	price_filter: true,
	target: 'fq',
	labels : ["Min PPSF", "Max PPSF"],
	min_max_values : [500, 4500],
	min_max_increment : 500,
	format_type : "number",
	search_value: ['{!frange l={min_listed_ppsf_price} u={max_listed_ppsf_price}}div(last_listed_sale_price_all,sq_ft)'],
	
	
},
	{
	name: "Bedrooms",
	show_name: true,
	search_key: "bedrooms",
	multi_filter: true,
	values: [
		{
			name: "Any",
			filter: "",
			dropdown_name: "Any",
			search_value: ""
		},
		{
			name: "Studio",
			filter: "studio",
			dropdown_name: "Studio",
			search_value: "0",
		},
		{
			name: "1 Bedroom",
			filter: "1-bedroom",
			dropdown_name: "1 Bedroom",
			search_value: "1",
		},
		{
			name: "2 Bedroom",
			filter: "2-bedroom",
			dropdown_name: "2 Bedroom",
			search_value: "2",
		},
		{
			name: "3 Bedroom",
			filter: "3-bedroom",
			dropdown_name: "3 Bedroom",
			search_value: "3",
		},
		{
			name: "4+ Bedroom",
			filter: "4-or-more",
			dropdown_name: "4+ Bedroom",
			search_value: "[4 TO *]",
		},
	]
},
{
	name: "Baths",
	show_name: true,
	search_key: "baths",
	multi_filter: true,
	values: [
		{
			name: "Any",
			filter: "",
			dropdown_name: "Any",
			search_value: ""
		},			
		{
			name: "1 Bath",
			filter: "1-bath",
			dropdown_name: "1 Bath",
			search_value: "1 OR baths:1",
		},
		{
			name: "2 Bath",
			filter: "2-bath",
			dropdown_name: "2 Bath",
			search_value: "2 OR baths:2",
		},
		{
			name: "3 Bath",
			filter: "3-bath",
			dropdown_name: "3 Bath",
			search_value: "3 OR baths:3",
		},
		{
			name: "4+ Bath",
			filter: "4-or-more",
			dropdown_name: "4+ Bath",
			search_value: "[4 TO *] OR baths:[4 TO *]",
		},
	]
},
{
	name: "Amenities",
	show_name: true,
	search_key: "building_features",
	autocomplete: true,
	core_name: "we3-properties",
	fq: ["deleted:false"],
	group_amenities: true
},
{
		name: "Square Feet",
		show_name: true,
		search_key: ["min_sq_ft", "max_sq_ft"],
		filter_key : "sq_ft",
		price_filter: true,
		labels : ["Min Sq Ft", "Max Sq Ft"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['sq_ft:[{min_sq_ft} TO {max_sq_ft}]'], 
		value_prefix : "",
		title_suffix : "SF"
},	
{
	name: "Style",
	show_name: true,
	search_key: "building_class",
	multi_filter:true,
	values: [
		{
			name: "Any",
			filter: "",
			dropdown_name: "Any",
			search_value: ""
		},	
		{
			name: "Townhouse",
			filter: "townhouse",
			dropdown_name: "Townhouse",
			search_value: "(A7 A4 A9 B1 B2 B3 B9) OR building_style:townhouse",
		},			
		{
			name: "Detached House",
			filter: "detached-house",
			dropdown_name: "Detached House",
			search_value: "(A1) AND -building_style:townhouse",
		},			
		{
			name: "Semi-Detached House",
			filter: "semi-detached-house",
			dropdown_name: "Semi-Detached House",
			search_value: "(A5) AND -building_style:townhouse",
		}
		
	]
},


	{
		name: "Features",
		show_name: true,
		search_key: "building_features",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},	
			{
				name: "Basement",
				filter: "basement",
				dropdown_name: "Basement",
				search_value: "(basement)",
			},
			{
				name: "Outdoor Space",
				filter: "outdoor",
				dropdown_name: "Outdoor Space",
				search_value: "(patio deck garden deck terrace porch)",
			},		
			{
				name: "Garage",
				filter: "garage",
				dropdown_name: "Garage",
				search_value: "(garage)",
			},	
			{
				name: "Driveway",
				filter: "driveway",
				dropdown_name: "Driveway",
				search_value: "(driveway)",
			},
			{
				name: "Elevator",
				filter: "elevator",
				dropdown_name: "Elevator",
				search_value: "(elevator)",
			},			
			{
				name: "Fireplace",
				filter: "fireplace",
				dropdown_name: "Fireplace",
				search_value: "(fireplace)",
			}
			
		]
	},

	{
			name: "Building Width",
			show_name: true,
			filter_type:'building',
			search_key: ["min_building_width", "max_building_width"],
			filter_key : "lot_frontage",
			price_filter: true,
			labels : ["Min Width", "Max Width"],
			min_max_values : [10, 40],
			min_max_increment : 5,
			format_type : "number",
			search_value: ['building_frontage:[{min_building_width} TO {max_building_width}]'], 
			value_prefix : "",
			title_suffix : "FT"
	},	
	{
			name: "Lot Width",
			show_name: true,
			filter_type:'building',
			search_key: ["min_lot_width", "max_lot_width"],
			filter_key : "lot_frontage",
			price_filter: true,
			labels : ["Min Width", "Max Width"],
			min_max_values : [10, 40],
			min_max_increment : 5,
			format_type : "number",
			search_value: ['lot_frontage:[{min_lot_width} TO {max_lot_width}]'], 
			value_prefix : "",
			title_suffix : "FT"
	},	
	{
			name: "Lot Size",
			show_name: true,
			filter_type:'building',
			search_key: ["min_lot_area", "max_lot_area"],
			filter_key : "lot_area",
			price_filter: true,
			labels : ["Min Size", "Max Size"],
			min_max_values : [1000, 10000],
			min_max_increment : 1000,
			format_type : "number",
			search_value: ['lot_area:[{min_lot_area} TO {max_lot_area}]'], 
			value_prefix : "",
			title_suffix : "FT"
	},	
	{
		name: "Listed Date",
		show_name: true,
		filter_key: "listed_date",
		search_key: ["min_listed_date", "max_listed_date"],
		date_filter: true,
		search_value: ['last_listed_sale_date_all:[{min_listed_date} TO {max_listed_date}]']		
	},
	{
		name: "Contract Date",
		show_name: true,
		filter_key: "contract_date",
		search_key: ["min_contract_date", "max_contract_date"],
		date_filter: true,
		search_value: ['last_contract_date_formatted:[{min_contract_date} TO {max_contract_date}]']		
	},
	{
		name: "Sold Date",
		show_name: true,
		filter_key: "sold_date",
		search_key: ["min_date", "max_date"],
		date_filter: true,
		search_value: ['last_sold_date_formatted:[{min_date} TO {max_date}]']		
	},
	{
		name: "Sold Price",
		show_name: true,
		search_key: ["min_sold_price", "max_sold_price"],
		filter_key : "sold_price",
		price_filter: true,
		search_value: ['last_sold_price:[{min_sold_price} TO {max_sold_price}]']		
	},
	{
		name: "Sold PPSF",
		show_name: true,
		search_key: ["min_sold_ppsf_price", "max_sold_ppsf_price"],
		filter_key : "sold_ppsf",
		price_filter: true,
		target: 'fq',
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['{!frange l={min_sold_ppsf_price} u={max_sold_ppsf_price}}div(last_sold_price,sq_ft)'],
		
		
	},
	{
		name: "Total Units",
		show_name: true,
		search_key: ["min_units", "max_units"],
		filter_key : "total_units",
		price_filter: true,
		labels : ["Min Units", "Max Units"],
		min_max_values : [1, 6],
		min_max_increment : 1,
		custom_min_max_values : {
			"min" : [1, 2, 3, 4, 5],
			"max" : [2, 3, 4, 5, 6]
		},		
		format_type : "number",
		search_value: ['total_units:[{min_units} TO {max_units}]'], 
		value_prefix : "",	
		title_suffix : "Units",
		
	},
	{
		name: "Year Built",
		show_name: true,
		exclude_stage: ["pipeline"],
		format_type : "number",
		search_key: ["min_year_built", "max_year_built"],
		filter_key : "year_built",
		price_filter: true,
		labels : ["Min Year Built", "Max Year Built"],
		min_max_values : [1900, current_year],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [1900, 1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020],
			"max" : [1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020, current_year]
		},		
		format_type : "year",
		search_value: ['year_built:[{min_year_built} TO {max_year_built}]'], 
		value_prefix : "",	
		title_suffix : ""
	},
	{
		name: "Year Renovated",
		show_name: true,
		exclude_stage: ["pipeline"],
		format_type : "number",
		filter_type: "building",
		search_key: ["min_year_converted", "max_year_converted"],
		filter_key : "year_converted",
		price_filter: true,
		labels : ["Min Year Renovated", "Max Year Renovated"],
		min_max_values : [1965, current_year],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [1965, 1970, 1980, 1990, 2000, 2010, 2020],
			"max" : [1970, 1980, 1990, 2000, 2010, 2020, current_year]
		},		
		format_type : "year",
		search_value: ['year_converted:[{min_year_converted} TO {max_year_converted}]', 'year_altered_1:[{min_year_converted} TO {max_year_converted}]', 'year_altered_2:[{min_year_converted} TO {max_year_converted}]'],
		value_prefix : "",
		title_suffix : "",
	},
	{
		"name": "Distress",
		"show_name": true,
		"search_key": "property_distress",
		"target": "fq",
		"multi_filter": true,
		"minWidth": 200,
		"join_query": "{!join method=crossCollection from=property fromIndex=we3-acris-real-property to=key}",
		"values": [
			{
				"name": "Any Lien",
				"filter": "any_liens",
				"dropdown_name": "Any Lien (Last 5 Years)",
				"search_value": "(doc_type:(fl ftl tls ntxl dpftl judg locc tolcc brup ucc1 ucc3) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Tax & Gov Liens",
				"filter": "gov_liens",
				"dropdown_name": "Tax & Gov Liens (Last 5 Years)",
				"search_value": "(doc_type:(fl ftl tls ntxl dpftl judg) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Common & Mech. Liens",
				"filter": "common_mechanics_liens",
				"dropdown_name": "Common & Mech. Liens (Last 5 Years)",
				"search_value": "(doc_type:(locc tolcc) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Bankruptcy & Debt Liens",
				"filter": "bankruptcy_liens",
				"dropdown_name": "Bankruptcy & Debt Liens (Last 5 Years)",
				"search_value": "(doc_type:(brup ucc1 ucc3) AND recorded_datetime:[NOW-5YEARS TO NOW])"
			},
			{
				"name": "Lis Pendens",
				"filter": "lis_pendens",
				"dropdown_name": "Lis Pendens (Foreclosure Filing)",
				"search_value": "",
				"additional_fq": "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}*:*"
			}
		]
	},
	// {
	// 		name: "Year Renovated",
	// 		show_name: true,
	// 		filter_type:'building',
	// 		search_key: ["min_year_renovated", "max_year_renovated"],
	// 		filter_key : "year_altered_1",
	// 		price_filter: true,
	// 		labels : ["Min Year", "Max Year"],
	// 		min_max_values : [2000, 2024],
	// 		min_max_increment : 10,
	// 		format_type : "number",
	// 		search_value: ['year_altered_1:[{min_year_renovated} TO {max_year_renovated}]'],
	// 		value_prefix : "",
	// 		title_suffix : ""
	// }
	,
	{
		// Houses only ever fall in the 1-3 family tier ($5M+ DOF market value), so the
		// condo/co-op option Owner Outreach and Inventory carry would always come back empty
		// here. Thresholds and join notes: _patFmvQuery at the top of this file.
		name: "Pied-à-Terre Surcharge",
		show_name: true,
		search_key: "pat_value",
		target: "fq",
		minWidth: 220,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Subject ($5M+)",
				filter: "pat_house",
				dropdown_name: "Subject to Surcharge ($5M+)",
				search_value: _patHouseQuery
			},
		]
	}

]

export const _buildingAdminFilters = [
	{
		name: "Ownership",
		show_name: true,
		search_key: "ownership_type",
		multi_filter:true,
		hide_velocity:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "(*condo* !condo*p)",
			},
			{
				name: "Co-op",
				filter: "coop",
				dropdown_name: "Co-op",
				search_value: "(*co?op* *coop* !condo*p)",
			},			
			{
				name: "Condop",
				filter: "condop",
				dropdown_name: "Condop",
				search_value: "(condop*)",
			}
			
		]
	},
	
	{
		name: "Residential Units",
		show_name: true,
/*
		search_key: "residential_units",
		multi_filter:true,
*/
		search_key: ["min_units", "max_units"],
		filter_key : "inventory_total",
		price_filter: true,
		labels : ["Min Units", "Max Units"],
		min_max_values : [25, 200],
		min_max_increment : 25,
		custom_min_max_values : {
			"min" : [1, 11, 31, 51, 101, 201],
			"max" : [10, 30, 50, 100, 200, 300]
		},		
		format_type : "number",
		search_value: ['inventory_total:[{min_units} TO {max_units}]'], 
		value_prefix : "",	
		title_suffix : "Units",
		values: [
			{
				name: "Any Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-10 Condo Units",
				filter: "1-to-10",
				dropdown_name: "1 to 10 Units",
				search_value: "[1 TO 10]"
			},
			{
				name: "11-30 Condo Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30 Units",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Condo Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50 Units",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Condo Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100 Units",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Condo Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200 Units",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 + Condo Units",
				filter: "201-or-more",
				dropdown_name: "201 or more Units",
				search_value: "[201 TO *]"
			}
		]
	},
	
	{
		name: "Unsold Units",
		show_name: true,
		exclude_stage: ["pipeline"],
/*
		search_key: "inventory_unsold",
		multi_filter:true,
*/
		search_key: ["min_unsold_units", "max_unsold_units"],
		filter_key : "inventory_unsold",
		price_filter: true,
		labels : ["Min Unsold Units", "Max Unsold Units"],
		min_max_values : [25, 200],
		min_max_increment : 25,
		custom_min_max_values : {
			"min" : [1, 11, 31, 51, 101, 201],
			"max" : [10, 30, 50, 100, 200, 300]
		},		
		format_type : "number",
		search_value: ['inventory_unsold:[{min_unsold_units} TO {max_unsold_units}]'], 
		value_prefix : "",	
		title_suffix : "Units",
		values: [
			{
				name: "Any Unsold Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-10 Unsold Units",
				filter: "1-to-10",
				dropdown_name: "1 to 10",
				search_value: "[1 TO 10]"
			},
			{
				name: "11-30 Unsold Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Unsold Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Unsold Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Unsold Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 + Unsold Units",
				filter: "201-or-more",
				dropdown_name: "201 or more",
				search_value: "[201 TO *]"
			}
		]
	},
	{
		name: "Amenities",
		show_name: true,
		search_key: "building_features",
		autocomplete: true,
		core_name: "we3-buildings",
		fq: ["deleted:false"],
		group_amenities: true
	},
	{
		name: "% Sold",
		show_name: true,
		exclude_stage: ["pipeline"],
		//search_key: "inventory_pct_sold",
		search_key: ["min_pct", "max_pct"],
		filter_key : "inventory_pct_sold",
		price_filter: true,
		labels : ["Min %", "Max %"],
		min_max_values : [10, 100],
		min_max_increment : 10,
		format_type : "number",
		search_value: ['inventory_pct_sold:[{min_pct} TO {max_pct}]'], 
		value_prefix : "",	
		value_suffix : "%",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Has Sales",
				filter: "has-sales",
				dropdown_name: "Has Sales",
				search_value: "[1 TO *] OR offering_verified_status:\"sold out\""
			},
			{
				name: "Has No Sales",
				filter: "no-sales",
				dropdown_name: "Has No Sales",
				search_value: "[* TO 0] AND -offering_verified_status:\"sold out\""
			},
			{
				name: "Less Than 15%",
				filter: "0-to-15",
				dropdown_name: "Less Than 15%",
				search_value: "[0 TO 15]"
			},
			{
				name: "Less Than 35%",
				filter: "0-to-35",
				dropdown_name: "Less Than 35%",
				search_value: "[0 TO 34]"
			},
			{
				name: "Less Than 50%",
				filter: "0-to-49",
				dropdown_name: "Less Than 50%",
				search_value: "[0 TO 49]"
			},
			{
				name: "More Than 15%",
				filter: "15-to-100",
				dropdown_name: "More Than 15%",
				search_value: "[15 TO *]"
			},
			{
				name: "More Than 50%",
				filter: "50-to-100",
				dropdown_name: "More Than 50%",
				search_value: "[50 TO *]"
			},
			{
				name: "50% to 79%",
				filter: "50-to-79",
				dropdown_name: "50% to 79%",
				search_value: "[50 TO 79]"
			},
			{
				name: "80% to 99%",
				filter: "80-to-99",
				dropdown_name: "80% to 99%",
				search_value: "[80 TO 99]"
			},
			{
				name: "Not Sold Out",
				filter: "not-sold-out",
				dropdown_name: "Not Sold Out",
				search_value: "[* TO 99] AND -offering_verified_status:\"sold out\""
			},
			{
				name: "Sold Out",
				filter: "sold-out",
				dropdown_name: "Sold Out",
				search_value: "[100 TO *]"
			}
		]
	},

	
	
	{
		name: "Asking PPSF",
		show_name: true,
/*
		search_key: "inventory_stats_listings_average_ppsf",
		multi_filter:true,
*/
		search_key: ["min_ppsf_price", "max_ppsf_price"],
		filter_key : "asking_ppsf",
		price_filter: true,
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['inventory_stats_listings_average_ppsf:[{min_ppsf_price} TO {max_ppsf_price}]'],
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< $750 PPSF ",
				filter: "less-than-750",
				dropdown_name: "Less than $750",
				search_value: "[100 TO 750]"
			},
			{
				name: "$750 to $1,000 PPSF",
				filter: "75-to-1000",
				dropdown_name: "$750 to $1,000",
				search_value: "[751 TO 1000]"
			},
			{
				name: "$1,000 to $1,500 PPSF",
				filter: "1000-to-1500",
				dropdown_name: "$1,000 to $1,500",
				search_value: "[1001 TO 1500]"
			},
			{
				name: "$1,500 to $2,000 PPSF",
				filter: "1500-to-2000",
				dropdown_name: "$1,500 to $2,000",
				search_value: "[1500 TO 2000]"
			},
			{
				name: "$2,000 to $2,500 PPSF",
				filter: "2000-to-2500",
				dropdown_name: "$2,000 to $2,500",
				search_value: "[2001 to 2500]"
			},
			{
				name: "$2,500 to $5,000 PPSF",
				filter: "2500-to-5000",
				dropdown_name: "$2,500 to $5,000",
				search_value: "[2501 to 5000]"
			},
			{
				name: "$5,000+ PPSF",
				filter: "5000-or-more",
				dropdown_name: "$5,000 or more",
				search_value: "[5001 TO *]"
			}
		]
	},
	{
			name: "Sold PPSF",
			show_name: true,
			search_key: ["min_sold_ppsf_price", "max_sold_ppsf_price"],
			filter_key : "sold_ppsf",
			price_filter: true,
			labels : ["Min PPSF", "Max PPSF"],
			min_max_values : [500, 4500],
			min_max_increment : 500,
			format_type : "number",
			search_value: ['inventory_stats_transactions_average_ppsf:[{min_sold_ppsf_price} TO {max_sold_ppsf_price}]'],
		},
	{
		name: "Starting From",
		show_name: true,
		search_key: "inventory_stats_listings_low_price",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "From: < $500,000 ",
				filter: "less-than-500",
				dropdown_name: "Less than $500K",
				search_value: "[100 TO 500000]"
			},
			{
				name: "From: $500K to $750K",
				filter: "500-to-750",
				dropdown_name: "$500K to $750K",
				search_value: "[500000 TO 750000]"
			},
			{
				name: "From: $750K to $1M",
				filter: "750-to-1000",
				dropdown_name: "$750K to $1M",
				search_value: "[750001 TO 1000000]"
			},
			{
				name: "From: $1M to $1.5M",
				filter: "1000-to-1500",
				dropdown_name: "$1M to $1.5M",
				search_value: "[1000001 TO 1500000]"
			},
			{
				name: "From: $1.5M to $2M",
				filter: "1500-to-2000",
				dropdown_name: "$1.5M to $2M",
				search_value: "[1500001 TO 2000000]"
			},
			{
				name: "From: $2M+",
				filter: "2000+",
				dropdown_name: "$2M+",
				search_value: "[2000000 TO *]"
			}
		]
	},
	{
		name: "Tax Benefits",
		show_name: true,
		search_key: "tax_benefits",
		target : "fq",
		multi_filter : true,
		join_query : "{!join from=alternate_buildings fromIndex=we3-tax-abatements-subsidies to=key}",
		values: [
			
			{
				name: "421a",
				filter: "421a",
				dropdown_name: "421a",
				search_value: "(program_421a:true)",
			},
			{
				name: "J51",
				filter: "j51",
				dropdown_name: "J51",
				search_value: "(program_j51:true)",
			},
			{
				name: "421a (10+ Years Left)",
				filter: "421a-10",
				dropdown_name: "421a (10+ Years Left)",
				search_value: "(program_421a_end_date:[NOW+10YEAR TO *])",
			},
			{
				name: "421a (5-10 Years Left)",
				filter: "421a-5",
				dropdown_name: "421a (5-10 Years Left)",
				search_value: "(program_421a_end_date:[NOW+5YEAR TO *])",
			},
			{
				name: "421a (<5 Years Left)",
				filter: "421a-4",
				dropdown_name: "421a (<5 Years Left)",
				search_value: "(program_421a_end_date:[NOW TO NOW+5YEAR])",
			},
			{
				name: "421a (Expired)",
				filter: "421a-exp",
				dropdown_name: "421a (Expired)",
				search_value: "(program_421a_end_date:[* TO NOW])",
			},
			
			{
				name: "J51 (10+ Years Left)",
				filter: "j51-10",
				dropdown_name: "J51 (10+ Years Left)",
				search_value: "(program_j51:true AND program_j51_end_date:[NOW+10YEAR TO *])",
			},
			{
				name: "J51 (5-10 Years Left)",
				filter: "j51-5",
				dropdown_name: "J51 (5-10 Years Left)",
				search_value: "(program_j51:true AND program_j51_end_date:[NOW+5YEAR TO NOW+10YEAR])",
			},
			{
				name: "J51 (<5 Years Left)",
				filter: "j51-4",
				dropdown_name: "J51 (<5 Years Left)",
				search_value: "(program_j51:true AND program_j51_end_date:[NOW TO NOW+5YEAR])",
			},
			{
				name: "J51 (Expired)",
				filter: "j51-exp",
				dropdown_name: "J51 (Expired)",
				search_value: "(program_j51_end_date:[* TO Now])",
			},
			
			
		]
	},
	{
		name: "Floors",
		show_name: true,
		search_key: ["min_floor", "max_floor"],
		filter_key : "total_floors",
		price_filter: true,
		// target: 'fq',
		labels : ["Min Floor", "Max Floor"],

		custom_min_max_values : {
			"min" : [1, 11, 21, 31, 41],
			"max" : [10, 20, 30, 40, 50,"No Max"]
		},	
		format_type : "number",
		search_value: ['total_floors:[{min_floor} TO {max_floor}]']		,
		format_type : "number",
		value_prefix : "",	
		no_max:true,
		
		
	},
	{
		name: "Construction",
		show_name: true,
		search_key: "building_effective_start",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Move-in Ready",
				filter: "completed",
				dropdown_name: "Move-in Ready (Has CO/TCO)",
				search_value: "[NOW-25YEARS TO NOW] OR building_status:(resale) OR offering_verified_status:(\"sold out\")",
			},
			{
				name: "Under Construction",
				filter: "not-completed",
				dropdown_name: "Under Construction (No CO/TCO)",
				search_value: "[* TO NOW-25YEARS] AND -building_status:(resale) AND -offering_verified_status:(\"sold out\")",
			}
			
		]
	},
	{
		name: "Offering Type",
		show_name: true,
		search_key: "offering_construction",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "New Construction",
				filter: "new-construction",
				dropdown_name: "New Construction",
				search_value: "new",
			},
			{
				name: "Conversion",
				filter: "conversion",
				dropdown_name: "Conversion",
				search_value: "conversion",
			},			
			{
				name: "Rehab",
				filter: "rehab",
				dropdown_name: "Rehab",
				search_value: "rehab",
			},
			{
				name: "Exclude Conversion",
				filter: "exclude-conversion",
				dropdown_name: "Exclude Conversion",
				search_value: "(new rehab \"\")",
			}
			
		]
	},
	// {
	// 	name: "Ownership",
	// 	show_name: true,
	// 	search_key: "ownership_type",
	// 	values: [
	// 		{
	// 			name: "Any",
	// 			filter: "",
	// 			dropdown_name: "Any",
	// 			search_value: ""
	// 		},
	// 		{
	// 			name: "Condo",
	// 			filter: "condo",
	// 			dropdown_name: "Condo",
	// 			search_value: "condo AND -ownership_type:cooperative",
	// 		},
	// 		{
	// 			name: "Co-op",
	// 			filter: "co-op",
	// 			dropdown_name: "Co-op",
	// 			search_value: "coop",
	// 		}
	// 		
	// 	]
	// },
	{
		name: "Building Age",
		show_name: true,
		search_key: "year_built",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: `New (After ${new_building_start})`,
				filter: "new-contruction",
				dropdown_name: `New (After ${new_building_start})`,
				search_value: `[ ${(new_building_start + 1)} TO *]`,
			},
			{
				name: `Recent (1991 to ${new_building_start})`,
				filter: "recent-construction",
				dropdown_name: `Recent (1991 to ${new_building_start})`,
				search_value: `[1991 TO ${new_building_start}]`,
			},
			{
				name: "Post-War (1946 to 1990)",
				filter: "post-war",
				dropdown_name: "Post-War (1946 to 1990)",
				search_value: "[1946 TO 1990]",
			},
			{
				name: "Pre-War (Before 1946)",
				filter: "pre-war",
				dropdown_name: "Pre-War (Before 1946)",
				search_value: "[1600 TO 1945]",
			}
			
		]
	},
	{
		name: "Year Built",
		show_name: true,
		exclude_stage: ["pipeline"],
		format_type : "number",
		search_key: ["min_year_built", "max_year_built"],
		filter_key : "year_built",
		price_filter: true,
		labels : ["Min Year Built", "Max Year Built"],
		min_max_values : [1900, current_year],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [1900, 1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020],
			"max" : [1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020, current_year]
		},		
		format_type : "year",
		search_value: ['year_built:[{min_year_built} TO {max_year_built}]'], 
		value_prefix : "",	
		title_suffix : ""
	},
	{
		name: "Year Converted",
		show_name: true,
		exclude_stage: ["pipeline"],
		format_type : "number",
		search_key: ["min_year_converted", "max_year_converted"],
		filter_key : "max_year_converted",
		price_filter: true,
		labels : ["Min Year Converted", "Max Year Converted"],
		min_max_values : [1965, current_year],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [1965, 1970, 1980, 1990, 2000, 2010, 2020],
			"max" : [1970, 1980, 1990, 2000, 2010, 2020, current_year]
		},		
		format_type : "year",
		search_value: ['year_converted:[{min_year_converted} TO {max_year_converted}]'], 
		value_prefix : "",	
		title_suffix : "",
	},
	{
		name: "Sales Started",
		show_name: true,
		search_key: ["min_date", "max_date"],
		exclude_stage: ["pipeline"],
		date_filter: true,
		search_value: ['sales_start_date:[{min_date} TO {max_date}]']		
	},
	{
		name: "Closing Started",
		show_name: true,
		search_key: ["min_close_date", "max_close_date"],
		exclude_stage: ["pipeline"],
		date_filter: true,
		search_value: ['first_closing_date:[{min_close_date} TO {max_close_date}]']		
	},
	{
		name: "Sponsor PPSF",
		show_name: true,
		search_key: ["min_sponsor_sold_ppsf_price", "max_sponsor_sold_ppsf_price"],
		filter_key : "sponsor_sold_ppsf",
		price_filter: true,
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['inventory_stats_transactions_average_sponsor_ppsf:[{min_sponsor_sold_ppsf_price} TO {max_sponsor_sold_ppsf_price}]'],
	},
	{
		name: "Resale PPSF",
		show_name: true,
		search_key: ["min_resale_sold_ppsf_price", "max_resale_sold_ppsf_price"],
		filter_key : "sponsor_sold_ppsf",
		price_filter: true,
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['inventory_stats_transactions_average_resale_ppsf:[{min_resale_sold_ppsf_price} TO {max_resale_sold_ppsf_price}]'],
	},
	{
		name: "Offering Submitted",
		show_name: true,
		search_key: ["min_submitted_date", "max_submitted_date"],
		date_filter: true,
		search_value: ['offering_submitted_date:[{min_submitted_date} TO {max_submitted_date}]']		
	},
	{
		name: "Offering Accepted",
		show_name: true,
		search_key: ["min_accepted_date", "max_accepted_date"],
		date_filter: true,
		search_value: ['offering_accepted_date:[{min_accepted_date} TO {max_accepted_date}]']		
	},
	{
		name: "Offering Effective",
		show_name: true,
		search_key: ["min_effective_date", "max_effective_date"],
		date_filter: true,
		search_value: ['offering_effective_date:[{min_effective_date} TO {max_effective_date}]']		
	},
/*	{
		name: "Sales Started",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "sales_start_date",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Not Started",
				filter: "not-started",
				dropdown_name: "Not Started",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z]",
			},
			{
				name: "Has Started",
				filter: "has-started",
				dropdown_name: "Has Started",
				search_value: "[NOW-15YEARS TO NOW]",
			},
			{
				name: "Within 1 Year",
				filter: "1-year",
				dropdown_name: "Within 1 Year",
				search_value: "[NOW-1YEARS TO NOW]",
			},
			{
				name: "1 to 2 Years",
				filter: "2-year",
				dropdown_name: "Last 1 to 2 Years",
				search_value: "[NOW-2YEARS TO NOW-1YEARS]",
			},
			{
				name: "2 to 5 Years",
				filter: "2-5-year",
				dropdown_name: "Last 2 to 5 Years",
				search_value: "[NOW-5YEARS TO NOW-2YEARS]",
			},
			{
				name: "5 to 7 Years",
				filter: "5-7-year",
				dropdown_name: "Last 5 to 7 Years",
				search_value: "[NOW-7YEARS TO NOW-5YEARS]",
			},
			{
				name: "7 to 10 Years",
				filter: "7-10-year",
				dropdown_name: "Last 5 to 10 Years",
				search_value: "[NOW-10YEARS TO NOW-7YEARS]",
			},
			{
				name: "10 to 15 Years",
				filter: "10-15-year",
				dropdown_name: "Last 10 to 15 Years",
				search_value: "[NOW-15YEARS TO NOW-10YEARS]",
			},
			{
				name: "More Than 15 Years",
				filter: "more-15-year",
				dropdown_name: "More Than 15 Years Ago",
				search_value: "[NOW-20YEARS TO NOW-15YEARS]",
			}			
		]
	}, */
	{
		name: "Last Listing",
		show_name: true,
		exclude_stage: ["pipeline"],
		//search_key: "last_listed_sale_date_formatted",
		multi_filter:true,
		search_key: ["min_listed_date", "max_listed_date"],
		date_filter: true,
		search_value: ['last_listed_sale_date_formatted:[{min_listed_date} TO {max_listed_date}]'],
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "No Activity",
				filter: "no-activity",
				dropdown_name: "No Activity",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z] AND -building_status:resale AND -offering_verified_status:\"sold out\"",
			},
			{
				name: "Any Activity",
				filter: "any-activity",
				dropdown_name: "Any Activity",
				search_value: "[NOW-15YEARS TO NOW] OR offering_verified_status:\"sold out\" OR building_status:resale",
			},
			{
				name: "Within 1 Year",
				filter: "1-year",
				dropdown_name: "Within 1 Year",
				search_value: "[NOW-1YEARS TO NOW]",
			},
			{
				name: "1 to 2 Years",
				filter: "2-year",
				dropdown_name: "Last 1 to 2 Years",
				search_value: "[NOW-2YEARS TO NOW-1YEARS]",
			},
			{
				name: "2 to 5 Years",
				filter: "2-5-year",
				dropdown_name: "Last 2 to 5 Years",
				search_value: "[NOW-5YEARS TO NOW-2YEARS]",
			},
			{
				name: "More Than 5 Years",
				filter: "more-5-year",
				dropdown_name: "More Than 5 Years Ago",
				search_value: "[NOW-15YEARS TO NOW-5YEARS]",
			}			
		]
	},
	{
		name: "Last Contract",
		show_name: true,
		exclude_stage: ["pipeline"],
		//search_key: "last_contract_date_formatted",
		multi_filter:true,
		search_key: ["min_contract_date", "max_contract_date"],
		date_filter: true,
		search_value: ['last_contract_date_formatted:[{min_contract_date} TO {max_contract_date}]'],
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "No Activity",
				filter: "no-activity",
				dropdown_name: "No Activity",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z] AND -building_status:resale AND -offering_verified_status:\"sold out\" AND -inventory_sold:[1 TO *] AND -inventory_contract:[1 TO *]",
			},
			{
				name: "Any Activity",
				filter: "any-activity",
				dropdown_name: "Any Activity",
				search_value: "[NOW-15YEARS TO NOW] OR offering_verified_status:\"sold out\" OR building_status:resale",
			},
			{
				name: "Within 1 Year",
				filter: "1-year",
				dropdown_name: "Within 1 Year",
				search_value: "[NOW-1YEARS TO NOW]",
			},
			{
				name: "1 to 2 Years",
				filter: "2-year",
				dropdown_name: "Last 1 to 2 Years",
				search_value: "[NOW-2YEARS TO NOW-1YEARS]",
			},
			{
				name: "2 to 5 Years",
				filter: "2-5-year",
				dropdown_name: "Last 2 to 5 Years",
				search_value: "[NOW-5YEARS TO NOW-2YEARS]",
			},
			{
				name: "More Than 5 Years",
				filter: "more-5-year",
				dropdown_name: "More Than 5 Years Ago",
				search_value: "[NOW-15YEARS TO NOW-5YEARS]",
			}			
		]
	},
	{
		name: "Last Sale",
		show_name: true,
		exclude_stage: ["pipeline"],
		//search_key: "last_sold_date_formatted",
		multi_filter:true,
		search_key: ["min_sold_date", "max_sold_date"],
		date_filter: true,
		search_value: ['last_sold_date_formatted:[{min_sold_date} TO {max_sold_date}]'],
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "No Activity",
				filter: "no-activity",
				dropdown_name: "No Activity",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z] AND -offering_verified_status:\"sold out\" AND inventory_sold:[* TO 0]",
			},
			{
				name: "Any Activity",
				filter: "any-activity",
				dropdown_name: "Any Activity",
				search_value: "[NOW-15YEARS TO NOW] OR offering_verified_status:\"sold out\"",
			},
			{
				name: "Within 1 Year",
				filter: "1-year",
				dropdown_name: "Within 1 Year",
				search_value: "[NOW-1YEARS TO NOW]",
			},
			{
				name: "1 to 2 Years",
				filter: "2-year",
				dropdown_name: "Last 1 to 2 Years",
				search_value: "[NOW-2YEARS TO NOW-1YEARS]",
			},
			{
				name: "2 to 5 Years",
				filter: "2-5-year",
				dropdown_name: "Last 2 to 5 Years",
				search_value: "[NOW-5YEARS TO NOW-2YEARS]",
			},
			{
				name: "More Than 5 Years",
				filter: "more-5-year",
				dropdown_name: "More Than 5 Years Ago",
				search_value: "[NOW-15YEARS TO NOW-5YEARS]",
			}			
		]
	},
	{
		name: "Offering Status",
		show_name: true,
		admin_only: true,
		search_key: "offering_submitted_date",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "All Submitted",
				filter: "submitted",
				dropdown_name: "All Submitted",
				search_value: "[NOW-25YEARS TO NOW]",
			},
			{
				name: "All Accepted",
				filter: "accepted",
				dropdown_name: "All Accepted",
				search_value: "[NOW-25YEARS TO NOW] AND offering_accepted_date:[NOW-25YEARS TO NOW]",
			},
			{
				name: "All Effective",
				filter: "effective",
				dropdown_name: "All Effective",
				search_value: "[NOW-25YEARS TO NOW] AND offering_effective_date:[NOW-25YEARS TO NOW]",
			},
			{
				name: "Only Submitted",
				filter: "submitted-only",
				dropdown_name: "Only Submitted",
				search_value: "[NOW-25YEARS TO NOW] AND offering_accepted_date:[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z]",
			},
			{
				name: "Only Accepted",
				filter: "accepted-only",
				dropdown_name: "Only Accepted",
				search_value: "[NOW-25YEARS TO NOW] AND offering_accepted_date:[NOW-25YEARS TO NOW] AND offering_effective_date:[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z]",
			},
			{
				name: "Only Effectiv Only",
				filter: "effective-only",
				dropdown_name: "Only Effective",
				search_value: "[NOW-25YEARS TO NOW] AND offering_effective_date:[NOW-25YEARS TO NOW]",
			}
			
		]
	},
	{
		name: "Condo Dec",
		show_name: true,
		exclude_stage: ["pipeline", "resale"],
		search_key: "cdec_date",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "No Condo Dec",
				filter: "no-cdec",
				dropdown_name: "No Condo Dec",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z] AND building_status:(pipeline sponsor)",
			},
			{
				name: "Has Condo Dec",
				filter: "has-cdec",
				dropdown_name: "Has Condo Dec",
				search_value: "[1970-01-05T00:00:00Z TO NOW] OR building_status:(resale)",
			}			
		]
	},
	{
		name: "Status",
		show_name: true,
		search_key: "offering_verified_status",
		admin_only: true,
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Monitoring",
				filter: "monitoring",
				dropdown_name: "Monitoring",
				search_value: 'monitoring'
			},
			{
				name: "Reviewed",
				filter: "reviewed",
				dropdown_name: "Reviewed",
				search_value: 'reviewed'
			},
			{
				name: "Requesting Offering Plan",
				filter: "offering-plan-requested",
				dropdown_name: "Requesting Offering Plan",
				search_value: '("offering plan required")'
			},
			{
				name: "Offering Plan Acquired",
				filter: "offering-plan-acquired",
				dropdown_name: "Offering Plan Acquired",
				search_value: '("offering plan acquired")'
			},
			{
				name: "Condo Dec Vetted",
				filter: "condo-dec-vetted",
				dropdown_name: "Condo Dec Vetted",
				search_value: '("condo declaration vetted")'
			},
			{
				name: "Vetting Required",
				filter: "vetting-required",
				dropdown_name: "Vetting Required",
				search_value: '("vetting required")'
			},
			{
				name: "Vetted",
				filter: "vetted",
				dropdown_name: "Vetted",
				search_value: "vetted"
			},
			{
				name: "Coming Soon",
				filter: "coming-soon",
				dropdown_name: "Coming Soon",
				search_value: '("coming soon")'
			},
			{
				name: "Active",
				filter: "active",
				dropdown_name: "Active",
				search_value: 'active'
			},
			{
				name: "Abandoned",
				filter: "abandoned",
				dropdown_name: "Abandoned",
				search_value: "abandoned"
			},
			{
				name: "Not Abandoned",
				filter: "not-abandoned",
				dropdown_name: "Not Abandoned",
				search_value: '("" "monitoring" "reviewed" "offering plan acquired" "offering plan required" "condo declaration vetted" "vetting required" "vetted" "coming soon" "active" "sold out")'
			},			
			{
				name: "Sold Out",
				filter: "sold-out",
				dropdown_name: "Sold Out",
				search_value: '("sold out")'
			},
			{
				name: "Excluded",
				filter: "excluded",
				dropdown_name: "Excluded",
				search_value: "excluded"
			},

		]
	},	
/*	{
		name: "Total Financed",
		show_name: true,
		search_key: "total_loan_amount",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< $5M",
				filter: "less-than-5m",
				dropdown_name: "Less than $5M",
				search_value: "[0 TO 5000000]"
			},
			{
				name: "$5M to $10M",
				filter: "5-to-10",
				dropdown_name: "$5M to $10M",
				search_value: "[5000000 TO 10000000]"
			},
			{
				name: "$10M to $25M",
				filter: "10-to-25",
				dropdown_name: "$10M to $25M",
				search_value: "[10000000 TO 25000000]"
			},
			{
				name: "$25M to $50M",
				filter: "25-to-50",
				dropdown_name: "$25M to $50M",
				search_value: "[25000000 TO 50000000]"
			},
			{
				name: "$50M to $100M",
				filter: "50-to-100",
				dropdown_name: "$50M to $100M",
				search_value: "[50000000 TO 100000000]"
			},
			{
				name: "$100M to $250M",
				filter: "100-to-250",
				dropdown_name: "$100M to $250M",
				search_value: "[100000000 TO 250000000]"
			},
			{
				name: "$250M to $500M",
				filter: "250-to-500",
				dropdown_name: "$250M to $500M",
				search_value: "[250000000 TO 500000000]"
			},
			{
				name: "$500M to $1B",
				filter: "500-to-1b",
				dropdown_name: "$500M to $1B",
				search_value: "[500000000 TO 1000000000]"
			},

			{
				name: "$1B+",
				filter: "1b-or-more",
				dropdown_name: "$1B or more",
				search_value: "[1000000000 TO *]"
			}
		]
	},*/

];

export const _marketActivityFilters = [
	{
		name: "Ownership",
		show_name: true,
		search_key: "event_tags",
		multi_filter:true,
		transaction_hide:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "(condo* !condo*p)",
			},
			{
				name: "Co-op",
				filter: "coop",
				dropdown_name: "Co-op",
				search_value: "(co?op* coop* !condo*p)",
			},			
			{
				name: "Condop",
				filter: "condop",
				dropdown_name: "Condop",
				search_value: "(condop*)",
			},
			{
				name: "Single-Family",
				filter: "single-family",
				dropdown_name: "Single-Family",
				search_value: "(single?family)",
			},			
			{
				name: "Multi-Family",
				filter: "multi-family",
				dropdown_name: "Multi-Family",
				search_value: "(multi?family)",
			}
			
		]
	},
	{
		name: "Ownership",
		show_name: true,
		search_key: "ownership_type",
		multi_filter:true,
		activity_hide:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "(*condo* !condo*p)",
			},
			{
				name: "Co-op",
				filter: "coop",
				dropdown_name: "Co-op",
				search_value: "(*co?op* *coop* !condo*p)",
			},			
			{
				name: "Condop",
				filter: "condop",
				dropdown_name: "Condop",
				search_value: "(condop*)",
			},
			{
				name: "Single-Family",
				filter: "single-family",
				dropdown_name: "Single-Family",
				search_value: "(single?family) AND total_units:[1 TO 1]",
			},			
			{
				name: "Multi-Family",
				filter: "multi-family",
				dropdown_name: "Multi-Family",
				search_value: "((multi?family) OR (ownership_type:rental AND total_units:[* TO 3]))",
			}
			
		]
	},
	{
		name: "Price",
		show_name: true,
		search_key: ["min_price", "max_price"],
		filter_key : "price",
		price_filter: true,
		search_value: ['price:[{min_price} TO {max_price}]']					
	},
	{
		name: "PPSF",
		show_name: true,
		search_key: ["min_ppsf_price", "max_ppsf_price"],
		filter_key : "asking_ppsf",
		price_filter: true,
		target: 'fq',
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['{!frange l={min_ppsf_price} u={max_ppsf_price}}div(price,sq_ft)'],
		
	},
	{
		name: "Bedrooms",
		show_name: true,
		search_key: "bedrooms",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Studio",
				filter: "studio",
				dropdown_name: "Studio",
				search_value: "0",
			},
			{
				name: "1 Bedroom",
				filter: "1-bedroom",
				dropdown_name: "1 Bedroom",
				search_value: "1",
			},
			{
				name: "2 Bedroom",
				filter: "2-bedroom",
				dropdown_name: "2 Bedroom",
				search_value: "2",
			},
			{
				name: "3 Bedroom",
				filter: "3-bedroom",
				dropdown_name: "3 Bedroom",
				search_value: "3",
			},
			{
				name: "4+ Bedroom",
				filter: "4-or-more",
				dropdown_name: "4+ Bedroom",
				search_value: "[4 TO *]",
			},
		]
	},
	{
		name: "Baths",
		show_name: true,
		search_key: "baths",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},			
			{
				name: "1 Bath",
				filter: "1-bath",
				dropdown_name: "1 Bath",
				search_value: "1",
			},
			{
				name: "2 Bath",
				filter: "2-bath",
				dropdown_name: "2 Bath",
				search_value: "2",
			},
			{
				name: "3 Bath",
				filter: "3-bath",
				dropdown_name: "3 Bath",
				search_value: "3",
			},
			{
				name: "4+ Bath",
				filter: "4-or-more",
				dropdown_name: "4+ Bath",
				search_value: "[4 TO *]",
			},
		]
	},	
	{
		name: "Amenities",
		show_name: true,
		search_key: "building_features",
		autocomplete: true,
		core_name: "we3-buildings",
		filter_type: "building",
		fq: ["deleted:false"],
		group_amenities: true
		
	},
	{
		name: "Interior SF",
/*
		show_name: true,
		search_key: "sq_ft",
*/
		name: "Interior SF",
		show_name: true,
		search_key: ["min_sq_ft", "max_sq_ft"],
		filter_key : "sq_ft",
		price_filter: true,
		labels : ["Min Sq Ft", "Max Sq Ft"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['sq_ft:[{min_sq_ft} TO {max_sq_ft}]'], 
		value_prefix : "",
		title_suffix : "SF",
		values: [
			{
				name: "Any Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< 500 ",
				filter: "less-than-500",
				dropdown_name: "Less than 500 SF",
				search_value: "[10 TO 500]"
			},
			{
				name: "501 to 750 SF",
				filter: "501-to-750",
				dropdown_name: "501 to 750 SF",
				search_value: "[501 TO 750]"
			},
			{
				name: "751 to 1,000 SF",
				filter: "751-to-1000",
				dropdown_name: "751 to 1000 SF",
				search_value: "[751 TO 1000]"
			},
			{
				name: "1,001 to 1,500 SF",
				filter: "1001-to-1500",
				dropdown_name: "1,001 to 1,500 SF",
				search_value: "[1001 TO 1500]"
			},
			{
				name: "1,501 to 2,000 SF",
				filter: "1501-to-2000",
				dropdown_name: "1,501 to 2,000 SF",
				search_value: "[1501 TO 2000]"
			},
			{
				name: "2,001 to 3,000 SF",
				filter: "2001-to-3000",
				dropdown_name: "2,001 to 3,000 SF",
				search_value: "[2001 TO 3000]"
			},
			{
				name: "3,000+ SF",
				filter: "3001-or-more",
				dropdown_name: "More than 3000 SF",
				search_value: "[3001 TO *]"
			}
		]
	},	
	{
		name: "Exterior SF",
		show_name: true,
		search_key: "offering_exterior_sq_ft",
		filter_type: "property",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Has Exterior SF",
				filter: "has-exterior-sf",
				dropdown_name: "Has Exterior SF",
				search_value: "[1 TO *]"
			},
			{
				name: "No Exterior SF",
				filter: "no-exterior-sf",
				dropdown_name: "No Exterior SF",
				search_value: "[* TO 0]"
			},
			{
				name: "< 50 ",
				filter: "less-than-50",
				dropdown_name: "Less than 50 SF",
				search_value: "[0 TO 50]"
			},
			{
				name: "50 to 100 SF",
				filter: "50-to-100",
				dropdown_name: "50 to 100 SF",
				search_value: "[51 TO 100]"
			},
			{
				name: "101 to 200 SF",
				filter: "100-to-200",
				dropdown_name: "101 to 200 SF",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 to 500 SF",
				filter: "200-to-500",
				dropdown_name: "201 to 500 SF",
				search_value: "[201 TO 500]"
			},			
			{
				name: "500+ SF",
				filter: "500-more",
				dropdown_name: "More than 500 SF",
				search_value: "[501 TO *]"
			}
		]
	},
// 	{
// 		name: "Floors",
// 		show_name: true,
// 		search_key: ["min_floor", "max_floor"],
// 		filter_key : "floor",
// 		price_filter: true,
// 		// target: 'fq',
// 		labels : ["Min Floor", "Max Floor"],
// 
// 		custom_min_max_values : {
// 			"min" : [1, 11, 21, 31, 41],
// 			"max" : [10, 20, 30, 40, 50,"No Max"]
// 		},	
// 		format_type : "number",
// 		search_value: ['floor:[{min_floor} TO {max_floor}]']		,
// 		format_type : "number",
// 		value_prefix : "",	
// 		no_max:true,
// 		
// 		
// 	},
	{
		name: "Floors",
		show_name: true,
		search_key: ["min_floor", "max_floor"],
		filter_key : "floor",
		price_filter: true,
		activity_hide:true,
		labels : ["Min Floor", "Max Floor"],
	
		custom_min_max_values : {
			"min" : [1, 11, 21, 31, 41],
			"max" : [10, 20, 30, 40, 50, "No Max"]
	
		},	
		no_max:true,
		format_type : "number",
		search_value: ['floor:[{min_floor} TO {max_floor}]']		,
		format_type : "number",
		value_prefix : "",	
		
		
	},
	{
		name: "Year Built",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "year_built",
		filter_type: "building",
		multi_filter: true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Within 1 Year",
				filter: "1-year",
				dropdown_name: "Within 1 Year",
				search_value: `[${(current_year - 1)} TO ${current_year}] OR year_converted:[${(current_year - 1)} TO ${current_year}]`,
			},
			{
				name: "1 to 2 Years",
				filter: "2-year",
				dropdown_name: "Last 1 to 2 Years",
				search_value: `[${(current_year - 2)} TO ${current_year-1}] OR year_converted:[${(current_year - 2)} TO ${current_year-1}]`,
			},			
			{
				name: "2 to 5 Years",
				filter: "2-5-year",
				dropdown_name: "Last 2 to 5 Years",
				search_value: `[${(current_year - 5)} TO ${current_year-2}] OR year_converted:[${(current_year - 5)} TO ${current_year-2}]`,
			},
			{
				name: "5 to 7 Years",
				filter: "5-7-year",
				dropdown_name: "Last 5 to 7 Years",
				search_value: `[${(current_year - 7)} TO ${current_year-5}] OR year_converted:[${(current_year - 7)} TO ${current_year-5}]`,
	
			},
			{
				name: "7 to 10 Years",
				filter: "7-10-year",
				dropdown_name: "Last 5 to 10 Years",
				search_value: `[${(current_year - 10)} TO ${current_year-7}] OR year_converted:[${(current_year - 10)} TO ${current_year-7}]`,
			},
			{
				name: "10 to 15 Years",
				filter: "10-15-year",
				dropdown_name: "Last 10 to 15 Years",
				search_value: `[${(current_year - 15)} TO ${current_year - 10}] OR year_converted:[${(current_year - 15)} TO ${current_year - 10}]`,
			},
			{
				name: "More Than 15 Years",
				filter: "more-15-year",
				dropdown_name: "More Than 15 Years Ago",
				search_value: `[* TO ${current_year - 15}] OR year_converted:[* TO ${current_year - 15}]`,
			}				
		]
	},
	{
		name: "Condo Units",
		show_name: true,
		search_key: "inventory_total",
		filter_type: "building",
		multi_filter:true,
		values: [
			{
				name: "Any Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-10 Total Units",
				filter: "1-to-10",
				dropdown_name: "1 to 10 Units",
				search_value: "[1 TO 10]"
			},
			{
				name: "11-30 Total Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30 Units",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Total Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50 Units",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Total Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100 Units",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Total Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200 Units",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 + Total Units",
				filter: "201-or-more",
				dropdown_name: "201 or more Units",
				search_value: "[201 TO *]"
			}
		]
	},
	{
		name: "Unsold Units",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "inventory_unsold",
		filter_type: "building",
		multi_filter:true,
		values: [
			{
				name: "Any Unsold Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-10 Unsold Units",
				filter: "1-to-10",
				dropdown_name: "1 to 10",
				search_value: "[1 TO 10]"
			},
			{
				name: "11-30 Unsold Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Unsold Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Unsold Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Unsold Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 + Unsold Units",
				filter: "201-or-more",
				dropdown_name: "201 or more",
				search_value: "[201 TO *]"
			}
		]
	},
	{
		name: "% Sold",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "inventory_pct_sold",
		filter_type: "building",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Has Sales",
				filter: "has-sales",
				dropdown_name: "Has Sales",
				search_value: "[1 TO *] OR offering_verified_status:\"sold out\""
			},
			{
				name: "Has No Sales",
				filter: "no-sales",
				dropdown_name: "Has No Sales",
				search_value: "[* TO 0] AND -offering_verified_status:\"sold out\""
			},
			{
				name: "Less Than 15%",
				filter: "0-to-15",
				dropdown_name: "Less Than 15%",
				search_value: "[0 TO 15]"
			},
			{
				name: "Less Than 35%",
				filter: "0-to-35",
				dropdown_name: "Less Than 35%",
				search_value: "[0 TO 34]"
			},
			{
				name: "Less Than 50%",
				filter: "0-to-49",
				dropdown_name: "Less Than 50%",
				search_value: "[0 TO 49]"
			},
			{
				name: "More Than 15%",
				filter: "15-to-100",
				dropdown_name: "More Than 15%",
				search_value: "[15 TO *]"
			},
			{
				name: "More Than 50%",
				filter: "50-to-100",
				dropdown_name: "More Than 50%",
				search_value: "[50 TO *]"
			},
			{
				name: "50% to 79%",
				filter: "50-to-79",
				dropdown_name: "50% to 79%",
				search_value: "[50 TO 79]"
			},
			{
				name: "80% to 99%",
				filter: "80-to-99",
				dropdown_name: "80% to 99%",
				search_value: "[80 TO 99]"
			},
			{
				name: "Not Sold Out",
				filter: "not-sold-out",
				dropdown_name: "Not Sold Out",
				search_value: "[* TO 99] AND -offering_verified_status:\"sold out\""
			},
			{
				name: "Sold Out",
				filter: "sold-out",
				dropdown_name: "Sold Out",
				search_value: "[100 TO *]"
			}
		]
	},
	{
		name: "Construction",
		show_name: true,
		search_key: "building_effective_start",
		filter_type: "building",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Move-in Ready",
				filter: "completed",
				dropdown_name: "Move-in Ready (Has CO/TCO)",
				search_value: "[NOW-25YEARS TO NOW] OR building_status:(resale) OR offering_verified_status:(\"sold out\")",
			},
			{
				name: "Under Construction",
				filter: "not-completed",
				dropdown_name: "Under Construction (No CO/TCO)",
				search_value: "[* TO NOW-25YEARS] AND -building_status:(resale) AND -offering_verified_status:(\"sold out\")",
			}
			
		]
	},	
	{
		name: "Offering Type",
		show_name: true,
		search_key: "offering_construction",
		filter_type: "building",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "New Construction",
				filter: "new-construction",
				dropdown_name: "New Construction",
				search_value: "new",
			},
			{
				name: "Conversion",
				filter: "conversion",
				dropdown_name: "Conversion",
				search_value: "conversion",
			},			
			{
				name: "Rehab",
				filter: "rehab",
				dropdown_name: "Rehab",
				search_value: "rehab",
			},
			{
				name: "Exclude Conversion",
				filter: "exclude-conversion",
				dropdown_name: "Exclude Conversion",
				search_value: "(new rehab \"\")",
			}
			
		]
	},	
	{
		name: "Ownership",
		show_name: true,
		search_key: "ownership_type",
		filter_type: "building",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "condo AND -ownership_type:cooperative",
			},
			{
				name: "Co-op",
				filter: "co-op",
				dropdown_name: "Co-op",
				search_value: "coop",
			}
			
		]
	},	
	
	{
		name: "Sales Started",
		show_name: true,
		search_key: ["min_date", "max_date"],
		exclude_stage: ["pipeline"],
		filter_type: "building",
		date_filter: true,
		search_value: ['sales_start_date:[{min_date} TO {max_date}]']		
	},
	{
		name: "Offering Submitted",
		show_name: true,
		search_key: ["min_submitted_date", "max_submitted_date"],
		date_filter: true,
		filter_type: "building",
		search_value: ['offering_submitted_date:[{min_submitted_date} TO {max_submitted_date}]']		
	},
	{
		name: "Offering Accepted",
		show_name: true,
		search_key: ["min_accepted_date", "max_accepted_date"],
		date_filter: true,
		filter_type: "building",
		search_value: ['offering_accepted_date:[{min_accepted_date} TO {max_accepted_date}]']		
	},
	{
		name: "Offering Effective",
		show_name: true,
		search_key: ["min_effective_date", "max_effective_date"],
		date_filter: true,
		filter_type: "building",
		search_value: ['offering_effective_date:[{min_effective_date} TO {max_effective_date}]']		
	},
	/*	{
		name: "Sales Started",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "sales_start_date",
		filter_type: "building",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Not Started",
				filter: "not-started",
				dropdown_name: "Not Started",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z]",
			},
			{
				name: "Has Started",
				filter: "has-started",
				dropdown_name: "Has Started",
				search_value: "[NOW-15YEARS TO NOW]",
			},
			{
				name: "Within 1 Year",
				filter: "1-year",
				dropdown_name: "Within 1 Year",
				search_value: "[NOW-1YEARS TO NOW]",
			},
			{
				name: "1 to 2 Years",
				filter: "2-year",
				dropdown_name: "Last 1 to 2 Years",
				search_value: "[NOW-2YEARS TO NOW-1YEARS]",
			},
			{
				name: "2 to 5 Years",
				filter: "2-5-year",
				dropdown_name: "Last 2 to 5 Years",
				search_value: "[NOW-5YEARS TO NOW-2YEARS]",
			},
			{
				name: "5 to 7 Years",
				filter: "5-7-year",
				dropdown_name: "Last 5 to 7 Years",
				search_value: "[NOW-7YEARS TO NOW-5YEARS]",
			},
			{
				name: "7 to 10 Years",
				filter: "7-10-year",
				dropdown_name: "Last 5 to 10 Years",
				search_value: "[NOW-10YEARS TO NOW-7YEARS]",
			},
			{
				name: "10 to 15 Years",
				filter: "10-15-year",
				dropdown_name: "Last 10 to 15 Years",
				search_value: "[NOW-15YEARS TO NOW-10YEARS]",
			},
			{
				name: "More Than 15 Years",
				filter: "more-15-year",
				dropdown_name: "More Than 15 Years Ago",
				search_value: "[NOW-20YEARS TO NOW-15YEARS]",
			}				
		]
	},*/
	{
		name: "Last Listing",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "last_listed_sale_date_formatted",
		filter_type: "building",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "No Activity",
				filter: "no-activity",
				dropdown_name: "No Activity",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z]",
			},
			{
				name: "Any Activity",
				filter: "any-activity",
				dropdown_name: "Any Activity",
				search_value: "[NOW-15YEARS TO NOW]",
			},
			{
				name: "Within 1 Year",
				filter: "1-year",
				dropdown_name: "Within 1 Year",
				search_value: "[NOW-1YEARS TO NOW]",
			},
			{
				name: "1 to 2 Years",
				filter: "2-year",
				dropdown_name: "Last 1 to 2 Years",
				search_value: "[NOW-2YEARS TO NOW-1YEARS]",
			},
			{
				name: "2 to 5 Years",
				filter: "2-5-year",
				dropdown_name: "Last 2 to 5 Years",
				search_value: "[NOW-5YEARS TO NOW-2YEARS]",
			},
			{
				name: "More Than 5 Years",
				filter: "more-5-year",
				dropdown_name: "More Than 5 Years Ago",
				search_value: "[NOW-15YEARS TO NOW-5YEARS]",
			}			
		]
	},
	{
		name: "Last Contract",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "last_contract_date_formatted",
		filter_type: "building",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "No Activity",
				filter: "no-activity",
				dropdown_name: "No Activity",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z]",
			},
			{
				name: "Any Activity",
				filter: "any-activity",
				dropdown_name: "Any Activity",
				search_value: "[NOW-15YEARS TO NOW]",
			},
			{
				name: "Within 1 Year",
				filter: "1-year",
				dropdown_name: "Within 1 Year",
				search_value: "[NOW-1YEARS TO NOW]",
			},
			{
				name: "1 to 2 Years",
				filter: "2-year",
				dropdown_name: "Last 1 to 2 Years",
				search_value: "[NOW-2YEARS TO NOW-1YEARS]",
			},
			{
				name: "2 to 5 Years",
				filter: "2-5-year",
				dropdown_name: "Last 2 to 5 Years",
				search_value: "[NOW-5YEARS TO NOW-2YEARS]",
			},
			{
				name: "More Than 5 Years",
				filter: "more-5-year",
				dropdown_name: "More Than 5 Years Ago",
				search_value: "[NOW-15YEARS TO NOW-5YEARS]",
			}			
		]
	},
	{
		name: "Last Sale",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "last_sold_date_formatted",
		filter_type: "building",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "No Activity",
				filter: "no-activity",
				dropdown_name: "No Activity",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z] AND -offering_verified_status:\"sold out\"",
			},
			{
				name: "Any Activity",
				filter: "any-activity",
				dropdown_name: "Any Activity",
				search_value: "[NOW-15YEARS TO NOW] OR offering_verified_status:\"sold out\"",
			},
			{
				name: "Within 1 Year",
				filter: "1-year",
				dropdown_name: "Within 1 Year",
				search_value: "[NOW-1YEARS TO NOW]",
			},
			{
				name: "1 to 2 Years",
				filter: "2-year",
				dropdown_name: "Last 1 to 2 Years",
				search_value: "[NOW-2YEARS TO NOW-1YEARS]",
			},
			{
				name: "2 to 5 Years",
				filter: "2-5-year",
				dropdown_name: "Last 2 to 5 Years",
				search_value: "[NOW-5YEARS TO NOW-2YEARS]",
			},
			{
				name: "More Than 5 Years",
				filter: "more-5-year",
				dropdown_name: "More Than 5 Years Ago",
				search_value: "[NOW-15YEARS TO NOW-5YEARS]",
			}			
		]
	},
	{
		name: "Condo Dec",
		show_name: true,
		exclude_stage: ["pipeline"],
		search_key: "cdec_date",
		filter_type: "building",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "No Condo Dec",
				filter: "no-cdec",
				dropdown_name: "No Condo Dec",
				search_value: "[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z] AND building_status:(pipeline sponsor)",
			},
			{
				name: "Has Condo Dec",
				filter: "has-cdec",
				dropdown_name: "Has Condo Dec",
				search_value: "[1970-01-05T00:00:00Z TO NOW] OR building_status:(resale)",
			}			
		]
	},
	{
		name: "Offering Status",
		show_name: true,
		search_key: "offering_submitted_date",
		filter_type: "building",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "All Submitted",
				filter: "submitted",
				dropdown_name: "All Submitted",
				search_value: "[NOW-25YEARS TO NOW]",
			},
			{
				name: "All Accepted",
				filter: "accepted",
				dropdown_name: "All Accepted",
				search_value: "[NOW-25YEARS TO NOW] AND offering_accepted_date:[NOW-25YEARS TO NOW]",
			},
			{
				name: "All Effective",
				filter: "effective",
				dropdown_name: "All Effective",
				search_value: "[NOW-25YEARS TO NOW] AND offering_effective_date:[NOW-25YEARS TO NOW]",
			},
			{
				name: "Only Submitted",
				filter: "submitted-only",
				dropdown_name: "Only Submitted",
				search_value: "[NOW-25YEARS TO NOW] AND offering_accepted_date:[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z]",
			},
			{
				name: "Only Accepted",
				filter: "accepted-only",
				dropdown_name: "Only Accepted",
				search_value: "[NOW-25YEARS TO NOW] AND offering_accepted_date:[NOW-25YEARS TO NOW] AND offering_effective_date:[1970-01-01T00:00:00Z TO 1970-01-02T00:00:00Z]",
			},
			{
				name: "Only Effectiv Only",
				filter: "effective-only",
				dropdown_name: "Only Effective",
				search_value: "[NOW-25YEARS TO NOW] AND offering_effective_date:[NOW-25YEARS TO NOW]",
			}
			
		]
	},


];


export const _pipeLineFilter = [
	
/*	{
		name: "Stage",
		show_name: true,
		search_key: "event_tags",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Permit Filed",
				filter: "permit-filed",
				dropdown_name: "Permit Filed",
				search_value: "permit-filing",
			},
			{
				name: "Offering Submitted",
				filter: "offering-submitted",
				dropdown_name: "Offering Submitted",
				search_value: "submitted",
			},
			{
				name: "Offering Accepted",
				filter: "offering-accepted",
				dropdown_name: "Offering Accepted",
				search_value: "accepted",
			},
			{
				name: "Offering Effective",
				filter: "offering-effective",
				dropdown_name: "Offering Effective",
				search_value: "effective",
			},
			
		]
	},	*/
	{
		name: "Planned Units",
		show_name: true,
		//search_key: "units",
		search_key: ["min_units", "max_units"],
		filter_key : "inventory_unsold",
		price_filter: true,
		labels : ["Min Unsold Units", "Max Unsold Units"],
		min_max_values : [25, 200],
		min_max_increment : 25,
		custom_min_max_values : {
			"min" : [1, 11, 31, 51, 101, 201],
			"max" : [10, 30, 50, 100, 200, 300]
		},		
		format_type : "number",
		search_value: ['units:[{min_units} TO {max_units}]'], 
		value_prefix : "",	
		title_suffix : "Units",
		values: [
			{
				name: "Any Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "3-10 Units",
				filter: "3-to-10",
				dropdown_name: "3 to 10 Units",
				search_value: "[3 TO 10]"
			},
			{
				name: "11-30 Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30 Units",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50 Units",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100 Units",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200 Units",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 + Units",
				filter: "201-or-more",
				dropdown_name: "201 or more Units",
				search_value: "[201 TO *]"
			}
		]
	},
	{
		name: "Permit Type",
		show_name: true,
		search_key: "topic",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "New Building",
				filter: "new-building",
				dropdown_name: "New Building",
				search_value: "permits AND event_tags:nb",
			},
			{
				name: "Major Alteration",
				filter: "major-alteration",
				dropdown_name: "Major Alteration",
				search_value: "permits AND event_tags:a1",
			}
			
		]
	},
	{
		name: "Offering Type",
		show_name: true,
		search_key: "event_tags",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "New Condo",
				filter: "new-condo",
				dropdown_name: "New Condo",
				search_value: "new-condo",
			},
			{
				name: "Condo Conversion",
				filter: "condo-conversion",
				dropdown_name: "Condo Conversion",
				search_value: "condo-conversion",
			},
			{
				name: "New Co-op",
				filter: "new-coop",
				dropdown_name: "New Co-op",
				search_value: "new-coop",
			},
			{
				name: "Co-op Conversion ",
				filter: "coop-conversion",
				dropdown_name: "Co-op Conversion",
				search_value: "coop-conversion",
			},
			
		]
	},
	{
		name: "Offering Sellout",
		show_name: true,
		search_key: "price",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Under $10M",
				filter: "under-10m",
				dropdown_name: "Under $10M",
				search_value: "[* TO 10000000]"
			},
			{
				name: "$10M to $50M",
				filter: "10-to-50",
				dropdown_name: "$10M to $50M",
				search_value: "[10000000 TO 50000000]"
			},
			{
				name: "$50M to $100M",
				filter: "50-to-100",
				dropdown_name: "$50M to $100M",
				search_value: "[50000000 TO 100000000]"
			},
			{
				name: "$100M to $200M",
				filter: "100-to-200",
				dropdown_name: "$100M to $200M",
				search_value: "[100000000 TO 200000000]"
			},
			{
				name: "More Than $200M",
				filter: "more-than-200",
				dropdown_name: "More Than $200M",
				search_value: "[200000000 TO *]"
			},

			
		]
	},	
	
];

export const _propertySearchFilters = [
	{
		name: "Property Type",
		show_name: true,
		search_key: "property_type_filter",
		hierarchical_filter: true,
		no_key: true,
		minWidth: 200,
		values: [
			// === RESIDENTIAL ===
			{
				name: "Residential", filter: "residential", dropdown_name: "Residential", search_value: "",
				children: [
					{
						name: "Single Family", filter: "Residential__SingleFamily", dropdown_name: "Single Family", search_value: "",
						children: [
							{ name: "Cape Cod", filter: "A0", dropdown_name: "Cape Cod (A0)", search_value: "building_class:(A0)" },
							{ name: "Two Stories Detached", filter: "A1", dropdown_name: "Two Stories Detached (A1)", search_value: "building_class:(A1)" },
							{ name: "One Story - Permanent Living Quarter", filter: "A2", dropdown_name: "One Story - Permanent Living Quarter (A2)", search_value: "building_class:(A2)" },
							{ name: "Large Suburban Residence", filter: "A3", dropdown_name: "Large Suburban Residence (A3)", search_value: "building_class:(A3)" },
							{ name: "City Residence One Family", filter: "A4", dropdown_name: "City Residence One Family (A4)", search_value: "building_class:(A4)" },
							{ name: "One Family Attached or Semi-Detached", filter: "A5", dropdown_name: "One Family Attached or Semi-Detached (A5)", search_value: "building_class:(A5)" },
							{ name: "Summer Cottage", filter: "A6", dropdown_name: "Summer Cottage (A6)", search_value: "building_class:(A6)" },
							{ name: "Bungalow Colony", filter: "A8", dropdown_name: "Bungalow Colony (A8)", search_value: "building_class:(A8)" },
							{ name: "Miscellaneous One Family", filter: "A9", dropdown_name: "Miscellaneous One Family (A9)", search_value: "building_class:(A9)" },
						]
					},
					{
						name: "Townhouse/Rowhouse", filter: "Residential__TownhouseRowhouse", dropdown_name: "Townhouse/Rowhouse", search_value: "",
						children: [
							{ name: "Mansion Type or Town House", filter: "A7", dropdown_name: "Mansion Type or Town House (A7)", search_value: "building_class:(A7)" },
						]
					},
					{
						name: "2-4 Family", filter: "Residential__Family", dropdown_name: "2-4 Family", search_value: "",
						children: [
							{ name: "Two Family Brick", filter: "B1", dropdown_name: "Two Family Brick (B1)", search_value: "building_class:(B1)" },
							{ name: "Two Family Frame", filter: "B2", dropdown_name: "Two Family Frame (B2)", search_value: "building_class:(B2)" },
							{ name: "Two Family Converted from One Family", filter: "B3", dropdown_name: "Two Family Converted from One Family (B3)", search_value: "building_class:(B3)" },
							{ name: "Miscellaneous Two Family", filter: "B9", dropdown_name: "Miscellaneous Two Family (B9)", search_value: "building_class:(B9)" },
							{ name: "Three Families", filter: "C0", dropdown_name: "Three Families (C0)", search_value: "building_class:(C0)" },
							{ name: "Four Families", filter: "C3", dropdown_name: "Four Families (C3)", search_value: "building_class:(C3)" },
						]
					},
					{
						name: "Co-op Building", filter: "Residential__CoopBuilding", dropdown_name: "Co-op Building", search_value: "",
						children: [
							{ name: "Walk-up Co-op Building; Loft/Warehouse", filter: "XC8", dropdown_name: "Walk-up Co-op Bldg; Loft/Warehouse (XC8)", search_value: "building_class:(XC8)" },
							{ name: "Elevator Co-op Building; Loft/Warehouse", filter: "XD0", dropdown_name: "Elevator Co-op Bldg; Loft/Warehouse (XD0)", search_value: "building_class:(XD0)" },
							{ name: "Elevator Apartments Cooperatives Building", filter: "XD4", dropdown_name: "Elevator Apts Co-op Bldg (XD4)", search_value: "building_class:(XD4)" },
							{ name: "Walk-up Cooperative Building", filter: "XC6", dropdown_name: "Walk-up Co-op Bldg (XC6)", search_value: "building_class:(XC6)" },
							{ name: "Walk-Up Co-Op Apartments <11 Units", filter: "XCC", dropdown_name: "Walk-Up Co-Op Apts <11 Units (XCC)", search_value: "building_class:(XCC)" },
							{ name: "Elevator Co-op Apartment <11 Units", filter: "XDC", dropdown_name: "Elevator Co-op Apt <11 Units (XDC)", search_value: "building_class:(XDC)" },
						]
					},
					{
						name: "Condo Building", filter: "Residential__CondoBuilding", dropdown_name: "Condo Building", search_value: "",
						children: [
							{ name: "Residential Building (Mixed Residential Condo Building)", filter: "RD", dropdown_name: "Mixed Residential Condo Bldg (RD)", search_value: "building_class:(RD)" },
							{ name: "Special Condominium Billing Lot", filter: "R0", dropdown_name: "Special Condo Billing Lot (R0)", search_value: "building_class:(R0)" },
						]
					},
					{
						name: "Other Residential", filter: "Residential__OtherResidential", dropdown_name: "Other Residential", search_value: "",
						children: [
							{ name: "Old Law Tenement", filter: "C4", dropdown_name: "Old Law Tenement (C4)", search_value: "building_class:(C4)" },
							{ name: "Terraces/Gardens/Cabanas", filter: "RT", dropdown_name: "Terraces/Gardens/Cabanas (RT)", search_value: "building_class:(RT)" },
						]
					},
				]
			},
			// === MULTI-FAMILY ===
			{
				name: "Multi-Family", filter: "multi-family", dropdown_name: "Multi-Family", search_value: "",
				children: [
					{
						name: "5+ Family", filter: "MultiFamily__Family", dropdown_name: "5+ Family", search_value: "",
						children: [
							{ name: "Over Six Families without Stores", filter: "C1", dropdown_name: "Over Six Families without Stores (C1)", search_value: "building_class:(C1)" },
							{ name: "Five to Six Families", filter: "C2", dropdown_name: "Five to Six Families (C2)", search_value: "building_class:(C2)" },
						]
					},
					{
						name: "Apartments", filter: "MultiFamily__Apartments", dropdown_name: "Apartments", search_value: "",
						children: [
							{ name: "Elevator Apartment - Semi-fireproof without Stores", filter: "D1", dropdown_name: "Elevator Apt - Semi-fireproof (D1)", search_value: "building_class:(D1)" },
							{ name: "Elevator Apartment - Artists in Residence", filter: "D2", dropdown_name: "Elevator Apt - Artists in Residence (D2)", search_value: "building_class:(D2)" },
							{ name: "Elevator Apartment - Fireproof without Stores", filter: "D3", dropdown_name: "Elevator Apt - Fireproof (D3)", search_value: "building_class:(D3)" },
							{ name: "Elevator Apartment - Converted", filter: "D5", dropdown_name: "Elevator Apt - Converted (D5)", search_value: "building_class:(D5)" },
							{ name: "Elevator Apartment - Luxury Type", filter: "D8", dropdown_name: "Elevator Apt - Luxury Type (D8)", search_value: "building_class:(D8)" },
							{ name: "Elevator Apartment - Miscellaneous", filter: "D9", dropdown_name: "Elevator Apt - Miscellaneous (D9)", search_value: "building_class:(D9)" },
							{ name: "Garden Apartments", filter: "C9", dropdown_name: "Garden Apartments (C9)", search_value: "building_class:(C9)" },
							{ name: "Walk-Up Apartments <11 Units", filter: "CB", dropdown_name: "Walk-Up Apts <11 Units (CB)", search_value: "building_class:(CB)" },
							{ name: "Elevator Apartments <11 Units", filter: "DB", dropdown_name: "Elevator Apts <11 Units (DB)", search_value: "building_class:(DB)" },
						]
					},
					{
						name: "Dormitories", filter: "MultiFamily__Dormitories", dropdown_name: "Dormitories", search_value: "",
						children: [
							{ name: "Dormitory", filter: "H8", dropdown_name: "Dormitory (H8)", search_value: "building_class:(H8)" },
						]
					},
					{
						name: "Public Housing/Subsidized", filter: "MultiFamily__PublicHousingSubsidized", dropdown_name: "Public Housing/Subsidized", search_value: "",
						children: [
							{ name: "SRO - Affordable Housing", filter: "HR", dropdown_name: "SRO - Affordable Housing (HR)", search_value: "building_class:(HR)" },
						]
					},
					{
						name: "Mobile Homes in Parks", filter: "MultiFamily__MobileHomesinParks", dropdown_name: "Mobile Homes in Parks", search_value: "",
						children: [
							{ name: "Mobile Homes/Trailer Parks", filter: "CM", dropdown_name: "Mobile Homes/Trailer Parks (CM)", search_value: "building_class:(CM)" },
						]
					},
				]
			},
			// === VACANT LAND ===
			{
				name: "Vacant Land", filter: "vacant-land", dropdown_name: "Vacant Land", search_value: "",
				children: [
					{
						name: "Vacant Residential", filter: "VacantLand__VacantResidential", dropdown_name: "Vacant Residential", search_value: "",
						children: [
							{ name: "Zoned Residential - Not Manhattan", filter: "V0", dropdown_name: "Zoned Residential - Not Manhattan (V0)", search_value: "building_class:(V0)" },
							{ name: "Zoned Primarily Residential - Not Manhattan", filter: "V3", dropdown_name: "Zoned Primarily Residential (V3)", search_value: "building_class:(V3)" },
							{ name: "Community Garden Zoned Residential", filter: "VG", dropdown_name: "Community Garden Zoned Residential (VG)", search_value: "building_class:(VG)" },
						]
					},
					{
						name: "Vacant Commercial", filter: "VacantLand__VacantCommercial", dropdown_name: "Vacant Commercial", search_value: "",
						children: [
							{ name: "Zoned Commercial or Manhattan Residential", filter: "V1", dropdown_name: "Zoned Commercial or Manhattan Res (V1)", search_value: "building_class:(V1)" },
							{ name: "Zoned Commercial Adjacent to Tax Class 1", filter: "V2", dropdown_name: "Zoned Commercial Adj Tax Class 1 (V2)", search_value: "building_class:(V2)" },
							{ name: "Community Garden Zoned Commercial", filter: "VC", dropdown_name: "Community Garden Zoned Commercial (VC)", search_value: "building_class:(VC)" },
						]
					},
					{
						name: "Vacant Government Owned", filter: "VacantLand__VacantGovernmentOwned", dropdown_name: "Vacant Government Owned", search_value: "",
						children: [
							{ name: "Port Authority of NY and NJ", filter: "V7", dropdown_name: "Port Authority of NY and NJ (V7)", search_value: "building_class:(V7)" },
							{ name: "New York State or US Government", filter: "V8", dropdown_name: "New York State or US Government (V8)", search_value: "building_class:(V8)" },
							{ name: "Police or Fire Department", filter: "V4", dropdown_name: "Police or Fire Department (V4)", search_value: "building_class:(V4)" },
						]
					},
					{
						name: "Other Vacant", filter: "VacantLand__OtherVacant", dropdown_name: "Other Vacant", search_value: "",
						children: [
							{ name: "Open Space", filter: "Q0", dropdown_name: "Open Space (Q0)", search_value: "building_class:(Q0)" },
							{ name: "School Site or Yard", filter: "V5", dropdown_name: "School Site or Yard (V5)", search_value: "building_class:(V5)" },
							{ name: "Library, Hospital or Museum", filter: "V6", dropdown_name: "Library, Hospital or Museum (V6)", search_value: "building_class:(V6)" },
							{ name: "Miscellaneous Vacant Land", filter: "V9", dropdown_name: "Miscellaneous Vacant Land (V9)", search_value: "building_class:(V9)" },
						]
					},
				]
			},
			// === MIXED USE ===
			{
				name: "Mixed Use", filter: "mixed-use", dropdown_name: "Mixed Use", search_value: "",
				children: [
					{
						name: "Small Residential & Retail/Office", filter: "MixedUse__SmallResidentialRetailOffice", dropdown_name: "Small Res & Retail/Office", search_value: "",
						children: [
							{ name: "Primarily One Family with Two Stores or Offices", filter: "S0", dropdown_name: "One Family w/ Two Stores/Offices (S0)", search_value: "building_class:(S0)" },
							{ name: "Primarily One Family with One Store or Office", filter: "S1", dropdown_name: "One Family w/ One Store/Office (S1)", search_value: "building_class:(S1)" },
							{ name: "Primarily Two Family with One Store or Office", filter: "S2", dropdown_name: "Two Family w/ One Store/Office (S2)", search_value: "building_class:(S2)" },
							{ name: "Primarily Three Family with One Store or Office", filter: "S3", dropdown_name: "Three Family w/ One Store/Office (S3)", search_value: "building_class:(S3)" },
							{ name: "Primarily Four Family with One Store or Office", filter: "S4", dropdown_name: "Four Family w/ One Store/Office (S4)", search_value: "building_class:(S4)" },
							{ name: "Single or Multiple Dwelling with Stores or Offices", filter: "S9", dropdown_name: "Dwelling w/ Stores or Offices (S9)", search_value: "building_class:(S9)" },
						]
					},
					{
						name: "Multi-Family & Retail/Office", filter: "MixedUse__MultiFamilyRetailOffice", dropdown_name: "Multi-Family & Retail/Office", search_value: "",
						children: [
							{ name: "Office with Apartments Only", filter: "O8", dropdown_name: "Office with Apartments Only (O8)", search_value: "building_class:(O8)" },
							{ name: "Primarily Five to Six Family with One Store", filter: "S5", dropdown_name: "Five to Six Family w/ Store (S5)", search_value: "building_class:(S5)" },
							{ name: "Elevator Apartment - Fireproof with Stores", filter: "D6", dropdown_name: "Elevator Apt - Fireproof w/ Stores (D6)", search_value: "building_class:(D6)" },
							{ name: "Elevator Apartment - Semi-Fireproof with Stores", filter: "D7", dropdown_name: "Elevator Apt - Semi-Fireproof w/ Stores (D7)", search_value: "building_class:(D7)" },
							{ name: "Walk-up Apartment Over Six Families with Stores", filter: "C7", dropdown_name: "Walk-up Apt 6+ Families w/ Stores (C7)", search_value: "building_class:(C7)" },
						]
					},
					{
						name: "Other Residential & Commercial", filter: "MixedUse__OtherResidentialCommercial", dropdown_name: "Other Res & Commercial", search_value: "",
						children: [
							{ name: "Mixed Residential & Commercial Building", filter: "RM", dropdown_name: "Mixed Res & Commercial Bldg (RM)", search_value: "building_class:(RM)" },
							{ name: "Mixed Residential & Commercial Building", filter: "RX", dropdown_name: "Mixed Res & Commercial Bldg (RX)", search_value: "building_class:(RX)" },
							{ name: "Commercial Building (Mixed Commercial Condo)", filter: "RC", dropdown_name: "Commercial Bldg - Mixed Condo (RC)", search_value: "building_class:(RC)" },
						]
					},
					{
						name: "Residential & Industrial", filter: "MixedUse__ResidentialIndustrial", dropdown_name: "Res & Industrial", search_value: "",
						children: [
							{ name: "Mixed Residential & Warehouse", filter: "RZ", dropdown_name: "Mixed Residential & Warehouse (RZ)", search_value: "building_class:(RZ)" },
						]
					},
					{
						name: "Commercial & Industrial", filter: "MixedUse__CommercialIndustrial", dropdown_name: "Commercial & Industrial", search_value: "",
						children: [
							{ name: "Mixed Warehouse/Factory/Industrial & Commercial", filter: "RI", dropdown_name: "Mixed Warehouse/Factory & Commercial (RI)", search_value: "building_class:(RI)" },
						]
					},
					{
						name: "Other Mixed Use", filter: "MixedUse__OtherMixedUse", dropdown_name: "Other Mixed Use", search_value: "",
						children: [
							{ name: "Cultural, Medical, Educational, etc.", filter: "RA", dropdown_name: "Cultural, Medical, Educational (RA)", search_value: "building_class:(RA)" },
						]
					},
				]
			},
			// === COMMERCIAL CONDOS ===
			{
				name: "Commercial Condos", filter: "commercial-condos", dropdown_name: "Commercial Condos", search_value: "",
				children: [
					{
						name: "Other Condos", filter: "CommercialCondos__OtherCondos", dropdown_name: "Other Condos", search_value: "",
						children: [
							{ name: "Condo - Commercial Unit of 1-3 Unit Building", filter: "R7", dropdown_name: "Condo - Commercial 1-3 Unit Bldg (R7)", search_value: "building_class:(R7)" },
							{ name: "Condo - Commercial Unit of 2-10 Unit Building", filter: "R8", dropdown_name: "Condo - Commercial 2-10 Unit Bldg (R8)", search_value: "building_class:(R8)" },
						]
					},
				]
			},
			// === OFFICE ===
			{
				name: "Office", filter: "office", dropdown_name: "Office", search_value: "",
				children: [
					{
						name: "Office Building", filter: "Office__OfficeBuilding", dropdown_name: "Office Building", search_value: "",
						children: [
							{ name: "Office Only - 1 Story", filter: "O1", dropdown_name: "Office Only - 1 Story (O1)", search_value: "building_class:(O1)" },
							{ name: "Office Only 2-6 Stories", filter: "O2", dropdown_name: "Office Only 2-6 Stories (O2)", search_value: "building_class:(O2)" },
							{ name: "Office Only 7-19 Stories", filter: "O3", dropdown_name: "Office Only 7-19 Stories (O3)", search_value: "building_class:(O3)" },
							{ name: "Office Only 20+ Stories", filter: "O4", dropdown_name: "Office Only 20+ Stories (O4)", search_value: "building_class:(O4)" },
							{ name: "Office with Commercial - 1 to 6 Stories", filter: "O5", dropdown_name: "Office w/ Commercial 1-6 Stories (O5)", search_value: "building_class:(O5)" },
							{ name: "Office with Commercial 7-19 Stories", filter: "O6", dropdown_name: "Office w/ Commercial 7-19 Stories (O6)", search_value: "building_class:(O6)" },
							{ name: "Professional Buildings/Funeral Homes", filter: "O7", dropdown_name: "Professional Bldgs/Funeral Homes (O7)", search_value: "building_class:(O7)" },
							{ name: "Office Space", filter: "RB", dropdown_name: "Office Space (RB)", search_value: "building_class:(RB)" },
						]
					},
					{
						name: "Bank/Credit Unions", filter: "Office__BankCreditUnions", dropdown_name: "Bank/Credit Unions", search_value: "",
						children: [
							{ name: "Banking Facilities with or without Parking", filter: "K7", dropdown_name: "Banking Facilities (K7)", search_value: "building_class:(K7)" },
							{ name: "Miscellaneous and Old Style Bank Buildings", filter: "O9", dropdown_name: "Old Style Bank Buildings (O9)", search_value: "building_class:(O9)" },
						]
					},
					{
						name: "Media/Technology", filter: "Office__MediaTechnology", dropdown_name: "Media/Technology", search_value: "",
						children: [
							{ name: "Television Studio", filter: "J6", dropdown_name: "Television Studio (J6)", search_value: "building_class:(J6)" },
						]
					},
					{
						name: "Government", filter: "Office__Government", dropdown_name: "Government", search_value: "",
						children: [
							{ name: "Court House", filter: "Z1", dropdown_name: "Court House (Z1)", search_value: "building_class:(Z1)" },
						]
					},
				]
			},
			// === RETAIL ===
			{
				name: "Retail", filter: "retail", dropdown_name: "Retail", search_value: "",
				children: [
					{
						name: "Store", filter: "Retail__Store", dropdown_name: "Store", search_value: "",
						children: [
							{ name: "Multi-Story Department Store", filter: "K3", dropdown_name: "Multi-Story Department Store (K3)", search_value: "building_class:(K3)" },
							{ name: "Big Box Retail", filter: "K8", dropdown_name: "Big Box Retail (K8)", search_value: "building_class:(K8)" },
							{ name: "Miscellaneous Store Building", filter: "K9", dropdown_name: "Miscellaneous Store Building (K9)", search_value: "building_class:(K9)" },
						]
					},
					{
						name: "Shopping Center", filter: "Retail__ShoppingCenter", dropdown_name: "Shopping Center", search_value: "",
						children: [
							{ name: "Shopping Center with or without Parking", filter: "K6", dropdown_name: "Shopping Center (K6)", search_value: "building_class:(K6)" },
						]
					},
					{
						name: "Showroom", filter: "Retail__Showroom", dropdown_name: "Showroom", search_value: "",
						children: [
							{ name: "Car Sales/Rental with Showroom", filter: "G8", dropdown_name: "Car Sales/Rental with Showroom (G8)", search_value: "building_class:(G8)" },
						]
					},
					{
						name: "Other Retail", filter: "Retail__OtherRetail", dropdown_name: "Other Retail", search_value: "",
						children: [
							{ name: "One Story Retail Building", filter: "K1", dropdown_name: "One Story Retail Building (K1)", search_value: "building_class:(K1)" },
							{ name: "Multi-Story Retail Building", filter: "K2", dropdown_name: "Multi-Story Retail Building (K2)", search_value: "building_class:(K2)" },
							{ name: "Predominant Retail with Other Uses", filter: "K4", dropdown_name: "Predominant Retail w/ Other Uses (K4)", search_value: "building_class:(K4)" },
							{ name: "Retail Space", filter: "RK", dropdown_name: "Retail Space (RK)", search_value: "building_class:(RK)" },
						]
					},
				]
			},
			// === INDUSTRIAL ===
			{
				name: "Industrial", filter: "industrial", dropdown_name: "Industrial", search_value: "",
				children: [
					{
						name: "Manufacturing/Processing", filter: "Industrial__ManufacturingProcessing", dropdown_name: "Manufacturing/Processing", search_value: "",
						children: [
							{ name: "Factory - Heavy Manufacturing (Fireproof)", filter: "F1", dropdown_name: "Heavy Manufacturing Fireproof (F1)", search_value: "building_class:(F1)" },
							{ name: "Factory - Special Construction (Fireproof)", filter: "F2", dropdown_name: "Special Construction Fireproof (F2)", search_value: "building_class:(F2)" },
							{ name: "Factory - Industrial Semi-Fireproof", filter: "F4", dropdown_name: "Industrial Semi-Fireproof (F4)", search_value: "building_class:(F4)" },
							{ name: "Factory - Light Manufacturing", filter: "F5", dropdown_name: "Light Manufacturing (F5)", search_value: "building_class:(F5)" },
							{ name: "Factory - Tank Farms", filter: "F8", dropdown_name: "Tank Farms (F8)", search_value: "building_class:(F8)" },
							{ name: "Factory - Industrial Miscellaneous", filter: "F9", dropdown_name: "Industrial Miscellaneous (F9)", search_value: "building_class:(F9)" },
						]
					},
					{
						name: "Warehouse", filter: "Industrial__Warehouse", dropdown_name: "Warehouse", search_value: "",
						children: [
							{ name: "Fireproof Warehouse", filter: "E1", dropdown_name: "Fireproof Warehouse (E1)", search_value: "building_class:(E1)" },
							{ name: "Contractors Warehouse", filter: "E2", dropdown_name: "Contractors Warehouse (E2)", search_value: "building_class:(E2)" },
							{ name: "Miscellaneous Warehouse", filter: "E9", dropdown_name: "Miscellaneous Warehouse (E9)", search_value: "building_class:(E9)" },
							{ name: "Warehouse/Factory/Industrial", filter: "RW", dropdown_name: "Warehouse/Factory/Industrial (RW)", search_value: "building_class:(RW)" },
						]
					},
					{
						name: "Self-Storage", filter: "Industrial__SelfStorage", dropdown_name: "Self-Storage", search_value: "",
						children: [
							{ name: "Self-Storage Warehouse", filter: "E7", dropdown_name: "Self-Storage Warehouse (E7)", search_value: "building_class:(E7)" },
							{ name: "Non-Business Storage Space", filter: "RS", dropdown_name: "Non-Business Storage Space (RS)", search_value: "building_class:(RS)" },
						]
					},
					{
						name: "Automotive", filter: "Industrial__Automotive", dropdown_name: "Automotive", search_value: "",
						children: [
							{ name: "Auto Body/Collision or Auto Repair", filter: "G2", dropdown_name: "Auto Body/Collision/Repair (G2)", search_value: "building_class:(G2)" },
							{ name: "Gas Station with Retail Store", filter: "G3", dropdown_name: "Gas Station w/ Retail Store (G3)", search_value: "building_class:(G3)" },
							{ name: "Gas Station with Service/Auto Repair", filter: "G4", dropdown_name: "Gas Station w/ Service/Repair (G4)", search_value: "building_class:(G4)" },
							{ name: "Gas Station Only", filter: "G5", dropdown_name: "Gas Station Only (G5)", search_value: "building_class:(G5)" },
							{ name: "Miscellaneous Garage", filter: "G9", dropdown_name: "Miscellaneous Garage (G9)", search_value: "building_class:(G9)" },
							{ name: "Car Sales/Rental without Showroom", filter: "GU", dropdown_name: "Car Sales/Rental w/o Showroom (GU)", search_value: "building_class:(GU)" },
							{ name: "Car Wash or Lubritorium Facility", filter: "GW", dropdown_name: "Car Wash or Lubritorium (GW)", search_value: "building_class:(GW)" },
						]
					},
				]
			},
			// === HOSPITALITY ===
			{
				name: "Hospitality", filter: "hospitality", dropdown_name: "Hospitality", search_value: "",
				children: [
					{
						name: "Hotel/Motel/Hostel/B&B", filter: "Hospitality__HotelMotelHostelBB", dropdown_name: "Hotel/Motel/Hostel/B&B", search_value: "",
						children: [
							{ name: "Luxury Hotel", filter: "H1", dropdown_name: "Luxury Hotel (H1)", search_value: "building_class:(H1)" },
							{ name: "Full Service Hotel", filter: "H2", dropdown_name: "Full Service Hotel (H2)", search_value: "building_class:(H2)" },
							{ name: "Limited Service Hotel", filter: "H3", dropdown_name: "Limited Service Hotel (H3)", search_value: "building_class:(H3)" },
							{ name: "Motel", filter: "H4", dropdown_name: "Motel (H4)", search_value: "building_class:(H4)" },
							{ name: "Hotel - Private Club, Luxury Type", filter: "H5", dropdown_name: "Hotel - Private Club Luxury (H5)", search_value: "building_class:(H5)" },
							{ name: "Apartment Hotel", filter: "H6", dropdown_name: "Apartment Hotel (H6)", search_value: "building_class:(H6)" },
							{ name: "Apartment Hotel - Cooperatively Owned", filter: "H7", dropdown_name: "Apartment Hotel - Co-op (H7)", search_value: "building_class:(H7)" },
							{ name: "Miscellaneous Hotel", filter: "H9", dropdown_name: "Miscellaneous Hotel (H9)", search_value: "building_class:(H9)" },
							{ name: "Boutique Hotel", filter: "HB", dropdown_name: "Boutique Hotel (HB)", search_value: "building_class:(HB)" },
							{ name: "Hostel", filter: "HH", dropdown_name: "Hostel (HH)", search_value: "building_class:(HH)" },
							{ name: "Hotel/Boatel", filter: "RH", dropdown_name: "Hotel/Boatel (RH)", search_value: "building_class:(RH)" },
							{ name: "Extended Stay/Suite", filter: "HS", dropdown_name: "Extended Stay/Suite (HS)", search_value: "building_class:(HS)" },
						]
					},
					{
						name: "Rooming/Boarding House", filter: "Hospitality__RoomingBoardingHouse", dropdown_name: "Rooming/Boarding House", search_value: "",
						children: [
							{ name: "Converted Dwellings or Rooming House", filter: "C5", dropdown_name: "Converted Dwellings/Rooming House (C5)", search_value: "building_class:(C5)" },
						]
					},
					{
						name: "Restaurant/Bar", filter: "Hospitality__RestaurantBar", dropdown_name: "Restaurant/Bar", search_value: "",
						children: [
							{ name: "Stand Alone Food Establishment", filter: "K5", dropdown_name: "Stand Alone Food Establishment (K5)", search_value: "building_class:(K5)" },
						]
					},
				]
			},
			// === ENTERTAINMENT/RECREATIONAL ===
			{
				name: "Entertainment/Recreational", filter: "entertainment", dropdown_name: "Entertainment/Recreational", search_value: "",
				children: [
					{
						name: "Parks", filter: "EntertainmentRecreational__Parks", dropdown_name: "Parks", search_value: "",
						children: [
							{ name: "Parks/Recreation Facilities", filter: "Q1", dropdown_name: "Parks/Recreation Facilities (Q1)", search_value: "building_class:(Q1)" },
							{ name: "Playground", filter: "Q2", dropdown_name: "Playground (Q2)", search_value: "building_class:(Q2)" },
						]
					},
					{
						name: "Country Club/Golf Course", filter: "EntertainmentRecreational__CountryClubGolfCourse", dropdown_name: "Country Club/Golf Course", search_value: "",
						children: [
							{ name: "Golf Course", filter: "Q5", dropdown_name: "Golf Course (Q5)", search_value: "building_class:(Q5)" },
						]
					},
					{
						name: "Marinas", filter: "EntertainmentRecreational__Marinas", dropdown_name: "Marinas", search_value: "",
						children: [
							{ name: "Marina/Yacht Club", filter: "Q8", dropdown_name: "Marina/Yacht Club (Q8)", search_value: "building_class:(Q8)" },
						]
					},
					{
						name: "Arts & Culture", filter: "EntertainmentRecreational__ArtsCulture", dropdown_name: "Arts & Culture", search_value: "",
						children: [
							{ name: "Theatre - Art Type Less than 400 Seats", filter: "J1", dropdown_name: "Theatre - Art Type <400 Seats (J1)", search_value: "building_class:(J1)" },
							{ name: "Theatre - Art Type More than 400 Seats", filter: "J2", dropdown_name: "Theatre - Art Type >400 Seats (J2)", search_value: "building_class:(J2)" },
							{ name: "Motion Picture Theatre with Balcony", filter: "J3", dropdown_name: "Motion Picture Theatre (J3)", search_value: "building_class:(J3)" },
							{ name: "Legitimate Theatre - Sole Use", filter: "J4", dropdown_name: "Legitimate Theatre (J4)", search_value: "building_class:(J4)" },
							{ name: "Theatre in Mixed-Use Building", filter: "J5", dropdown_name: "Theatre in Mixed-Use Bldg (J5)", search_value: "building_class:(J5)" },
							{ name: "Museum", filter: "P7", dropdown_name: "Museum (P7)", search_value: "building_class:(P7)" },
							{ name: "Library", filter: "P8", dropdown_name: "Library (P8)", search_value: "building_class:(P8)" },
							{ name: "Off Broadway Type Theatre", filter: "J7", dropdown_name: "Off Broadway Theatre (J7)", search_value: "building_class:(J7)" },
							{ name: "Multiplex Picture Theatre", filter: "J8", dropdown_name: "Multiplex Picture Theatre (J8)", search_value: "building_class:(J8)" },
							{ name: "Miscellaneous Theatre", filter: "J9", dropdown_name: "Miscellaneous Theatre (J9)", search_value: "building_class:(J9)" },
						]
					},
					{
						name: "Athletic Facilities", filter: "EntertainmentRecreational__AthleticFacilities", dropdown_name: "Athletic Facilities", search_value: "",
						children: [
							{ name: "Outdoor Pool", filter: "Q3", dropdown_name: "Outdoor Pool (Q3)", search_value: "building_class:(Q3)" },
							{ name: "Stadium, Race Track, Baseball Field", filter: "Q6", dropdown_name: "Stadium/Race Track/Baseball (Q6)", search_value: "building_class:(Q6)" },
							{ name: "Tennis Court", filter: "Q7", dropdown_name: "Tennis Court (Q7)", search_value: "building_class:(Q7)" },
							{ name: "Tennis Court, Pool, Shed, etc.", filter: "Z0", dropdown_name: "Tennis Court, Pool, Shed (Z0)", search_value: "building_class:(Z0)" },
						]
					},
					{
						name: "Lodges/Halls", filter: "EntertainmentRecreational__LodgesHalls", dropdown_name: "Lodges/Halls", search_value: "",
						children: [
							{ name: "Concert Hall", filter: "P1", dropdown_name: "Concert Hall (P1)", search_value: "building_class:(P1)" },
							{ name: "Lodge Room", filter: "P2", dropdown_name: "Lodge Room (P2)", search_value: "building_class:(P2)" },
							{ name: "Community Center", filter: "P5", dropdown_name: "Community Center (P5)", search_value: "building_class:(P5)" },
						]
					},
					{
						name: "Other Recreational", filter: "EntertainmentRecreational__OtherRecreational", dropdown_name: "Other Recreational", search_value: "",
						children: [
							{ name: "Beach Club", filter: "P4", dropdown_name: "Beach Club (P4)", search_value: "building_class:(P4)" },
							{ name: "Amusement Place, Bath House, Boat House", filter: "P6", dropdown_name: "Amusement/Bath/Boat House (P6)", search_value: "building_class:(P6)" },
							{ name: "Miscellaneous Indoor Public Assembly", filter: "P9", dropdown_name: "Indoor Public Assembly (P9)", search_value: "building_class:(P9)" },
							{ name: "Miscellaneous Outdoor Recreational Facility", filter: "Q9", dropdown_name: "Outdoor Recreational Facility (Q9)", search_value: "building_class:(Q9)" },
							{ name: "Beach", filter: "Q4", dropdown_name: "Beach (Q4)", search_value: "building_class:(Q4)" },
							{ name: "Community Gardens", filter: "QG", dropdown_name: "Community Gardens (QG)", search_value: "building_class:(QG)" },
						]
					},
				]
			},
			// === PUBLIC SERVICES/FACILITIES ===
			{
				name: "Public Services/Facilities", filter: "public-services", dropdown_name: "Public Services/Facilities", search_value: "",
				children: [
					{
						name: "Police/Fire Department", filter: "PublicServicesFacilities__PoliceFireDepartment", dropdown_name: "Police/Fire Department", search_value: "",
						children: [
							{ name: "Fire Department", filter: "Y1", dropdown_name: "Fire Department (Y1)", search_value: "building_class:(Y1)" },
							{ name: "Police Department", filter: "Y2", dropdown_name: "Police Department (Y2)", search_value: "building_class:(Y2)" },
						]
					},
					{
						name: "Post Office", filter: "PublicServicesFacilities__PostOffice", dropdown_name: "Post Office", search_value: "",
						children: [
							{ name: "Post Office", filter: "Z3", dropdown_name: "Post Office (Z3)", search_value: "building_class:(Z3)" },
						]
					},
					{
						name: "Parking", filter: "PublicServicesFacilities__Parking", dropdown_name: "Parking", search_value: "",
						children: [
							{ name: "Indoor Parking", filter: "RG", dropdown_name: "Indoor Parking (RG)", search_value: "building_class:(RG)" },
							{ name: "Outdoor Parking", filter: "RP", dropdown_name: "Outdoor Parking (RP)", search_value: "building_class:(RP)" },
							{ name: "Public Parking Area", filter: "Z2", dropdown_name: "Public Parking Area (Z2)", search_value: "building_class:(Z2)" },
							{ name: "All Parking Garages", filter: "G1", dropdown_name: "All Parking Garages (G1)", search_value: "building_class:(G1)" },
							{ name: "Garage - Residential Tax Class 1", filter: "G0", dropdown_name: "Garage - Residential Tax Class 1 (G0)", search_value: "building_class:(G0)" },
							{ name: "Licensed Parking Lot", filter: "G6", dropdown_name: "Licensed Parking Lot (G6)", search_value: "building_class:(G6)" },
							{ name: "Unlicensed Parking Lot", filter: "G7", dropdown_name: "Unlicensed Parking Lot (G7)", search_value: "building_class:(G7)" },
						]
					},
					{
						name: "Transportation", filter: "PublicServicesFacilities__Transportation", dropdown_name: "Transportation", search_value: "",
						children: [
							{ name: "Miscellaneous Transportation Facility", filter: "T9", dropdown_name: "Misc Transportation Facility (T9)", search_value: "building_class:(T9)" },
							{ name: "Railroad - Private Ownership", filter: "U6", dropdown_name: "Railroad - Private Ownership (U6)", search_value: "building_class:(U6)" },
							{ name: "Transportation - Public Ownership", filter: "U7", dropdown_name: "Transportation - Public Ownership (U7)", search_value: "building_class:(U7)" },
						]
					},
					{
						name: "Airports", filter: "PublicServicesFacilities__Airports", dropdown_name: "Airports", search_value: "",
						children: [
							{ name: "Airport, Airfield, Terminal", filter: "T1", dropdown_name: "Airport, Airfield, Terminal (T1)", search_value: "building_class:(T1)" },
						]
					},
					{
						name: "Utilities", filter: "PublicServicesFacilities__Utilities", dropdown_name: "Utilities", search_value: "",
						children: [
							{ name: "Utility Company Land and Building", filter: "U0", dropdown_name: "Utility Company Land & Bldg (U0)", search_value: "building_class:(U0)" },
							{ name: "Gas or Electric Utility", filter: "U2", dropdown_name: "Gas or Electric Utility (U2)", search_value: "building_class:(U2)" },
							{ name: "Telephone Utility", filter: "U4", dropdown_name: "Telephone Utility (U4)", search_value: "building_class:(U4)" },
							{ name: "Communication Facility Other than Telephone", filter: "U5", dropdown_name: "Communication Facility (U5)", search_value: "building_class:(U5)" },
							{ name: "Miscellaneous Utility Property", filter: "U9", dropdown_name: "Miscellaneous Utility (U9)", search_value: "building_class:(U9)" },
						]
					},
				]
			},
			// === INSTITUTIONAL ===
			{
				name: "Institutional", filter: "institutional", dropdown_name: "Institutional", search_value: "",
				children: [
					{
						name: "Education", filter: "Institutional__Education", dropdown_name: "Education", search_value: "",
						children: [
							{ name: "Public Elementary, Junior or Senior High", filter: "W1", dropdown_name: "Public Elementary/Junior/Senior High (W1)", search_value: "building_class:(W1)" },
							{ name: "Parochial School, Yeshiva", filter: "W2", dropdown_name: "Parochial School, Yeshiva (W2)", search_value: "building_class:(W2)" },
							{ name: "School or Academy", filter: "W3", dropdown_name: "School or Academy (W3)", search_value: "building_class:(W3)" },
							{ name: "Training School", filter: "W4", dropdown_name: "Training School (W4)", search_value: "building_class:(W4)" },
							{ name: "City University", filter: "W5", dropdown_name: "City University (W5)", search_value: "building_class:(W5)" },
							{ name: "Other College and University", filter: "W6", dropdown_name: "Other College and University (W6)", search_value: "building_class:(W6)" },
							{ name: "Theological Seminary", filter: "W7", dropdown_name: "Theological Seminary (W7)", search_value: "building_class:(W7)" },
							{ name: "Other Private School", filter: "W8", dropdown_name: "Other Private School (W8)", search_value: "building_class:(W8)" },
							{ name: "Miscellaneous Educational Facility", filter: "W9", dropdown_name: "Misc Educational Facility (W9)", search_value: "building_class:(W9)" },
						]
					},
					{
						name: "Religious Institutions", filter: "Institutional__ReligiousInstitutions", dropdown_name: "Religious Institutions", search_value: "",
						children: [
							{ name: "Church, Synagogue, Chapel", filter: "M1", dropdown_name: "Church, Synagogue, Chapel (M1)", search_value: "building_class:(M1)" },
							{ name: "Mission House (Non-Residential)", filter: "M2", dropdown_name: "Mission House (M2)", search_value: "building_class:(M2)" },
							{ name: "Parsonage, Rectory", filter: "M3", dropdown_name: "Parsonage, Rectory (M3)", search_value: "building_class:(M3)" },
							{ name: "Convent", filter: "M4", dropdown_name: "Convent (M4)", search_value: "building_class:(M4)" },
							{ name: "Miscellaneous Religious Facility", filter: "M9", dropdown_name: "Misc Religious Facility (M9)", search_value: "building_class:(M9)" },
							{ name: "YWCA, YMCA, YWHA, YMHA, PAL", filter: "P3", dropdown_name: "YWCA, YMCA, YWHA, YMHA, PAL (P3)", search_value: "building_class:(P3)" },
						]
					},
					{
						name: "Hospitals/Health Care", filter: "Institutional__HospitalsHealthCare", dropdown_name: "Hospitals/Health Care", search_value: "",
						children: [
							{ name: "Hospital, Sanitarium, Mental Institution", filter: "I1", dropdown_name: "Hospital, Sanitarium (I1)", search_value: "building_class:(I1)" },
							{ name: "Infirmary", filter: "I2", dropdown_name: "Infirmary (I2)", search_value: "building_class:(I2)" },
							{ name: "Dispensary", filter: "I3", dropdown_name: "Dispensary (I3)", search_value: "building_class:(I3)" },
							{ name: "Hospital - Staff Facility", filter: "I4", dropdown_name: "Hospital - Staff Facility (I4)", search_value: "building_class:(I4)" },
							{ name: "Health Center, Child Center, Clinic", filter: "I5", dropdown_name: "Health Center/Clinic (I5)", search_value: "building_class:(I5)" },
							{ name: "Miscellaneous Hospital, Health Care Facility", filter: "I9", dropdown_name: "Misc Hospital/Health Care (I9)", search_value: "building_class:(I9)" },
						]
					},
					{
						name: "Care Facilities", filter: "Institutional__CareFacilities", dropdown_name: "Care Facilities", search_value: "",
						children: [
							{ name: "Home for Indigent Children, Aged, Homeless", filter: "N2", dropdown_name: "Home for Indigent/Aged/Homeless (N2)", search_value: "building_class:(N2)" },
							{ name: "Orphanage", filter: "N3", dropdown_name: "Orphanage (N3)", search_value: "building_class:(N3)" },
							{ name: "Asylum", filter: "N1", dropdown_name: "Asylum (N1)", search_value: "building_class:(N1)" },
							{ name: "Miscellaneous Asylum, Home", filter: "N9", dropdown_name: "Miscellaneous Asylum, Home (N9)", search_value: "building_class:(N9)" },
							{ name: "Nursing Home", filter: "I6", dropdown_name: "Nursing Home (I6)", search_value: "building_class:(I6)" },
							{ name: "Adult Care Facility", filter: "I7", dropdown_name: "Adult Care Facility (I7)", search_value: "building_class:(I7)" },
						]
					},
					{
						name: "Cemeteries/Funeral Home", filter: "Institutional__CemeteriesFuneralHome", dropdown_name: "Cemeteries/Funeral Home", search_value: "",
						children: [
							{ name: "Cemetery", filter: "Z8", dropdown_name: "Cemetery (Z8)", search_value: "building_class:(Z8)" },
						]
					},
					{
						name: "Correctional Facility", filter: "Institutional__CorrectionalFacility", dropdown_name: "Correctional Facility", search_value: "",
						children: [
							{ name: "Detention House for Wayward Girls", filter: "N4", dropdown_name: "Detention House (N4)", search_value: "building_class:(N4)" },
							{ name: "Prison, Jail, House of Detention", filter: "Y3", dropdown_name: "Prison, Jail, House of Detention (Y3)", search_value: "building_class:(Y3)" },
						]
					},
					{
						name: "Military Facility", filter: "Institutional__MilitaryFacility", dropdown_name: "Military Facility", search_value: "",
						children: [
							{ name: "Military and Naval Installation", filter: "Y4", dropdown_name: "Military and Naval Installation (Y4)", search_value: "building_class:(Y4)" },
						]
					},
				]
			},
			// === OTHER ===
			{
				name: "Other", filter: "other", dropdown_name: "Other", search_value: "",
				children: [
					{
						name: "Commercial", filter: "Other__Commercial", dropdown_name: "Commercial", search_value: "",
						children: [
							{ name: "Miscellaneous Commercial", filter: "R5", dropdown_name: "Miscellaneous Commercial (R5)", search_value: "building_class:(R5)" },
						]
					},
					{
						name: "Miscellaneous", filter: "Other__Miscellaneous", dropdown_name: "Miscellaneous", search_value: "",
						children: [
							{ name: "Bridge, Tunnel, Highway", filter: "U1", dropdown_name: "Bridge, Tunnel, Highway (U1)", search_value: "building_class:(U1)" },
							{ name: "Other Miscellaneous", filter: "Z9", dropdown_name: "Other Miscellaneous (Z9)", search_value: "building_class:(Z9)" },
							{ name: "Pier, Dock, Bulkhead", filter: "T2", dropdown_name: "Pier, Dock, Bulkhead (T2)", search_value: "building_class:(T2)" },
						]
					},
					{
						name: "Government/Exempt", filter: "Other__GovernmentExempt", dropdown_name: "Government/Exempt", search_value: "",
						children: [
							{ name: "Department of Real Estate", filter: "Y5", dropdown_name: "Dept of Real Estate (Y5)", search_value: "building_class:(Y5)" },
							{ name: "Department of Sanitation", filter: "Y6", dropdown_name: "Dept of Sanitation (Y6)", search_value: "building_class:(Y6)" },
							{ name: "Department of Ports and Terminals", filter: "Y7", dropdown_name: "Dept of Ports & Terminals (Y7)", search_value: "building_class:(Y7)" },
							{ name: "Department of Public Works", filter: "Y8", dropdown_name: "Dept of Public Works (Y8)", search_value: "building_class:(Y8)" },
							{ name: "Department of Environmental Protection", filter: "Y9", dropdown_name: "Dept of Environmental Protection (Y9)", search_value: "building_class:(Y9)" },
							{ name: "Foreign Government", filter: "Z4", dropdown_name: "Foreign Government (Z4)", search_value: "building_class:(Z4)" },
							{ name: "United Nations", filter: "Z5", dropdown_name: "United Nations (Z5)", search_value: "building_class:(Z5)" },
							{ name: "Easement", filter: "Z7", dropdown_name: "Easement (Z7)", search_value: "building_class:(Z7)" },
						]
					},
				]
			},
		]
	},
	{
		name: "Stories",
		show_name: true,
		search_key: ["min_stories", "max_stories"],
		filter_key : "number_of_floors",
		price_filter: true,
		labels : ["Min Stories", "Max Stories"],
		min_max_values : [1, 50],
		min_max_increment : 5,
		custom_min_max_values : {
			"min" : [1, 5, 10, 15, 20, 30],
			"max" : [5, 10, 15, 20, 30, 50, "No Max"]
		},
		format_type : "number",
		search_value: ['number_of_floors:[{min_stories} TO {max_stories}]'],
		value_prefix : "",
		title_suffix : "",
		no_max: true,
	},
	{
		name: "Total Units",
		show_name: true,
		search_key: "total_units",
		values: [
			{ name: "Any", filter: "", dropdown_name: "Any", search_value: "" },
			{ name: "1-10 Units", filter: "1-to-10", dropdown_name: "1 to 10", search_value: "[1 TO 10]" },
			{ name: "11-50 Units", filter: "11-to-50", dropdown_name: "11 to 50", search_value: "[11 TO 50]" },
			{ name: "51-100 Units", filter: "51-to-100", dropdown_name: "51 to 100", search_value: "[51 TO 100]" },
			{ name: "101-200 Units", filter: "101-to-200", dropdown_name: "101 to 200", search_value: "[101 TO 200]" },
			{ name: "201+ Units", filter: "201-or-more", dropdown_name: "201 or More", search_value: "[201 TO *]" },
		]
	},
	{
		name: "Year Built",
		show_name: true,
		search_key: ["min_year_built", "max_year_built"],
		filter_key : "year_built",
		price_filter: true,
		labels : ["Min Year Built", "Max Year Built"],
		min_max_values : [1900, current_year],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [1900, 1920, 1940, 1960, 1980, 2000, 2020],
			"max" : [1920, 1940, 1960, 1980, 2000, 2020, current_year]
		},
		format_type : "year",
		search_value: ['year_built:[{min_year_built} TO {max_year_built}]'],
		value_prefix : "",
		title_suffix : ""
	},
	{
		name: "Lot Size (SF)",
		show_name: true,
		search_key: ["min_lot_area", "max_lot_area"],
		filter_key : "lot_area",
		price_filter: true,
		labels : ["Min Lot SF", "Max Lot SF"],
		min_max_values : [1000, 10000],
		min_max_increment : 1000,
		custom_min_max_values : {
			"min" : [500, 1000, 2000, 3000, 5000, 7500],
			"max" : [1000, 2000, 3000, 5000, 7500, 10000, "No Max"]
		},
		format_type : "number",
		search_value: ['lot_area:[{min_lot_area} TO {max_lot_area}]'],
		value_prefix : "",
		title_suffix : "SF",
		no_max: true,
	},
	{
		name: "Building Size (SF)",
		show_name: true,
		search_key: ["min_bldg_sq_ft", "max_bldg_sq_ft"],
		filter_key : "bldg_sq_ft",
		price_filter: true,
		labels : ["Min Bldg SF", "Max Bldg SF"],
		min_max_values : [1000, 10000],
		min_max_increment : 1000,
		custom_min_max_values : {
			"min" : [500, 1000, 2000, 3000, 5000, 10000],
			"max" : [1000, 2000, 3000, 5000, 10000, 50000, "No Max"]
		},
		format_type : "number",
		search_value: ['sq_ft:[{min_bldg_sq_ft} TO {max_bldg_sq_ft}]'],
		value_prefix : "",
		title_suffix : "SF",
		no_max: true,
	},
	{
		name: "Building Width",
		show_name: true,
		search_key: ["min_building_width", "max_building_width"],
		filter_key : "building_frontage",
		price_filter: true,
		labels : ["Min Width", "Max Width"],
		min_max_values : [10, 100],
		min_max_increment : 5,
		custom_min_max_values : {
			"min" : [10, 15, 20, 25, 30, 50],
			"max" : [20, 25, 30, 50, 75, 100, "No Max"]
		},
		format_type : "number",
		search_value: ['building_frontage:[{min_building_width} TO {max_building_width}]'],
		value_prefix : "",
		title_suffix : "FT",
		no_max: true,
	},
	{
		name: "Building Depth",
		show_name: true,
		search_key: ["min_building_depth", "max_building_depth"],
		filter_key : "building_depth",
		price_filter: true,
		labels : ["Min Depth", "Max Depth"],
		min_max_values : [10, 200],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [10, 25, 50, 75, 100, 150],
			"max" : [25, 50, 75, 100, 150, 200, "No Max"]
		},
		format_type : "number",
		search_value: ['building_depth:[{min_building_depth} TO {max_building_depth}]'],
		value_prefix : "",
		title_suffix : "FT",
		no_max: true,
	},
	{
		name: "Residential SF",
		show_name: true,
		search_key: ["min_res_sq_ft", "max_res_sq_ft"],
		filter_key : "residential_floor_area",
		price_filter: true,
		labels : ["Min Res SF", "Max Res SF"],
		min_max_values : [1000, 50000],
		min_max_increment : 1000,
		custom_min_max_values : {
			"min" : [500, 1000, 2000, 5000, 10000, 25000],
			"max" : [1000, 2000, 5000, 10000, 25000, 50000, "No Max"]
		},
		format_type : "number",
		search_value: ['residential_floor_area:[{min_res_sq_ft} TO {max_res_sq_ft}]'],
		value_prefix : "",
		title_suffix : "SF",
		no_max: true,
	},
	{
		name: "Retail SF",
		show_name: true,
		search_key: ["min_retail_sq_ft", "max_retail_sq_ft"],
		filter_key : "retail_floor_area",
		price_filter: true,
		labels : ["Min Retail SF", "Max Retail SF"],
		min_max_values : [500, 25000],
		min_max_increment : 500,
		custom_min_max_values : {
			"min" : [500, 1000, 2000, 5000, 10000, 15000],
			"max" : [1000, 2000, 5000, 10000, 15000, 25000, "No Max"]
		},
		format_type : "number",
		search_value: ['retail_floor_area:[{min_retail_sq_ft} TO {max_retail_sq_ft}]'],
		value_prefix : "",
		title_suffix : "SF",
		no_max: true,
	},
	{
		name: "Lot Depth",
		show_name: true,
		search_key: ["min_lot_depth", "max_lot_depth"],
		filter_key : "lot_depth",
		price_filter: true,
		labels : ["Min Depth", "Max Depth"],
		min_max_values : [10, 200],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [25, 50, 75, 100, 125, 150],
			"max" : [50, 75, 100, 125, 150, 200, "No Max"]
		},
		format_type : "number",
		search_value: ['lot_depth:[{min_lot_depth} TO {max_lot_depth}]'],
		value_prefix : "",
		title_suffix : "FT",
		no_max: true,
	},
	{
		name: "Lot Width",
		show_name: true,
		search_key: ["min_lot_width", "max_lot_width"],
		filter_key : "lot_frontage",
		price_filter: true,
		labels : ["Min Width", "Max Width"],
		min_max_values : [10, 100],
		min_max_increment : 5,
		custom_min_max_values : {
			"min" : [10, 15, 20, 25, 30, 50],
			"max" : [20, 25, 30, 50, 75, 100, "No Max"]
		},
		format_type : "number",
		search_value: ['lot_frontage:[{min_lot_width} TO {max_lot_width}]'],
		value_prefix : "",
		title_suffix : "FT",
		no_max: true,
	},
	{
		name: "Buildable Area",
		show_name: true,
		search_key: ["min_buildable_area", "max_buildable_area"],
		filter_key : "buildable_area",
		price_filter: true,
		labels : ["Min Area", "Max Area"],
		min_max_values : [1000, 250000],
		min_max_increment : 5000,
		custom_min_max_values : {
			"min" : [2500, 5000, 10000, 25000, 50000, 100000],
			"max" : [5000, 10000, 25000, 50000, 100000, 250000, "No Max"]
		},
		format_type : "number",
		search_value: ['{!frange l={min_buildable_area} u={max_buildable_area}}product(lot_area,max_residential_far)'],
		value_prefix : "",
		title_suffix : "SF",
		no_max: true,
		target: "fq",
	},
];

export const _multiFamilyFilters = [
	{
		name: "Total Units",
		show_name: true,
		search_key: "total_units",
		values: [
			{ name: "Any", filter: "", dropdown_name: "Any", search_value: "" },
			{ name: "2-4 Units", filter: "2-to-4", dropdown_name: "2 to 4", search_value: "[2 TO 4]" },
			{ name: "5-10 Units", filter: "5-to-10", dropdown_name: "5 to 10", search_value: "[5 TO 10]" },
			{ name: "11-20 Units", filter: "11-to-20", dropdown_name: "11 to 20", search_value: "[11 TO 20]" },
			{ name: "21-50 Units", filter: "21-to-50", dropdown_name: "21 to 50", search_value: "[21 TO 50]" },
			{ name: "51+ Units", filter: "51-or-more", dropdown_name: "51 or More", search_value: "[51 TO *]" },
		]
	},
	{
		name: "Total Floors",
		show_name: true,
		search_key: "total_floors",
		values: [
			{ name: "Any", filter: "", dropdown_name: "Any", search_value: "" },
			{ name: "1-5 Floors", filter: "1-to-5", dropdown_name: "1 to 5 Floors", search_value: "[1 TO 5]" },
			{ name: "6-10 Floors", filter: "6-to-10", dropdown_name: "6 to 10 Floors", search_value: "[6 TO 10]" },
			{ name: "11-20 Floors", filter: "11-to-20", dropdown_name: "11 to 20 Floors", search_value: "[11 TO 20]" },
			{ name: "21+ Floors", filter: "21-or-more", dropdown_name: "21 or More Floors", search_value: "[21 TO *]" },
		]
	},
	{
		name: "Year Built",
		show_name: true,
		search_key: ["min_year_built", "max_year_built"],
		filter_key : "year_built",
		price_filter: true,
		labels : ["Min Year Built", "Max Year Built"],
		min_max_values : [1900, current_year],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [1900, 1920, 1940, 1960, 1980, 2000, 2020],
			"max" : [1920, 1940, 1960, 1980, 2000, 2020, current_year]
		},
		format_type : "year",
		search_value: ['year_built:[{min_year_built} TO {max_year_built}]'],
		value_prefix : "",
		title_suffix : ""
	},
	{
		name: "Lot Size (SF)",
		show_name: true,
		search_key: ["min_lot_area", "max_lot_area"],
		filter_key : "lot_area",
		price_filter: true,
		labels : ["Min Lot SF", "Max Lot SF"],
		min_max_values : [1000, 10000],
		min_max_increment : 1000,
		custom_min_max_values : {
			"min" : [500, 1000, 2000, 3000, 5000, 7500],
			"max" : [1000, 2000, 3000, 5000, 7500, 10000, "No Max"]
		},
		format_type : "number",
		search_value: ['lot_area:[{min_lot_area} TO {max_lot_area}]'],
		value_prefix : "",
		title_suffix : "SF",
		no_max: true,
	},
	{
		name: "Building Size (SF)",
		show_name: true,
		search_key: ["min_bldg_sq_ft", "max_bldg_sq_ft"],
		filter_key : "bldg_sq_ft",
		price_filter: true,
		labels : ["Min Bldg SF", "Max Bldg SF"],
		min_max_values : [1000, 10000],
		min_max_increment : 1000,
		custom_min_max_values : {
			"min" : [500, 1000, 2000, 3000, 5000, 10000],
			"max" : [1000, 2000, 3000, 5000, 10000, 50000, "No Max"]
		},
		format_type : "number",
		search_value: ['sq_ft:[{min_bldg_sq_ft} TO {max_bldg_sq_ft}]'],
		value_prefix : "",
		title_suffix : "SF",
		no_max: true,
	},
];


export const _buildingAdminFiltersRental = [
	{
		name: "Borough",
		show_name: true,
		default_name: 'All Boroughs',
		search_key: "borough",
		values: [
			{
				name: "All Boroughs",
				filter: "",
				dropdown_name: "All Boroughs",
				search_value: ""
			},
			{
				name: "Manhattan",
				filter: "manhattan",
				dropdown_name: "Manhattan",
				search_value: "manhattan",
			},
			{
				name: "Brooklyn",
				filter: "brooklyn",
				dropdown_name: "Brooklyn",
				search_value: "brooklyn",
			},
			{
				name: "Queens",
				filter: "queens",
				dropdown_name: "Queens",
				search_value: "queens",
			},
			{
				name: "Bronx",
				filter: "bronx",
				dropdown_name: "Bronx",
				search_value: "bronx",
			},
			{
				name: "Staten Island",
				filter: "staten-island",
				dropdown_name: "Staten Island",
				search_value: "staten-island",
			},
		]
	},
	{
		name: "Type",
		show_name: true,
		search_key: "ownership_type",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Hybrid - Condo",
				filter: "hybrid",
				dropdown_name: "Hybrid - Condo",
				search_value: "* AND ((ownership_type:*condo* AND ownership_occupancy_sub_types:*rental*) OR (ownership_occupancy_sub_types:*condo* AND ownership_type:*rental*))",
			},
			{
				name: "OP Filed",
				filter: "op-filed",
				dropdown_name: "Offering Plan Filed",
				search_value: "* AND (offering_submitted_date:[NOW-15YEARS TO NOW])",
			}
			
		]
	},
	{
		name: "Total Floors",
		show_name: true,
		search_key: "total_floors",
		values: [
			{
				name: "Any Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-5 Floors",
				filter: "1-to-10",
				dropdown_name: "1 to 5 Floors",
				search_value: "[1 TO 5]"
			},
			{
				name: "6-10 Floors",
				filter: "6-to-10",
				dropdown_name: "6 to 10 Floors",
				search_value: "[6 TO 10]"
			},
			{
				name: "11-20 Floors",
				filter: "11-to-20",
				dropdown_name: "11 to 20 Floors",
				search_value: "[11 TO 20]"
			},
			{
				name: "21-30 Floors",
				filter: "21-to-30",
				dropdown_name: "21 to 30 Floors",
				search_value: "[21 TO 30]"
			},
			{
				name: "31-40 Floors",
				filter: "31-to-40",
				dropdown_name: "31 to 40 Floors",
				search_value: "[31 TO 40]"
			},
			{
				name: "40+ Floors",
				filter: "40-or-more",
				dropdown_name: "41 or More Floors",
				search_value: "[41 TO *]"
			}
		]
	},
/*
	{
		name: "Construction",
		show_name: true,
		search_key: "building_effective_start",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Move-in Ready",
				filter: "completed",
				dropdown_name: "Move-in Ready",
				search_value: "[NOW-15YEARS TO NOW]",
			},
			{
				name: "Under Construction",
				filter: "not-completed",
				dropdown_name: "Under Construction",
				search_value: "[* TO NOW-15YEARS]",
			}
			
		]
	},
*/
	{
		name: "Condo Units",
		show_name: true,
		search_key: "inventory_total",
		values: [
			{
				name: "Any Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "1-10 Condo Units",
				filter: "1-to-10",
				dropdown_name: "1 to 10 Units",
				search_value: "[1 TO 10]"
			},
			{
				name: "11-30 Condo Units",
				filter: "11-to-30",
				dropdown_name: "11 to 30 Units",
				search_value: "[11 TO 30]"
			},
			{
				name: "31-50 Condo Units",
				filter: "31-to-50",
				dropdown_name: "31 to 50 Units",
				search_value: "[31 TO 50]"
			},
			{
				name: "51-100 Condo Units",
				filter: "51-to-100",
				dropdown_name: "51 to 100 Units",
				search_value: "[51 TO 100]"
			},
			{
				name: "101-200 Condo Units",
				filter: "101-to-200",
				dropdown_name: "101 to 200 Units",
				search_value: "[101 TO 200]"
			},
			{
				name: "201 + Condo Units",
				filter: "201-or-more",
				dropdown_name: "201 or more Units",
				search_value: "[201 TO *]"
			}
		]
	}
];

export const _buildingStageFilter = [{
		search_key: "building_stage",
		type: "array",
		values: [
			{
				filter: "pipeline"
			},
			{
				filter: "sponsor"
			},
			{
				filter: "resale"
			},
			{
				filter: "all"
			}
			
		]
}]

export const _unitStageFilter = [{
		search_key: "unit_stage",
		type: "array",
		values: [
			{
				filter: "sponsor"
			},
			{
				filter: "resale"
			}			
		]
}]

export const _salesFilters = [	
	{
		name: "Ownership",
		show_name: true,
		search_key: "ownership_type",
		multi_filter:true,
		hide_building:true,
		no_key:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "ownership_type:(*condo* !condo*p)",
			},
			{
				name: "Co-op",
				filter: "coop",
				dropdown_name: "Co-op",
				search_value: "ownership_type:(*co?op* *coop* !condo*p)",
			},			
			{
				name: "Condop",
				filter: "condop",
				dropdown_name: "Condop",
				search_value: "ownership_type:(condop*)",
			},
			{
				name: "Townhouse",
				filter: "townhouse",
				dropdown_name: "Townhouse",
				search_value: "property_type:(f1 f4 f5 fs d1 d2 d3 d4 d5 d6 r1 rp rg rv gr)",
			},	
			{
				name: "Single-Family",
				filter: "single-family",
				dropdown_name: "Single-Family",
				search_value: "ownership_type:single?family",
			},			
			{
				name: "Multi-Family",
				filter: "multi-family",
				dropdown_name: "Multi-Family",
				search_value: "(ownership_type:multi?family OR (ownership_type:rental AND total_units:[* TO 3]))",
			}
			
		]
	},
	
	{
		name: "Sold Date",
		show_name: true,
		search_key: ["min_date", "max_date"],
		filter_key : "sold_date",
		date_filter: true,
		search_value: ['close_date_formatted:[{min_date} TO {max_date}]']		
	},
	{
		name: "Contract Date",
		show_name: true,
		search_key: ["min_contract_date", "max_contract_date"],
		filter_key : "contract_date",
		date_filter: true,
		search_value: ['contract_date_formatted:[{min_contract_date} TO {max_contract_date}]']		
	},
	{
		name: "Price",
		show_name: true,
		search_key: ["min_price", "max_price"],
		price_filter: true,
		search_value: ['price:[{min_price} TO {max_price}]']		
	},	
	{
		name: "Bedrooms",
		show_name: true,
		search_key: "bedrooms",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Studio",
				filter: "studio",
				dropdown_name: "Studio",
				search_value: "0",
			},
			{
				name: "1 Bedroom",
				filter: "1-bedroom",
				dropdown_name: "1 Bedroom",
				search_value: "1",
			},
			{
				name: "2 Bedroom",
				filter: "2-bedroom",
				dropdown_name: "2 Bedroom",
				search_value: "2",
			},
			{
				name: "3 Bedroom",
				filter: "3-bedroom",
				dropdown_name: "3 Bedroom",
				search_value: "3",
			},
			{
				name: "4+ Bedroom",
				filter: "4-or-more",
				dropdown_name: "4+ Bedroom",
				search_value: "[4 TO *]",
			},
		]
	},
	
	{
		name: "Baths",
		show_name: true,
		search_key: "baths",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},			
			{
				name: "1 Bath",
				filter: "1-bath",
				dropdown_name: "1 Bath",
				search_value: "1",
			},
			{
				name: "2 Bath",
				filter: "2-bath",
				dropdown_name: "2 Bath",
				search_value: "2",
			},
			{
				name: "3 Bath",
				filter: "3-bath",
				dropdown_name: "3 Bath",
				search_value: "3",
			},
			{
				name: "4+ Bath",
				filter: "4-or-more",
				dropdown_name: "4+ Bath",
				search_value: "[4 TO *]",
			},
		]
	},
	{
		name: "Amenities",
		show_name: true,
		search_key: "building_features",
		autocomplete: true,
		core_name: "we3-transactions",
		fq: ["status:sold"],
		group_amenities: true,
		hide_building:true,
	},
	{
		name: "Interior SF",
		show_name: true,
		search_key: ["min_sq_ft", "max_sq_ft"],
		filter_key : "sq_ft",
		price_filter: true,
		labels : ["Min Sq Ft", "Max Sq Ft"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['sq_ft:[{min_sq_ft} TO {max_sq_ft}]'], 
		value_prefix : "",
		title_suffix : "SF",

		values: [
			{
				name: "Any Units",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< 500 ",
				filter: "less-than-500",
				dropdown_name: "Less than 500 SF",
				search_value: "[10 TO 500] OR sq_ft:[10 TO 500] "
			},
			{
				name: "501 to 750 SF",
				filter: "501-to-750",
				dropdown_name: "501 to 750 SF",
				search_value: "[501 TO 750] OR sq_ft:[501 TO 750] "
			},
			{
				name: "751 to 1,000 SF",
				filter: "751-to-1000",
				dropdown_name: "751 to 1000 SF",
				search_value: "[751 TO 1000] OR sq_ft:[751 TO 1000]"
			},
			{
				name: "1,001 to 1,500 SF",
				filter: "1001-to-1500",
				dropdown_name: "1,001 to 1,500 SF",
				search_value: "[1001 TO 1500] OR sq_ft:[1001 TO 1500]"
			},
			{
				name: "1,501 to 2,000 SF",
				filter: "1501-to-2000",
				dropdown_name: "1,501 to 2,000 SF",
				search_value: "[1501 TO 2000] OR sq_ft:[1501 TO 2000]"
			},
			{
				name: "2,001 to 3,000 SF",
				filter: "2001-to-3000",
				dropdown_name: "2,001 to 3,000 SF",
				search_value: "[2001 TO 3000] OR sq_ft:[2001 TO 3000]"
			},
			{
				name: "3,000+ SF",
				filter: "3001-or-more",
				dropdown_name: "More than 3000 SF",
				search_value: "[3001 TO *] OR sq_ft:[3001 TO *]"
			}
		]
	},
	{
		name: "Sold PPSF",
		show_name: true,
		//search_key: "price_per_sqft",
		search_key: ["min_ppsf_price", "max_ppsf_price"],
		filter_key : "price_per_sqft",
		price_filter: true,
		labels : ["Min PPSF", "Max PPSF"],
		min_max_values : [500, 4500],
		min_max_increment : 500,
		format_type : "number",
		search_value: ['price_per_sqft:[{min_ppsf_price} TO {max_ppsf_price}]'],
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< $750 PPSF ",
				filter: "less-than-750",
				dropdown_name: "Less than $750",
				search_value: "[100 TO 749]"
			},
			{
				name: "$750 to $1,000 PPSF",
				filter: "75-to-1000",
				dropdown_name: "$750 to $1,000",
				search_value: "[750 TO 1000]"
			},
			{
				name: "$1,000 to $1,500 PPSF",
				filter: "1000-to-1500",
				dropdown_name: "$1,000 to $1,500",
				search_value: "[1000 TO 1500]"
			},
			{
				name: "$1,500 to $2,000 PPSF",
				filter: "1500-to-2000",
				dropdown_name: "$1,500 to $2,000",
				search_value: "[1500 TO 2000]"
			},
			{
				name: "$2,000 to $2,500 PPSF",
				filter: "2000-to-2500",
				dropdown_name: "$2,000 to $2,500",
			    search_value: "[2000 TO 2500]"
			},
			{
				name: "$2,500 to $5,000 PPSF",
				filter: "2500-to-5000",
				dropdown_name: "$2,500 to $5,000",
				search_value: "[2500 TO 5000]"
			},
			{
				name: "$5,000+ PPSF",
				filter: "5000-or-more",
				dropdown_name: "$5,000 or more",
				search_value: "[5000 TO *]"
			}
		]
	},
	{
		name: "Floors",
		show_name: true,
		search_key: ["min_floor", "max_floor"],
		filter_key : "floor",
		price_filter: true,
		// target: 'fq',
		labels : ["Min Floor", "Max Floor"],

		custom_min_max_values : {
			"min" : [1, 11, 21, 31, 41],
			"max" : [10, 20, 30, 40, 50,"No Max"]
		},	
		format_type : "number",
		search_value: ['floor:[{min_floor} TO {max_floor}]']		,
		format_type : "number",
		value_prefix : "",	
		no_max:true,
		
	},
	{
		name: "Sale Type",
		show_name: true,
		search_key: "parcels_count",
		multi_filter:true,
		no_key:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Market Sale",
				filter: "market-sale",
				dropdown_name: "Market Sale",
				search_value: "(parcels_count:1 AND (price:[5001 TO *] OR price:[* TO -1]))"
			},
			
			{
				name: "Non-Market Transfer",
				filter: "non-market",
				dropdown_name: "Non-Market Transfer",
				search_value: "(price:[* TO 5000])"
			},
			{
				name: "Bulk Sale (Any)",
				filter: "bulk-sale",
				dropdown_name: "Bulk Sale (Any)",
				search_value: "(parcels_count:[2 TO *] AND parcels_condocoop_count:[* TO 0]) OR (parcels_condocoop_count:[2 TO *])"
			},
			{
				name: "Bulk Sale (2 to 5 Units)",
				filter: "bulk-sale-2-to-5",
				dropdown_name: "Bulk Sale  (2 to 5 Units)",
				search_value: "(parcels_count:[2 TO 5] AND parcels_condocoop_count:[* TO 0]) OR (parcels_condocoop_count:[2 TO 5])"
			},
			{
				name: "Bulk Sale (6 to 10 Units)",
				filter: "bulk-sale-6-to-5",
				dropdown_name: "Bulk Sale  (6 to 10 Units)",
				search_value: "(parcels_count:[6 TO 10] AND parcels_condocoop_count:[* TO 0]) OR (parcels_condocoop_count:[6 TO 10])"
			},
			{
				name: "Bulk Sale (11 to 20 Units)",
				filter: "bulk-sale-11-to-20",
				dropdown_name: "Bulk Sale  (11 to 20 Units)",
				search_value: "(parcels_count:[11 TO 20] AND parcels_condocoop_count:[* TO 0]) OR (parcels_condocoop_count:[11 TO 20])"
			},
			{
				name: "Bulk Sale (20+ Units)",
				filter: "bulk-sale-more-20",
				dropdown_name: "Bulk Sale (20+ Units)",
				search_value: "(parcels_count:[21 TO *] AND parcels_condocoop_count:[* TO 0]) OR (parcels_condocoop_count:[21 TO *])"
			}
		]
	},
	{
		name: "Ownership",
		show_name: true,
		search_key: "ownership_type",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "condo AND -ownership_type:cooperative",
			},
			{
				name: "Co-op",
				filter: "co-op",
				dropdown_name: "Co-op",
				search_value: "coop",
			}
			
		]
	},
	{
		name: "Discount",
		show_name: true,
		search_key: "discount",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Has Discount",
				filter: "has-discount",
				dropdown_name: "Has Discount",
				search_value: "[-90 TO -.01]"
			},
			{
				name: "1% to 3%",
				filter: "1-to-3",
				dropdown_name: "1% to 3%",
				search_value: "[-3 TO -1]"
			},
			{
				name: "3% to 5%",
				filter: "3-to-5",
				dropdown_name: "3% to 5%",
				search_value: "[-5 TO -3]"
			},
			{
				name: "5% to 10%",
				filter: "5-to-10",
				dropdown_name: "5% to 10%",
				search_value: "[-10 TO -5]"
			},
			{
				name: "10% to 15%",
				filter: "10-to-15",
				dropdown_name: "10% to 15%",
				search_value: "[-15 TO -10]"
			},
			{
				name: "More Than 15%",
				filter: "more-15",
				dropdown_name: "More Than 15%",
				search_value: "[-90 TO -15]"
			},
			{
				name: "Sold for More Than Asking",
				filter: "more-than",
				dropdown_name: "Sold for More Than Asking",
				search_value: "[.01 TO 90]"
			},
		]
	},
	{
		name: "% Financed",
		show_name: true,
		search_key: "percent_financed",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Has Financing",
				filter: "has-financing",
				dropdown_name: "Has Financing",
				search_value: "[.01 TO 100]"
			},
			{
				name: "No Financing",
				filter: "no-financing",
				dropdown_name: "No Financing",
				search_value: "[* TO 0]"
			},
			{
				name: "90% to 100%",
				filter: "90-to-100",
				dropdown_name: "90% to 100%",
				search_value: "[90 TO 100]"
			},
			{
				name: "80% to 90%",
				filter: "80-to-90",
				dropdown_name: "80% to 90%",
				search_value: "[80 TO 90]"
			},
			{
				name: "70% to 80%",
				filter: "70-to-80",
				dropdown_name: "70% to 80%",
				search_value: "[70 TO 80]"
			},
			{
				name: "50% to 70%",
				filter: "50-to-70",
				dropdown_name: "50% to 70%",
				search_value: "[50 TO 70]"
			},
			{
				name: "30% to 50%",
				filter: "30-to-50",
				dropdown_name: "30% to 50%",
				search_value: "[30 TO 50]"
			},
			{
				name: "Less than 30%",
				filter: "1-to-30",
				dropdown_name: "Less than 30%",
				search_value: "[1 TO 30]"
			}
		]
	},
	{
		name: "Owner Type",
		show_name: true,
		search_key: "type",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Original Owner",
				filter: "original",
				dropdown_name: "Original Owner",
				search_value: "* AND owner:* AND sponsored_sale:false"
			},
			{
				name: "Corporate Owner",
				filter: "corporate",
				dropdown_name: "Corporate Owner",
				search_value: "* AND owner:(*llc *inc *corp *trust *l.p. *authority *associates *company *l.l.c)"
			}
		]
	},
	{
		name: "Year Built",
		show_name: true,
		exclude_stage: ["pipeline"],
		format_type : "number",
		search_key: ["min_year_built", "max_year_built"],
		filter_key : "year_built",
		price_filter: true,
		labels : ["Min Year Built", "Max Year Built"],
		min_max_values : [1900, current_year],
		min_max_increment : 10,
		custom_min_max_values : {
			"min" : [1900, 1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020],
			"max" : [1910, 1920, 1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020, current_year]
		},		
		format_type : "year",
		search_value: ['year_built:[{min_year_built} TO {max_year_built}]'], 
		value_prefix : "",	
		title_suffix : ""
	},
	{
		name: "Sale Includes",
		show_name: true,
		search_key: "parcels_property_types",
		multi_filter:true,
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "Includes Parking",
				filter: "parking",
				dropdown_name: "Includes Parking",
				search_value: "ps"
			},			
			{
				name: "Includes Storage",
				filter: "storage",
				dropdown_name: "Includes Storage",
				search_value: "sr"
			},
			{
				name: "Includes Parking & Storage",
				filter: "parking-storage",
				dropdown_name: "Includes Parking & Storage",
				search_value: "ps AND parcels_property_types:sr"
			},
			{
				name: "Excludes Parking",
				filter: "exclude-parking",
				dropdown_name: "Excludes Parking",
				search_value: "* AND -parcels_property_types:ps"
			},			
			{
				name: "Excludes Storage",
				filter: "exclude-storage",
				dropdown_name: "Excludes Storage",
				search_value: "* AND -parcels_property_types:sr"
			},
			{
				name: "Excludes Parking & Storage",
				filter: "exclude-parking-storage",
				dropdown_name: "Excludes Parking & Storage",
				search_value: "* AND -parcels_property_types:ps AND -parcels_property_types:sr"
			},
		]
	},
	{
		name: "Buyer Name",
		show_name: true,
		search_key: "owner",
		text_filter: true
	},
	{
		name: "Seller Name",
		show_name: true,
		search_key: "seller",
		text_filter: true
	},
	{
		name: "Lender",
		show_name: true,
		search_key: "lender",
		search_fields: ["lender", "verified_lender_name", "verified_lender_nmls_id"],
		text_filter: true
	},
	{
		name: "Loan Officer",
		show_name: true,
		search_key: "loan_officer",
		search_fields: ["verified_loan_officer_first_name", "verified_loan_officer_middle_name", "verified_loan_officer_last_name", "verified_loan_officer_nmls_id"],
		text_filter: true
	},
	{
		name: "Title Company",
		show_name: true,
		search_key: "title_company",
		search_fields: ["verified_title_company_name"],
		text_filter: true
	},
	{
		name: "Buyer's Law Firm",
		show_name: true,
		search_key: "buyer_law_firm",
		search_fields: ["verified_buyers_attorney_firm_name"],
		text_filter: true
	},
	{
		name: "Buyer's Attorney",
		show_name: true,
		search_key: "buyer_attorney",
		search_fields: ["verified_buyers_attorney_first_name", "verified_buyers_attorney_middle_name", "verified_buyers_attorney_last_name"],
		text_filter: true
	},
	{
		name: "Listing Broker",
		show_name: true,
		search_key: "broker",
		text_filter: true
	},
	{
		name: "Listing Agent",
		show_name: true,
		search_key: "agents",
		text_filter: true
	},
	// {
	// 	name: "Verified Sale",
	// 	show_name: true,
	// 	search_key: "verified_close_price",
	// 	multi_filter:true,
	// 	no_key:true,
	// 	values: [
	// 		{
	// 			name: "Any",
	// 			filter: "",
	// 			dropdown_name: "Any",
	// 			search_value: ""
	// 		},
	// 		{
	// 			name: "Verified",
	// 			filter: "verified",
	// 			dropdown_name: "Verified",
	// 			search_value: "(verified_close_price:[0 TO *])"
	// 		},
	// 		
	// 		{
	// 			name: "Not Verified",
	// 			filter: "not-verified",
	// 			dropdown_name: "Not Verified",
	// 			search_value: "verified_close_price:[0 TO *]"
	// 		},
	// 	]
	// },
	
]

export const _reportFilters = [
{
	name: "Share Type",
	show_name: true,
	search_key: "share_type",
	values: [
		{
			name: "Any",
			filter: "",
			dropdown_name: "Any",
			search_value: ""
		},
		{
			name: "Buildings",
			filter: "buildings",
			dropdown_name: "Buildings",
			search_value: {"$and" : [ {"building.key" : { "$exists" : true}}, {"property.key" : { "$exists" : false}} ]}
		},
		{
			name: "Units",
			filter: "units",
			dropdown_name: "Units",
			search_value: {"$and" : [ {"property.key" : { "$exists" : true}} ]}
		},
		{
			name: "Collections",
			filter: "collections",
			dropdown_name: "Collections",
			search_value: {"report_type" : {"$in" : ["comp", "grid"]}},
		}		
	]
}]

export const _permitFilters = [
	{
	  name: "Filing Date",
	  show_name: true,
	  search_key: ["filed_min_date", "filed_max_date"],
	  filter_key: 'filing_date',
	  date_filter: true,
	  search_value: ["filing_date:[{filed_min_date} TO {filed_min_date}]"],
	},
	{
	  name: "Issuance Date",
	  show_name: true,
	  search_key: ["min_date", "max_date"],
	  filter_key: 'issuance_date',
	  date_filter: true,
	  search_value: ["issuance_date:[{min_date} TO {max_date}]"],
	},
	{
	  name: "Job Type",
	  show_name: true,
	  search_key: "job_type",
	  multi_filter: true,
	  values: [
		{
		  name: "Any",
		  filter: "",
		  dropdown_name: "Any",
		  search_value: "",
		},
		{
		  name: "New Building",
		  filter: "nb",
		  dropdown_name: "New Building",
		  search_value: "(nb 5 6)",
		},
		{
		  name: "Demolition",
		  filter: "dm",
		  dropdown_name: "Demolition",
		  search_value: "(dm 7)",
		},
		{
		  name: "Major Alteration",
		  filter: "a1",
		  dropdown_name: "Major Alteration",
		  search_value: "(a1 4)",
		},
		{
		  name: "Minor Alteration",
		  filter: "a3",
		  dropdown_name: "Minor Alteration",
		  search_value: "(a3 alt 1)",
		}
	  ],
	},
	{
	  name: "Work Type",
	  show_name: true,
	  search_key: "work_type",
	  multi_filter: true,
	  values: [
		{
		  name: "Any",
		  filter: "",
		  dropdown_name: "Any",
		  search_value: "",
		},
		{
		  name: "General Construction",
		  filter: "gc",
		  dropdown_name: "General Construction",
		  search_value: "(gc *general*const*)",
		},
		{
		  name: "Plumbing",
		  filter: "pl",
		  dropdown_name: "Plumbing",
		  search_value: "(pl *plumbing*)",
		},
		{
		  name: "Construction Equipment",
		  filter: "eq",
		  dropdown_name: "Construction Equipment",
		  search_value: "(eq */eq* *eq/*)",
		},
		{
		  name: "Foundation/Earthwork",
		  filter: "fo",
		  dropdown_name: "Foundation/Earthwork",
		  search_value: "(fo */fo* *fo/* foundation ea)",
		},
		{
		  name: "Sidewalk Shed",
		  filter: "sh",
		  dropdown_name: "Sidewalk Shed",
		  search_value: "(sh */sh* *sh/* sidewalk*shed)",
		},
		{
		  name: "Scaffolding",
		  filter: "sf",
		  dropdown_name: "Scaffolding",
		  search_value: "(sf */sf* *sf/* supported*scaffold sc */sc *sc/)",
		},
		{
		  name: "Boiler",
		  filter: "bl",
		  dropdown_name: "Boiler",
		  search_value: "(bl */bl* *bl/* boiler)",
		},
		{
		  name: "Construction Fence",
		  filter: "fn",
		  dropdown_name: "Construction Fence",
		  search_value: "(fn */fn *fn/* construction*fence)",
		},
		{
		  name: "Mechanical Systems",
		  filter: "mh",
		  dropdown_name: "Mechanical Systems",
		  search_value: "(mh */md *md/* mechanical*systems)",
		},
		{
		  name: "Standpipe",
		  filter: "sd",
		  dropdown_name: "Standpipe",
		  search_value: "(sd */sd *sd/* standpipe)",
		},
		
		{
		  name: "Fire Suppression",
		  filter: "fp",
		  dropdown_name: "Fire Suppression",
		  search_value: "(fp */fp* *fp/* fire*suppression)",
		},
		{
		  name: "Sprinklers",
		  filter: "sp",
		  dropdown_name: "Sprinklers",
		  search_value: "(*/sp* *sp/* sp sprinklers)",
		},
		{
		  name: "Fuel Storage",
		  filter: "fs",
		  dropdown_name: "Fuel Storage",
		  search_value: "(fs */fs* *fs/* fuel*storage)",
		},
		{
		  name: "Fuel Burning",
		  filter: "fb",
		  dropdown_name: "Fuel Burning",
		  search_value: "(fb */fb* *fb/* fuel*burning)",
		},
		{
		  name: "Sign",
		  filter: "sg",
		  dropdown_name: "Sign",
		  search_value: "(sg */sg* *sg/* *sign*)",
		},
		{
		  name: "Electrical",
		  filter: "el",
		  dropdown_name: "Electrical",
		  search_value: "(el */el* *el/* *electrical*)",
		},
		{
		  name: "Curb Cut",
		  filter: "cc",
		  dropdown_name: "Curb Cut",
		  search_value: "(cc */cc* *cc/* curb*cut)",
		},
		{
		  name: "Protection and Mechanical Methods",
		  filter: "pm",
		  dropdown_name: "Protection and Mechanical Methods",
		  search_value: "(pm pmm */pm* *pm/*)",
		}
	  ],
	},
	{
	  name: "Status",
	  show_name: true,
	  search_key: "filing_status",
	  multi_filter: true,
	  hide_issuance:false,
	  values: [
		{
		  name: "Any",
		  filter: "",
		  dropdown_name: "Any",
		  search_value: "",
		},
		{
		  name: "Assigned",
		  filter: "assign",
		  dropdown_name: "Assigned",
		  search_value: "(3 25 33 67 84 7)",
		},
		{
		  name: "Review",
		  filter: "review",
		  dropdown_name: "Review",
		  search_value: "(4 26 5 68 13 2 71 85 24 25 8 47 10 11 50 52 72)",
		},
		{
		  name: "Objections",
		  filter: "objections",
		  dropdown_name: "Objections",
		  search_value: "(19 70 8 86 16 14)",
		},
		{
		  name: "Approved",
		  filter: "approved",
		  dropdown_name: "Approved",
		  search_value: "(6 8 16 37 13 102 15 52)",
		},
		{
		  name: "Issued - Initial",
		  filter: "issued-initial",
		  dropdown_name: "Issued - Initial",
		  search_value: "(initial)",
		},
		{
		  name: "Issued - Renewal",
		  filter: "issued-renewal",
		  dropdown_name: "Issued - Renewal",
		  search_value: "(renewal)",
		}
	  ],
	},
	{
	  name: "Applicant Company Name",
	  show_name: true,
	  search_key: "applicant_business_name",
	  text_filter: true,
	  hide_issuance:false
	},
	{
	  name: "Applicant Contact Name",
	  show_name: true,
	  search_key: "applicant_contact_name",
	  search_fields: ["applicant_first_name", "applicant_first_name"],
	  text_filter: true,
	  hide_issuance:false
	},
	{
	  name: "Filing Rep Company Name",
	  show_name: true,
	  search_key: "filing_applicant_business_name",
	  text_filter: true,
	  hide_issuance:false
	},
	{
	  name: "Filing Rep Contact Name",
	  show_name: true,
	  search_key: "filing_applicant_contact_name",
	  search_fields: ["filing_applicant_irst_name", "filing_applicant_first_name"],
	  text_filter: true,
	  hide_issuance:true
	},
	{
	  name: "Permittee Company Name",
	  show_name: true,
	  search_key: "permittee_business_name",
	  text_filter: true,
	},
	{
	  name: "Permittee Contact Name",
	  show_name: true,
	  search_key: "permittee_contact_name",
	  search_fields: ["permittee_first_name", "permittee_last_name"],
	  text_filter: true,
	},
	{
	  name: "Owner Company Name",
	  show_name: true,
	  search_key: "owner_business_name",
	  text_filter: true,
	},
	{
	  name: "Owner Contact Name",
	  show_name: true,
	  search_key: "owner_contact_name",
	  search_fields: ["owner_first_name","owner_last_name"],
	  text_filter: true,
	},
  ];
  
  export const _proMapFilters = [

	{
	  name: "Total Units",
	  show_name: true,
	  search_key: "residential_units",
	  type: "residential",
	  values: [
		{
		  name: "Any Units",
		  filter: "",
		  dropdown_name: "Any",
		  search_value: "",
		},
		{
		  name: "1-10 Total Units",
		  filter: "1-to-10",
		  dropdown_name: "1 to 10 Units",
		  search_value: [
			"all",
			[">=", "residential_units", 1],
			["<=", "residential_units", 10],
		  ],
		},
		{
		  name: "11-30 Total Units",
		  filter: "11-to-30",
		  dropdown_name: "11 to 30 Units",
		  search_value: [
			"all",
			[">=", "residential_units", 11],
			["<=", "residential_units", 30],
		  ],
		},
		{
		  name: "31-50 Total Units",
		  filter: "31-to-50",
		  dropdown_name: "31 to 50 Units",
		  search_value: [
			"all",
			[">=", "residential_units", 31],
			["<=", "residential_units", 50],
		  ],
		},
		{
		  name: "51-100 Total Units",
		  filter: "51-to-100",
		  dropdown_name: "51 to 100 Units",
		  search_value: [
			"all",
			[">=", "residential_units", 51],
			["<=", "residential_units", 100],
		  ],
		},
		{
		  name: "101-200 Total Units",
		  filter: "101-to-200",
		  dropdown_name: "101 to 200 Units",
		  search_value: [
			"all",
			[">=", "residential_units", 101],
			["<=", "residential_units", 200],
		  ],
		},
		{
		  name: "201 + Total Units",
		  filter: "201-or-more",
		  dropdown_name: "201 or more Units",
		  search_value: [">", "residential_units", 200],
		},
	  ],
	},
	{
	  name: "Total Floors",
	  show_name: true,
	  search_key: "total_floors",
	  values: [
		{
		  name: "Any Units",
		  filter: "",
		  dropdown_name: "Any",
		  search_value: "",
		},
		{
		  name: "1 Floor",
		  filter: "1",
		  dropdown_name: "1 Floor",
		  search_value: ["==", "total_floors", 1],
		},
		{
		  name: "2 Floors",
		  filter: "2",
		  dropdown_name: "2 Floors",
		  search_value: ["==", "total_floors", 2],
		},
		{
		  name: "3-5 Floors",
		  filter: "3-to-5",
		  dropdown_name: "3 to 5 Floors",
		  search_value: [
			"all",
			[">=", "total_floors", 3],
			["<=", "total_floors", 5],
		  ],
		},
		{
		  name: "6-10 Floors",
		  filter: "6-to-10",
		  dropdown_name: "6 to 10 Floors",
		  search_value: [
			"all",
			[">=", "total_floors", 6],
			["<=", "total_floors", 10],
		  ],
		},
		{
		  name: "11-20 Floors",
		  filter: "11-to-20",
		  dropdown_name: "11 to 20 Floors",
		  search_value: [
			"all",
			[">=", "total_floors", 11],
			["<=", "total_floors", 20],
		  ],
		},
		{
		  name: "21-40 Floors",
		  filter: "21-to-40",
		  dropdown_name: "21 to 40 Floors",
		  search_value: [
			"all",
			[">=", "total_floors", 21],
			["<=", "total_floors", 40],
		  ],
		},
		{
		  name: "41+ Floors",
		  filter: "41-or-more",
		  dropdown_name: "41+ Floors",
		  search_value: [">", "total_floors", 40],
		},
	  ],
	},
	{
	  name: "Building Age",
	  show_name: true,
	  search_key: "year_built",
	  values: [
		  {
			  name: "Any",
			  filter: "",
			  dropdown_name: "Any",
			  search_value: ""
		  },
		  {
			  name: `New (After ${new_building_start})`,
			  filter: "new-contruction",
			  dropdown_name: `New (After ${new_building_start})`,
			  search_value: [
				  "all",
				  [">=", "year_built", new_building_start],
				],
		  },
		  {
			  name: `Recent (1991 to ${new_building_start})`,
			  filter: "recent-construction",
			  dropdown_name: `Recent (1991 to ${new_building_start})`,
			  search_value: [
				"all",
				[">=", "year_built", 1991],
				["<=", "year_built", new_building_start],
			  ],
		  },
		  {
			  name: "Post-War (1946 to 1990)",
			  filter: "post-war",
			  dropdown_name: "Post-War (1946 to 1990)",
			  search_value: [
				  "all",
				  [">=", "year_built", 1946],
				  ["<=", "year_built", 1990],
				],
		  },
		  {
			  name: "Pre-War (Before 1946)",
			  filter: "pre-war",
			  dropdown_name: "Pre-War (Before 1946)",
			  search_value: [
				"all",
				[">=", "year_built", 1600],
				["<=", "year_built", 1946],
			  ],
		  }
		  
	  ],
	},
	{
		name: "Asking PPSF",
		show_name: true,
		search_key: "inventory_stats_listings_average_ppsf",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< $750 PPSF ",
				filter: "less-than-750",
				dropdown_name: "Less than $750",
				search_value: [
					"all",
					["<=", "inventory_stats_listings_average_ppsf", 750],
				  ]
			},
			{
				name: "$750 to $1,000 PPSF",
				filter: "750-to-1000",
				dropdown_name: "$750 to $1,000",
				search_value: [
					"all",
					[">=", "inventory_stats_listings_average_ppsf", 750],
					["<=", "inventory_stats_listings_average_ppsf", 1000],
				  ]
			},
			{
				name: "$1,000 to $1,500 PPSF",
				filter: "1000-to-1500",
				dropdown_name: "$1,000 to $1,500",
				search_value: [
					"all",
					[">=", "inventory_stats_listings_average_ppsf", 1001],
					["<=", "inventory_stats_listings_average_ppsf", 1500],
				  ]
			},
			{
				name: "$1,500 to $2,000 PPSF",
				filter: "1500-to-2000",
				dropdown_name: "$1,500 to $2,000",
				search_value: [
					"all",
					[">=", "inventory_stats_listings_average_ppsf", 1501],
					["<=", "inventory_stats_listings_average_ppsf", 2000],
				  ]
			},
			{
				name: "$2,000 to $2,500 PPSF",
				filter: "2000-to-2500",
				dropdown_name: "$2,000 to $2,500",
				search_value: [
					"all",
					[">=", "inventory_stats_listings_average_ppsf", 2001],
					["<=", "inventory_stats_listings_average_ppsf", 2500],
				  ]
			},
			{
				name: "$2,500 to $5,000 PPSF",
				filter: "2500-to-5000",
				dropdown_name: "$2,500 to $5,000",
				search_value: [
					"all",
					[">=", "inventory_stats_listings_average_ppsf", 2501],
					["<=", "inventory_stats_listings_average_ppsf", 5000],
				  ]
			},
			{
				name: "$5,000+ PPSF",
				filter: "5000-or-more",
				dropdown_name: "$5,000 or more",
				search_value: [
					"all",
					[">=", "inventory_stats_listings_average_ppsf", 5001],
				  ]
			}
		]
	},
	{
		name: "Sold PPSF",
		show_name: true,
		search_key: "inventory_stats_transactions_average_ppsf",
		values: [
			{
				name: "Any",
				filter: "",
				dropdown_name: "Any",
				search_value: ""
			},
			{
				name: "< $750 PPSF ",
				filter: "less-than-750",
				dropdown_name: "Less than $750",
				search_value: [
					"all",
					["<=", "inventory_stats_transactions_average_ppsf", 750],
				  ]
			},
			{
				name: "$750 to $1,000 PPSF",
				filter: "750-to-1000",
				dropdown_name: "$750 to $1,000",
				search_value: [
					"all",
					[">=", "inventory_stats_transactions_average_ppsf", 750],
					["<=", "inventory_stats_transactions_average_ppsf", 1000],
				  ]
			},
			{
				name: "$1,000 to $1,500 PPSF",
				filter: "1000-to-1500",
				dropdown_name: "$1,000 to $1,500",
				search_value: [
					"all",
					[">=", "inventory_stats_transactions_average_ppsf", 1001],
					["<=", "inventory_stats_transactions_average_ppsf", 1500],
				  ]
			},
			{
				name: "$1,500 to $2,000 PPSF",
				filter: "1500-to-2000",
				dropdown_name: "$1,500 to $2,000",
				search_value: [
					"all",
					[">=", "inventory_stats_transactions_average_ppsf", 1501],
					["<=", "inventory_stats_transactions_average_ppsf", 2000],
				  ]
			},
			{
				name: "$2,000 to $2,500 PPSF",
				filter: "2000-to-2500",
				dropdown_name: "$2,000 to $2,500",
				search_value: [
					"all",
					[">=", "inventory_stats_transactions_average_ppsf", 2001],
					["<=", "inventory_stats_transactions_average_ppsf", 2500],
				  ]
			},
			{
				name: "$2,500 to $5,000 PPSF",
				filter: "2500-to-5000",
				dropdown_name: "$2,500 to $5,000",
				search_value: [
					"all",
					[">=", "inventory_stats_transactions_average_ppsf", 2501],
					["<=", "inventory_stats_transactions_average_ppsf", 5000],
				  ]
			},
			{
				name: "$5,000+ PPSF",
				filter: "5000-or-more",
				dropdown_name: "$5,000 or more",
				search_value: [
					"all",
					[">=", "inventory_stats_transactions_average_ppsf", 5001],
				  ]
			}
		]
	},

  ];

export const _foreclosureSearchFilters = [
	{
		name: "Property Type",
		show_name: true,
		search_key: "ownership_type",
		multi_filter: true,
		minWidth: 180,
		values: [
			{
				name: "Condo",
				filter: "condo",
				dropdown_name: "Condo",
				search_value: "(*condo* !condo*p)"
			},
			{
				name: "Co-op",
				filter: "coop",
				dropdown_name: "Co-op",
				search_value: "(*co?op* *coop* !condo*p)"
			},
			{
				name: "Single-Family",
				filter: "single-family",
				dropdown_name: "Single-Family",
				search_value: "(single?family)"
			},
			{
				name: "Multi-Family",
				filter: "multi-family",
				dropdown_name: "Multi-Family",
				search_value: "(multi?family)"
			},
			{
				name: "Commercial",
				filter: "commercial",
				dropdown_name: "Commercial",
				search_value: "(commercial)"
			},
			{
				name: "Rental",
				filter: "rental",
				dropdown_name: "Rental",
				search_value: "(rental rentals)"
			}
		]
	},
	{
		name: "Distress Source",
		show_name: true,
		search_key: "distress_source",
		multi_filter: true,
		target: "fq",
		minWidth: 220,
		values: [
			{
				name: "Lis Pendens",
				filter: "lis_pendens",
				dropdown_name: "Lis Pendens (Pre-Auction)",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}-has_auction:true"
			},
			{
				name: "Auction Scheduled",
				filter: "auction_scheduled",
				dropdown_name: "Auction Scheduled",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}has_auction:true AND -auction_status:sold"
			},
			{
				name: "Sold at Auction",
				filter: "sold_at_auction",
				dropdown_name: "Sold at Auction",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}auction_status:sold"
			},
			{
				name: "ACRIS Distress Liens (5y)",
				filter: "acris_liens",
				dropdown_name: "ACRIS Distress Liens (Last 5 Years)",
				search_value: "",
				additional_fq: "{!join method=crossCollection from=property fromIndex=we3-acris-real-property to=key}doc_type:(fl ftl tls ntxl dpftl judg locc tolcc brup) AND recorded_datetime:[NOW-5YEARS TO NOW]"
			}
		]
	},
	{
		name: "Case Stage",
		show_name: true,
		search_key: "case_stage",
		multi_filter: true,
		target: "fq",
		minWidth: 200,
		values: [
			{
				name: "Lis Pendens Filed",
				filter: "lis_pendens_filed",
				dropdown_name: "Lis Pendens Filed",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}case_stage:lis_pendens_filed"
			},
			{
				name: "Summons & Complaint",
				filter: "summons_complaint",
				dropdown_name: "Summons & Complaint",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}case_stage:summons_complaint"
			},
			{
				name: "RJI Filed",
				filter: "rji_filed",
				dropdown_name: "RJI Filed",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}case_stage:rji_filed"
			},
			{
				name: "Motion for Default",
				filter: "motion_for_default",
				dropdown_name: "Motion for Default",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}case_stage:motion_for_default"
			},
			{
				name: "Judgment of Foreclosure",
				filter: "judgment_of_foreclosure",
				dropdown_name: "Judgment of Foreclosure",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}case_stage:judgment_of_foreclosure"
			},
			{
				name: "Referee Appointed",
				filter: "referee_appointed",
				dropdown_name: "Referee Appointed",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}case_stage:referee_appointed"
			},
			{
				name: "Auction Scheduled",
				filter: "stage_auction_scheduled",
				dropdown_name: "Auction Scheduled",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}case_stage:auction_scheduled"
			}
		]
	},
	{
		name: "Case Type",
		show_name: true,
		search_key: "case_type_filter",
		multi_filter: true,
		target: "fq",
		minWidth: 200,
		values: [
			{
				name: "Residential Mortgage",
				filter: "residential_mortgage",
				dropdown_name: "Residential Mortgage Foreclosure",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}case_type:\"real property - mortgage foreclosure - residential\""
			},
			{
				name: "Commercial Mortgage",
				filter: "commercial_mortgage",
				dropdown_name: "Commercial Mortgage Foreclosure",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}case_type:(\"real property - mortgage foreclosure - commercial\" \"real property - mortgage foreclosure - commercial - commercial division\" \"commercial mortgage foreclosure\")"
			},
			{
				name: "Tax Foreclosure",
				filter: "tax_foreclosure",
				dropdown_name: "Tax Foreclosure",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}case_type:\"real property - tax foreclosure\""
			}
		]
	},
	{
		name: "Lis Pendens Date",
		show_name: true,
		search_key: "lis_pendens_date_bucket",
		multi_filter: true,
		target: "fq",
		minWidth: 200,
		values: [
			{
				name: "Last 30 Days",
				filter: "lp_last_30",
				dropdown_name: "Lis Pendens in Last 30 Days",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}lis_pendens_date:[NOW-30DAYS TO NOW]"
			},
			{
				name: "Last 90 Days",
				filter: "lp_last_90",
				dropdown_name: "Lis Pendens in Last 90 Days",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}lis_pendens_date:[NOW-90DAYS TO NOW]"
			},
			{
				name: "Last 6 Months",
				filter: "lp_last_6_mo",
				dropdown_name: "Lis Pendens in Last 6 Months",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}lis_pendens_date:[NOW-6MONTHS TO NOW]"
			},
			{
				name: "Last 12 Months",
				filter: "lp_last_12_mo",
				dropdown_name: "Lis Pendens in Last 12 Months",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}lis_pendens_date:[NOW-1YEAR TO NOW]"
			},
			{
				name: "Older than 1 Year",
				filter: "lp_older_1y",
				dropdown_name: "Lis Pendens Over 1 Year Ago",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}lis_pendens_date:[* TO NOW-1YEAR]"
			}
		]
	},
	{
		name: "Filing Date",
		show_name: true,
		search_key: "filing_date_bucket",
		multi_filter: true,
		target: "fq",
		minWidth: 180,
		values: [
			{
				name: "Last 30 Days",
				filter: "last_30",
				dropdown_name: "Filed in Last 30 Days",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}filing_date:[NOW-30DAYS TO NOW]"
			},
			{
				name: "Last 90 Days",
				filter: "last_90",
				dropdown_name: "Filed in Last 90 Days",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}filing_date:[NOW-90DAYS TO NOW]"
			},
			{
				name: "Last 6 Months",
				filter: "last_6_mo",
				dropdown_name: "Filed in Last 6 Months",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}filing_date:[NOW-6MONTHS TO NOW]"
			},
			{
				name: "Last 12 Months",
				filter: "last_12_mo",
				dropdown_name: "Filed in Last 12 Months",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}filing_date:[NOW-1YEAR TO NOW]"
			},
			{
				name: "Older than 1 Year",
				filter: "older_1y",
				dropdown_name: "Filed Over 1 Year Ago",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}filing_date:[* TO NOW-1YEAR]"
			}
		]
	},
	{
		name: "Original Amount",
		show_name: true,
		search_key: "original_amount_bucket",
		multi_filter: true,
		target: "fq",
		minWidth: 180,
		values: [
			{
				name: "Under $500K",
				filter: "under_500k",
				dropdown_name: "Under $500K",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}original_amount:[0 TO 499999]"
			},
			{
				name: "$500K - $1M",
				filter: "500k_1m",
				dropdown_name: "$500K - $1M",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}original_amount:[500000 TO 999999]"
			},
			{
				name: "$1M - $5M",
				filter: "1m_5m",
				dropdown_name: "$1M - $5M",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}original_amount:[1000000 TO 4999999]"
			},
			{
				name: "$5M - $25M",
				filter: "5m_25m",
				dropdown_name: "$5M - $25M",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}original_amount:[5000000 TO 24999999]"
			},
			{
				name: "Over $25M",
				filter: "over_25m",
				dropdown_name: "Over $25M",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}original_amount:[25000000 TO *]"
			}
		]
	},
	{
		name: "Auction Date",
		show_name: true,
		search_key: "auction_date_bucket",
		multi_filter: true,
		target: "fq",
		minWidth: 200,
		values: [
			{
				name: "Upcoming (30 Days)",
				filter: "upcoming_30",
				dropdown_name: "Upcoming Auction (Next 30 Days)",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}has_auction:true AND auction_sale_date:[NOW TO NOW+30DAYS]"
			},
			{
				name: "Upcoming (90 Days)",
				filter: "upcoming_90",
				dropdown_name: "Upcoming Auction (Next 90 Days)",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}has_auction:true AND auction_sale_date:[NOW TO NOW+90DAYS]"
			},
			{
				name: "Past 90 Days",
				filter: "past_90",
				dropdown_name: "Auction in Past 90 Days",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}has_auction:true AND auction_sale_date:[NOW-90DAYS TO NOW]"
			}
		]
	},
	{
		name: "Auction Status",
		show_name: true,
		search_key: "auction_status_filter",
		multi_filter: true,
		target: "fq",
		minWidth: 180,
		values: [
			{
				name: "Scheduled",
				filter: "scheduled",
				dropdown_name: "Scheduled",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}auction_status:scheduled"
			},
			{
				name: "Postponed",
				filter: "postponed",
				dropdown_name: "Postponed",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}auction_status:postponed"
			},
			{
				name: "Sold",
				filter: "sold",
				dropdown_name: "Sold",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}auction_status:sold"
			}
		]
	},
	{
		name: "Court",
		show_name: true,
		search_key: "court",
		multi_filter: true,
		target: "fq",
		minWidth: 180,
		values: [
			{
				name: "Manhattan",
				filter: "manhattan",
				dropdown_name: "Manhattan",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}court:manhattan"
			},
			{
				name: "Brooklyn",
				filter: "brooklyn",
				dropdown_name: "Brooklyn",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}court:brooklyn"
			},
			{
				name: "Queens",
				filter: "queens",
				dropdown_name: "Queens",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}court:queens"
			},
			{
				name: "Bronx",
				filter: "bronx",
				dropdown_name: "Bronx",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}court:bronx"
			},
			{
				name: "Staten Island",
				filter: "staten_island",
				dropdown_name: "Staten Island",
				search_value: "",
				additional_fq: "{!join from=property fromIndex=nyscef-cases-foreclosures to=alternate_keys}court:(\"staten island\" richmond)"
			}
		]
	}
];